import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { HueSaturationShader } from 'three/examples/jsm/shaders/HueSaturationShader.js';
import { BrightnessContrastShader } from 'three/examples/jsm/shaders/BrightnessContrastShader.js';
import { OutlineEffect } from 'three/examples/jsm/effects/OutlineEffect.js';
import { Muxer as Mp4Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { ProjectData, LayerData, PresetItem, PresetManifest } from '../types';
import { HDRI_MAPS, ensurePBR, centerAndWrap, fetchJSON, loadRemoteTexture, setTextureProperties, FabpFormat, type FabpExportOptions } from '../utils';
import { consumeDemoQuota, isDemoMode } from '../utils/demoMode';
import { CanvasEditor } from '../components/CanvasEditor';
import { LightManager } from './LightManager';
import { MaterialManager } from './MaterialManager';
import { t as translate } from '../i18n/translations';

export class SceneManager {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private lightManager: LightManager;
  public materialManager: MaterialManager;
  
  private currentModel: THREE.Group | null = null;
  private baseSizeY = 1;
  private modelScale = 1;
  private project!: ProjectData;
  private currentModelPath: string | null = null; // Путь к текущей модели
  private currentModelFile: File | null = null; // Файл модели для сохранения
  
  private ground!: THREE.Mesh;
  private pmremGen: THREE.PMREMGenerator;
  private uvViewEnabled = false;
  private originalMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
  private printOffset: number = 0.01;
  private currentEditingPrintId: string | null = null;
  private turntableState: {
    startTime: number;
    duration: number;
    target: THREE.Vector3;
    radius: number;
    height: number;
    initialAngle: number;
    onDone?: () => void;
  } | null = null;
  private isRecordingVideo: boolean = false;
  private cancelVideoRecording: boolean = false; // Флаг отмены записи
  private camPoseStart: { position: THREE.Vector3; target: THREE.Vector3; modelRotation: THREE.Euler } | null = null;
  private camPoseEnd: { position: THREE.Vector3; target: THREE.Vector3; modelRotation: THREE.Euler } | null = null;
  private camArcLong: boolean = false; // По умолчанию короткая дуга
  private animDuration: number = 6000; // 6 секунд по умолчанию
  private turntableMode: boolean = false; // Режим вращения модели вокруг оси
  private videoAspectRatio: string = 'free'; // Aspect ratio для экспорта видео
  private isModelRotating: boolean = false;
  private modelRotationSpeed: number = 0.005; // Скорость поворота (радиан на кадр)
  // Каноничное "начальное" положение модели (лицом к камере) — фиксируется при загрузке модели
  private modelBaseRotationY: number = 0;
  // Положение модели на момент включения витрины (turntable) — чтобы корректно возвращаться при остановке
  private turntableStartRotationY: number = 0;
  private isStoppingRotation: boolean = false; // Флаг плавной остановки
  private targetRotationSpeed: number = 0; // Целевая скорость для плавной остановки
  
  // Мультивыбор слоёв
  private selectedLayers: Set<string> = new Set(); // UUID выбранных слоёв
  private selectedMeshes: THREE.Mesh[] = []; // Меши выбранных слоёв
  // Отдельное хранилище исходных данных холста принтов, чтобы не потерять их при работе с baked-картами
  private printCanvasData: Record<string, any> = {};
  private allLayerElements: HTMLElement[] = []; // Все элементы слоёв в порядке DOM
  private allLayerMeshes: Map<string, THREE.Mesh> = new Map(); // Соответствие id -> mesh
  private toolbarMessageTimeout: number | null = null; // Таймер для автоскрытия сообщения
  private lastAnchorIndex: number = -1; // Индекс "якоря" для Shift-выделения
  
  private camAnimState: {
    startTime: number;
    duration: number;
    startPos: THREE.Vector3;
    endPos: THREE.Vector3;
    startDir: THREE.Vector3;
    axis: THREE.Vector3;
    angle: number;
    startLen: number;
    endLen: number;
    pivot: THREE.Vector3;
    startTarget: THREE.Vector3;
    endTarget: THREE.Vector3;
    startModelRot?: THREE.Euler;
    endModelRot?: THREE.Euler;
    onDone?: () => void;
    recording?: boolean;
  } | null = null;

  private container: HTMLElement;
  private readonly SIDEBAR_WIDTH = 233;

  // Mini preview (edges-only) renderer
  private miniRenderer: THREE.WebGLRenderer | null = null;
  private miniScene: THREE.Scene | null = null;
  private miniCamera: THREE.PerspectiveCamera | null = null;
  private miniRoot: THREE.Group | null = null;
  private miniOutline: any | null = null;
  private miniSilhouetteMaterial: THREE.MeshBasicMaterial | null = null;
  
  // Performance mode (low pixel ratio for interactive rendering only)
  private performanceModeEnabled: boolean = false;
  private userPixelRatio: number | null = null;

  // Rotation controls: rotate scene (OrbitControls) vs rotate model (drag)
  private rotationControlMode: 'scene' | 'model' = 'model';
  private modelRotateDragging: boolean = false;
  private modelRotatePointerId: number | null = null;
  private modelRotateLastX: number = 0;
  private modelRotateLastY: number = 0;
  private modelRotateVelX: number = 0;
  private modelRotateVelY: number = 0;
  private modelRotateLastT: number = 0;
  private modelRotateInertiaRaf: number = 0;
  // If showcase (auto-rotation) is active, we temporarily pause it during manual drag,
  // but keep the "active" state consistent (button stays active, rotation resumes after drag).
  private modelRotationPausedByDrag: boolean = false;
  private resumeShowcaseAfterDrag: boolean = false;

  // Postprocessing (Debug): Bloom
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private bloomEnabled: boolean = false;
  private bloomStrength: number = 0.35;
  private bloomRadius: number = 0.35;
  private bloomThreshold: number = 0.85;

  // Postprocessing (Debug): DOF
  private dofPass: BokehPass | null = null;
  private dofEnabled: boolean = false;
  private dofFocus: number = 1.5;
  private dofAperture: number = 0.002;
  private dofMaxBlur: number = 0.008;

  // Postprocessing (Debug): Film/Grain
  private filmPass: FilmPass | null = null;
  private filmEnabled: boolean = false;
  private filmNoiseIntensity: number = 0.5;
  private filmScanlinesIntensity: number = 0.5;
  private filmScanlinesCount: number = 2048;

  // Postprocessing (Debug): Color correction
  private colorCorrectionPass1: ShaderPass | null = null; // Brightness/Contrast
  private colorCorrectionPass2: ShaderPass | null = null; // Hue/Saturation (we'll use saturation only)
  private colorCorrectionEnabled: boolean = false;
  private ccBrightness: number = 0;
  private ccContrast: number = 0;
  private ccSaturation: number = 0;

  // Postprocessing (Debug): SSAO
  private ssaoPass: SSAOPass | null = null;
  private ssaoEnabled: boolean = false;
  private ssaoRadius: number = 16;
  private ssaoMinDistance: number = 0.005;
  private ssaoMaxDistance: number = 0.3;
  
  // SMAA для антиалиасинга при использовании постобработки
  private smaaPass: SMAAPass | null = null;
  private outputPass: OutputPass | null = null;
  
  // Render scale для supersampling (1.0 = обычное разрешение, 1.5-2.0 = лучшее качество)
  private composerRenderScale: number = 1.5;

  private getDefaultPixelRatio(): number {
    return Math.min(window.devicePixelRatio, 2);
  }

  private applyInteractivePixelRatio(): void {
    if (this.performanceModeEnabled) {
      this.renderer.setPixelRatio(1);
      return;
    }
    if (this.userPixelRatio !== null) {
      this.renderer.setPixelRatio(Math.min(this.userPixelRatio, window.devicePixelRatio));
      return;
    }
    this.renderer.setPixelRatio(this.getDefaultPixelRatio());
  }

  private ensureComposer(): void {
    if (this.composer) return;
    this.composer = new EffectComposer(this.renderer);
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    // SSAO - должен идти сразу после RenderPass
    const size = this.renderer.getSize(new THREE.Vector2());
    this.ssaoPass = new SSAOPass(this.scene, this.camera, size.x, size.y);
    this.ssaoPass.kernelRadius = this.ssaoRadius;
    this.ssaoPass.minDistance = this.ssaoMinDistance;
    this.ssaoPass.maxDistance = this.ssaoMaxDistance;
    this.composer.addPass(this.ssaoPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      this.bloomStrength,
      this.bloomRadius,
      this.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    // DOF
    this.dofPass = new BokehPass(this.scene, this.camera, {
      focus: this.dofFocus,
      aperture: this.dofAperture,
      maxblur: this.dofMaxBlur
    } as any);
    this.composer.addPass(this.dofPass);

    // Color correction (Brightness/Contrast then Hue/Saturation)
    this.colorCorrectionPass1 = new ShaderPass(BrightnessContrastShader as any);
    this.composer.addPass(this.colorCorrectionPass1);
    this.colorCorrectionPass2 = new ShaderPass(HueSaturationShader as any);
    this.composer.addPass(this.colorCorrectionPass2);

    // Film / grain
    // FilmPass typings differ across three versions; use `any` to avoid TS constructor signature mismatch.
    // Extra args are harmless in JS even if not used by the implementation.
    this.filmPass = new (FilmPass as any)(
      this.filmNoiseIntensity,
      this.filmScanlinesIntensity,
      this.filmScanlinesCount,
      false
    ) as FilmPass;
    this.composer.addPass(this.filmPass);
    
    // SMAA pass для высококачественного антиалиасинга
    this.smaaPass = new SMAAPass(size.x * this.renderer.getPixelRatio(), size.y * this.renderer.getPixelRatio());
    this.composer.addPass(this.smaaPass);
    
    // OutputPass для корректного tone mapping и цветового пространства (должен быть последним)
    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);

    this.updatePostprocessingPasses();
    this.updateComposerSizeFromRenderer();
  }

  private updatePostprocessingPasses(): void {
    // If composer is not created yet, just keep stored values.
    if (this.bloomPass) {
      this.bloomPass.enabled = this.bloomEnabled;
      this.bloomPass.strength = this.bloomStrength;
      this.bloomPass.radius = this.bloomRadius;
      this.bloomPass.threshold = this.bloomThreshold;
    }

    if (this.dofPass) {
      (this.dofPass as any).enabled = this.dofEnabled;
      const uniforms = (this.dofPass as any).materialBokeh?.uniforms;
      if (uniforms) {
        if (uniforms.focus) uniforms.focus.value = this.dofFocus;
        if (uniforms.aperture) uniforms.aperture.value = this.dofAperture;
        if (uniforms.maxblur) uniforms.maxblur.value = this.dofMaxBlur;
      }
    }

    if (this.colorCorrectionPass1 && this.colorCorrectionPass2) {
      this.colorCorrectionPass1.enabled = this.colorCorrectionEnabled;
      this.colorCorrectionPass2.enabled = this.colorCorrectionEnabled;
      const u1 = (this.colorCorrectionPass1 as any).uniforms;
      if (u1) {
        if (u1.brightness) u1.brightness.value = this.ccBrightness;
        if (u1.contrast) u1.contrast.value = this.ccContrast;
      }
      const u2 = (this.colorCorrectionPass2 as any).uniforms;
      if (u2) {
        // Hue left at 0; saturation is adjusted by user
        if (u2.hue) u2.hue.value = 0;
        if (u2.saturation) u2.saturation.value = this.ccSaturation;
      }
    }

    if (this.filmPass) {
      this.filmPass.enabled = this.filmEnabled;
      // FilmPass может иметь разные структуры в зависимости от версии Three.js
      const u = (this.filmPass as any).uniforms;
      const material = (this.filmPass as any).material;
      
      if (u) {
        // Проверяем несколько вариантов именования uniforms
        if (u.nIntensity) u.nIntensity.value = this.filmNoiseIntensity;
        if (u.sIntensity) u.sIntensity.value = this.filmScanlinesIntensity;
        if (u.sCount) u.sCount.value = this.filmScanlinesCount;
        if (u.grayscale) u.grayscale.value = 0; // Выключаем grayscale
      }
      
      if (material?.uniforms) {
        if (material.uniforms.nIntensity) material.uniforms.nIntensity.value = this.filmNoiseIntensity;
        if (material.uniforms.sIntensity) material.uniforms.sIntensity.value = this.filmScanlinesIntensity;
        if (material.uniforms.sCount) material.uniforms.sCount.value = this.filmScanlinesCount;
        if (material.uniforms.grayscale) material.uniforms.grayscale.value = 0;
      }
    }

    if (this.ssaoPass) {
      this.ssaoPass.enabled = this.ssaoEnabled;
      this.ssaoPass.kernelRadius = this.ssaoRadius;
      this.ssaoPass.minDistance = this.ssaoMinDistance;
      this.ssaoPass.maxDistance = this.ssaoMaxDistance;
    }
    
    // SMAA должен быть всегда включен когда используется composer
    if (this.smaaPass) {
      this.smaaPass.enabled = true;
    }
    
    // OutputPass должен быть всегда включен для корректного tone mapping
    if (this.outputPass) {
      this.outputPass.enabled = true;
    }
  }

  private updateComposerSizeFromRenderer(): void {
    if (!this.composer) return;
    const size = this.renderer.getSize(new THREE.Vector2());
    const pixelRatio = this.renderer.getPixelRatio();
    
    // Рендерим composer с увеличенным разрешением (supersampling) для лучшего качества
    const renderWidth = size.x * this.composerRenderScale;
    const renderHeight = size.y * this.composerRenderScale;
    
    this.composer.setSize(renderWidth, renderHeight);
    
    // Обновляем размеры для SMAA с учетом pixel ratio и render scale
    if (this.smaaPass) {
      this.smaaPass.setSize(renderWidth * pixelRatio, renderHeight * pixelRatio);
    }
  }

  private renderFrame(): void {
    const anyEnabled =
      this.bloomEnabled ||
      this.dofEnabled ||
      this.filmEnabled ||
      this.colorCorrectionEnabled ||
      this.ssaoEnabled;

    if (anyEnabled) {
      this.ensureComposer();
      this.updatePostprocessingPasses();
      this.composer!.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }

    // Mini preview render (if enabled)
    this.renderMiniPreview();
  }

  constructor(container: HTMLElement, materialManager: MaterialManager) {
    this.container = container;
    this.scene = new THREE.Scene();
    
    const width = window.innerWidth - this.SIDEBAR_WIDTH;
    const height = window.innerHeight;
    
    this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 2000);
    this.camera.position.set(0.5, 0.6, 2.5);
    
    // Добавляем объемный свет (volumetric fog)
    this.scene.fog = new THREE.FogExp2(0x000000, 0.08); // Черный туман с плотностью 0.08
    
    this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.domElement.style.display = 'block';
    this.applyInteractivePixelRatio();
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2; // Немного увеличиваем exposure для более яркого света
    this.renderer.shadowMap.enabled = false; // Тени выключены по умолчанию
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    container.appendChild(this.renderer.domElement);
    
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.minDistance = 0.5;
    this.controls.maxDistance = 6;
    this.controls.enablePan = true;
    this.controls.screenSpacePanning = true;
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    };

    this.lightManager = new LightManager(this.scene);
    this.materialManager = materialManager;
    
    this.pmremGen = new THREE.PMREMGenerator(this.renderer);
    this.pmremGen.compileEquirectangularShader();
    
    this.setupGround();
    this.setupProject();
    this.setupEventListeners();
    this.setupRotationControlHandlers();
    this.applyInitialRotationControlMode();

    // Mini preview (edges) is optional, init if DOM canvas exists
    this.initMiniPreviewIfAvailable();
    
    // HDRI по умолчанию
    this.setHDRI('night');
    this.createDefaultModel();
  }

  private initMiniPreviewIfAvailable(): void {
    const canvas = document.getElementById('model-mini-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    // Prevent double init
    if (this.miniRenderer) return;

    this.miniRenderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: false,
    });
    this.miniRenderer.setClearColor(0x000000, 0);
    // Более чёткий контур (рендерим мини-превью в повышенном pixel ratio)
    this.miniRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.miniRenderer.setSize(84, 84, false);

    this.miniScene = new THREE.Scene();
    this.miniCamera = new THREE.PerspectiveCamera(40, 1, 0.01, 2000);
    this.miniCamera.position.set(0.6, 0.5, 2.2);

    // Silhouette only: render meshes to depth (no color), then draw outline around them.
    this.miniSilhouetteMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    this.miniSilhouetteMaterial.depthWrite = true;
    (this.miniSilhouetteMaterial as any).colorWrite = false;

    // Outline effect draws external contour (no "fold" edges)
    this.miniOutline = new (OutlineEffect as any)(this.miniRenderer, {
      // Ровная и более чёткая обводка
      defaultThickness: 0.0035,
      defaultColor: [1, 1, 1],
      defaultAlpha: 1.0,
      defaultKeepAlive: true,
    });

    this.miniRoot = new THREE.Group();
    this.miniScene.add(this.miniRoot);

    // Build preview from current model if already present
    this.rebuildMiniPreviewFromCurrentModel();
  }

  private rebuildMiniPreviewFromCurrentModel(): void {
    if (!this.miniRoot || !this.miniSilhouetteMaterial) return;

    // Clear old
    while (this.miniRoot.children.length) {
      const child = this.miniRoot.children.pop()!;
      child.traverse((o) => {
        const any = o as any;
        if (any.geometry && typeof any.geometry.dispose === 'function') any.geometry.dispose();
        if (any.material && typeof any.material.dispose === 'function') any.material.dispose();
      });
    }

    if (!this.currentModel) return;

    const build = (src: THREE.Object3D, dst: THREE.Object3D) => {
      src.children.forEach((c) => {
        // Не показываем принт-меши в мини-превью
        if ((c as any).isMesh && (c as any).name?.startsWith('Print_')) return;

        if ((c as any).isMesh) {
          const mesh = c as THREE.Mesh;
          const geo = mesh.geometry;
          if (!geo) return;
          const m = this.miniSilhouetteMaterial!.clone();
          m.depthWrite = true;
          (m as any).colorWrite = false;
          const miniMesh = new THREE.Mesh(geo, m);
          miniMesh.position.copy(mesh.position);
          miniMesh.quaternion.copy(mesh.quaternion);
          miniMesh.scale.copy(mesh.scale);
          (dst as THREE.Group).add(miniMesh);
        } else {
          const g = new THREE.Group();
          g.position.copy(c.position);
          g.quaternion.copy(c.quaternion);
          g.scale.copy(c.scale);
          (dst as THREE.Group).add(g);
          build(c, g);
        }
      });
    };

    // Строим из детей currentModel (без применения transform currentModel)
    build(this.currentModel, this.miniRoot);

    // Подстроим камеру под габариты
    this.fitMiniCamera();
  }

  private fitMiniCamera(): void {
    if (!this.miniCamera || !this.miniRoot) return;

    // Синхронизируем текущий transform модели в мини-превью перед расчетом
    if (this.currentModel) {
      this.miniRoot.rotation.y = this.currentModel.rotation.y;
      this.miniRoot.scale.copy(this.currentModel.scale);
      this.miniRoot.position.copy(this.currentModel.position);
      this.miniRoot.updateMatrixWorld(true);
    }

    const box = new THREE.Box3().setFromObject(this.miniRoot);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (!isFinite(maxDim) || maxDim <= 0) return;

    const fov = this.miniCamera.fov * (Math.PI / 180);
    const dist = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.6;

    this.miniCamera.position.set(center.x, center.y + size.y * 0.12, center.z + dist);
    this.miniCamera.lookAt(center);
    this.miniCamera.updateProjectionMatrix();
  }

  private renderMiniPreview(): void {
    if (!this.miniRenderer || !this.miniScene || !this.miniCamera || !this.miniRoot) return;
    if (!this.currentModel) return;

    // Синхронизируем только Y (по требованию — вращаем только вокруг вертикальной оси)
    this.miniRoot.rotation.x = 0;
    this.miniRoot.rotation.z = 0;
    this.miniRoot.rotation.y = this.currentModel.rotation.y;
    this.miniRoot.scale.copy(this.currentModel.scale);
    this.miniRoot.position.copy(this.currentModel.position);

    if (this.miniOutline) {
      this.miniOutline.render(this.miniScene, this.miniCamera);
    } else {
      this.miniRenderer.render(this.miniScene, this.miniCamera);
    }
  }

  private setupGround(): void {
    this.ground = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 20),
      new THREE.ShadowMaterial({ opacity: 0.35 })
    );
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -0.9;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);
    this.scene.background = new THREE.Color(0x000000);
  }

  private setupProject(): void {
    this.project = {
      model: { name: 'Default' },
      environment: 'night',
      background: '#000000',
      cameraFov: this.camera.fov,
      lights: {
        top: { intensity: 2.2 },
        bottom: { intensity: 1.5 }
      },
      layers: {},
      ui: {
        fpsPanelDismissed: false,
        rotationControlMode: 'model'
      },
      totalWorkTime: 0
    };
  }

  public setFpsPanelDismissed(dismissed: boolean): void {
    if (!this.project) return;
    if (!(this.project as any).ui) (this.project as any).ui = {};
    (this.project as any).ui.fpsPanelDismissed = dismissed;
  }

  public isFpsPanelDismissed(): boolean {
    return Boolean((this.project as any)?.ui?.fpsPanelDismissed);
  }
  
  public setWorkTime(time: number): void {
    if (this.project) {
      (this.project as any).totalWorkTime = time;
    }
  }
  
  public syncLayerParams(layerId: string, params: any, maps?: any, preset?: string | null): void {
    if (!this.project.layers[layerId]) {
      this.project.layers[layerId] = {
        name: '',
        maps: maps != null ? { ...maps } : {},
        params: params,
        preset: preset ?? null,
        type: 'layer'
      };
    } else {
      this.project.layers[layerId].params = { ...params };
      if (maps !== undefined) {
        this.project.layers[layerId].maps = { ...maps };
      }
      if (preset !== undefined) {
        this.project.layers[layerId].preset = preset;
      }
    }
  }

  private setupEventListeners(): void {
    window.addEventListener('resize', () => this.handleResize());
  }

  private applyInitialRotationControlMode(): void {
    let mode: 'scene' | 'model' = 'model';
    const fromProject = (this.project as any)?.ui?.rotationControlMode;
    if (fromProject === 'scene' || fromProject === 'model') {
      mode = fromProject;
    }
    try {
      const stored = localStorage.getItem('fabrica.rotationControlMode');
      if (stored === 'scene' || stored === 'model') mode = stored;
    } catch {
      // ignore
    }
    this.setRotationControlMode(mode);
  }

  private setupRotationControlHandlers(): void {
    const el = this.renderer.domElement;
    
    const applyModelEulerDeltaNoStop = (deltaX: number, deltaY: number, deltaZ: number) => {
      if (!this.currentModel) return;
      // X ограничим умеренно, чтобы не переворачивать "вверх ногами" случайно
      const nextX = this.currentModel.rotation.x + deltaX;
      this.currentModel.rotation.x = THREE.MathUtils.clamp(nextX, -Math.PI / 2, Math.PI / 2);
      this.currentModel.rotation.y += deltaY;
      this.currentModel.rotation.z += deltaZ;
    };

    const onDown = (e: PointerEvent) => {
      if (this.rotationControlMode !== 'model') return;
      // left mouse button only (touch uses button=0 too)
      if (typeof e.button === 'number' && e.button !== 0) return;

      // Stop any inertia
      if (this.modelRotateInertiaRaf) {
        cancelAnimationFrame(this.modelRotateInertiaRaf);
        this.modelRotateInertiaRaf = 0;
      }

      // Если витрина активна — НЕ выключаем её (чтобы кнопка не рассинхронизировалась),
      // а временно паузим автоповорот на время ручного drag.
      if (this.currentModel && (this.isModelRotating || this.isStoppingRotation)) {
        this.modelRotationPausedByDrag = true;
        this.resumeShowcaseAfterDrag = true;
        // Отменяем "плавную остановку" — пользователь явно хочет интерактив
        this.isStoppingRotation = false;
        this.targetRotationSpeed = 0;
        // Чтобы "стоп" потом не дёргал назад, обновим стартовый угол на текущий
        this.turntableStartRotationY = this.currentModel.rotation.y;
        // Скорость вернём к базовой, чтобы после drag витрина шла стабильно
        this.modelRotationSpeed = 0.005;
      } else {
        this.modelRotationPausedByDrag = false;
        this.resumeShowcaseAfterDrag = false;
      }

      this.modelRotateDragging = true;
      this.modelRotatePointerId = e.pointerId;
      this.modelRotateLastX = e.clientX;
      this.modelRotateLastY = e.clientY;
      this.modelRotateVelX = 0;
      this.modelRotateVelY = 0;
      this.modelRotateLastT = performance.now();

      try { el.setPointerCapture(e.pointerId); } catch {}
      e.preventDefault();
      e.stopPropagation();
    };

    const onMove = (e: PointerEvent) => {
      if (this.rotationControlMode !== 'model') return;
      if (!this.modelRotateDragging) return;
      if (this.modelRotatePointerId !== null && e.pointerId !== this.modelRotatePointerId) return;

      const now = performance.now();
      const dt = Math.max(1, now - this.modelRotateLastT);
      const dx = e.clientX - this.modelRotateLastX;
      const dy = e.clientY - this.modelRotateLastY;

      this.modelRotateLastX = e.clientX;
      this.modelRotateLastY = e.clientY;
      this.modelRotateLastT = now;

      // px/ms velocities for inertia
      const targetVx = dx / dt;
      const targetVy = dy / dt;
      // Лёгкое сглаживание скорости — инерция будет приятнее
      const vLerp = 0.35;
      this.modelRotateVelX = THREE.MathUtils.lerp(this.modelRotateVelX, targetVx, vLerp);
      this.modelRotateVelY = THREE.MathUtils.lerp(this.modelRotateVelY, targetVy, vLerp);

      // Sensitivity tuned to feel close to OrbitControls
      const sens = 0.0046; // radians per px (чуть плавнее)
      const yaw = dx * sens;
      // Инвертируем вертикаль, как в режиме поворота сцены (OrbitControls)
      const pitch = dy * sens;
      if (yaw || pitch) {
        applyModelEulerDeltaNoStop(pitch, yaw, 0);
      }

      e.preventDefault();
      e.stopPropagation();
    };

    const endDrag = (e?: PointerEvent) => {
      if (!this.modelRotateDragging) return;
      if (e && this.modelRotatePointerId !== null && e.pointerId !== this.modelRotatePointerId) return;

      this.modelRotateDragging = false;
      const lastVelX = this.modelRotateVelX;
      const lastVelY = this.modelRotateVelY;
      this.modelRotatePointerId = null;

      if (e) {
        try { el.releasePointerCapture(e.pointerId); } catch {}
      }

      // Inertia (damping) to keep "same feel"
      const speed = Math.hypot(lastVelX, lastVelY);
      if (speed > 0.015) {
        const sens = 0.0046;
        // Чуть более плавное затухание
        const decay = 0.86;
        let vx = lastVelX;
        let vy = lastVelY;
        let prevT = performance.now();
        const step = () => {
          const now = performance.now();
          const dt = Math.min(40, Math.max(1, now - prevT));
          prevT = now;

          // Convert velocity back to delta px for this frame
          const dx = vx * dt;
          const dy = vy * dt;
          const yaw = dx * sens;
          const pitch = dy * sens;
          if (yaw || pitch) applyModelEulerDeltaNoStop(pitch, yaw, 0);

          // Exponential-ish decay
          const k = Math.pow(decay, dt / 16);
          vx *= k;
          vy *= k;

          if (Math.hypot(vx, vy) < 0.008 || this.rotationControlMode !== 'model') {
            this.modelRotateInertiaRaf = 0;
            // Если витрина была активна, возвращаем автоповорот после инерции
            if (this.resumeShowcaseAfterDrag) {
              this.modelRotationPausedByDrag = false;
              this.resumeShowcaseAfterDrag = false;
            }
            return;
          }
          this.modelRotateInertiaRaf = requestAnimationFrame(step);
        };
        this.modelRotateInertiaRaf = requestAnimationFrame(step);
        // Пока идёт инерция — витрина остаётся на паузе
        return;
      }

      // Без инерции — сразу возвращаем витрину (если была)
      if (this.resumeShowcaseAfterDrag) {
        this.modelRotationPausedByDrag = false;
        this.resumeShowcaseAfterDrag = false;
      }
    };

    el.addEventListener('pointerdown', onDown, { passive: false });
    el.addEventListener('pointermove', onMove, { passive: false });
    el.addEventListener('pointerup', endDrag as any, { passive: false });
    el.addEventListener('pointercancel', endDrag as any, { passive: false });
    el.addEventListener('lostpointercapture', () => endDrag());
  }

  public setRotationControlMode(mode: 'scene' | 'model'): void {
    this.rotationControlMode = mode;
    this.controls.enableRotate = mode === 'scene';

    // If switching away from model rotation, stop any inertia/drag state
    if (mode !== 'model') {
      this.modelRotateDragging = false;
      this.modelRotatePointerId = null;
      if (this.modelRotateInertiaRaf) {
        cancelAnimationFrame(this.modelRotateInertiaRaf);
        this.modelRotateInertiaRaf = 0;
      }
    }

    // Persist as UI preference (in project + localStorage)
    try {
      if (!(this.project as any).ui) (this.project as any).ui = {};
      (this.project as any).ui.rotationControlMode = mode;
    } catch {}
    try {
      localStorage.setItem('fabrica.rotationControlMode', mode);
    } catch {}

    // OrbitControls internal state may need refresh
    this.controls.update();
  }

  private handleResize(): void {
    const width = window.innerWidth - this.SIDEBAR_WIDTH;
    const height = window.innerHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.updateComposerSizeFromRenderer();
  }


  public setHDRI(key: string): void {
    this.project.environment = key;
    
    if (key === 'none') {
      this.scene.environment = null;
      return;
    }

    const hdriPath = HDRI_MAPS[key as keyof typeof HDRI_MAPS];
    if (!hdriPath) {
      console.warn(`HDRI "${key}" not found in HDRI_MAPS`);
      this.scene.environment = null;
      return;
    }

    // Устанавливаем курсор загрузки
    document.body.classList.add('loading-hdri');
    
    // Показываем индикатор загрузки
    const toastId = 'hdri-loading';
    window.dispatchEvent(new CustomEvent('showLoadingToast', { 
      detail: { id: toastId, text: translate('loading.hdri'), progress: 0 } 
    }));

    new RGBELoader().load(
      hdriPath, 
      (hdr) => {
        // Прогресс 50% - файл загружен, обрабатываем
        window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
          detail: { id: toastId, progress: 50 } 
        }));
        
      const env = this.pmremGen.fromEquirectangular(hdr).texture;
      hdr.dispose();
      this.scene.environment = env;
        
        // Готово
        window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
          detail: { id: toastId, progress: 100 } 
        }));
        
        // Скрываем через небольшую задержку
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('hideLoadingToast', { 
            detail: { id: toastId } 
          }));
          // Убираем курсор загрузки
          document.body.classList.remove('loading-hdri');
          
          // Сигнал о завершении загрузки HDRI
          window.dispatchEvent(new CustomEvent('hdriLoadComplete'));
        }, 500);
      }, 
      (progress) => {
        // Обновляем прогресс загрузки
        if (progress.lengthComputable) {
          const percent = (progress.loaded / progress.total) * 50; // 0-50% для загрузки файла
          window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
            detail: { id: toastId, progress: percent } 
          }));
        }
      }, 
      (error) => {
        console.error(`Failed to load HDRI "${key}":`, error);
        this.scene.environment = null;
        window.dispatchEvent(new CustomEvent('hideLoadingToast', { 
          detail: { id: toastId } 
        }));
        // Убираем курсор загрузки при ошибке
        document.body.classList.remove('loading-hdri');
        
        // Сигнал о завершении загрузки HDRI (даже при ошибке)
        window.dispatchEvent(new CustomEvent('hdriLoadComplete'));
      }
    );
  }

  public setBackground(color: string): void {
    if (!color) color = '#000000';
    
    // ВАЖНО: Проверяем прозрачный цвет ПЕРЕД любыми операциями с THREE.Color
    // THREE.Color обрезает альфа-канал из 8-символьного hex (#RRGGBBAA -> #RRGGBB)
    const colorLower = color.toLowerCase().trim();
    
    // Проверяем все возможные варианты прозрачного цвета
    const isTransparent = 
      colorLower === 'transparent' || 
      colorLower === '' || 
      (color.startsWith('#') && color.length === 9 && parseInt(color.slice(-2), 16) === 0) || // #00000000
      (color.startsWith('#') && color.length === 9 && color.slice(-2).toLowerCase() === '00') || // #00000000
      (colorLower.includes('rgba') && (colorLower.endsWith(', 0)') || colorLower.endsWith(',0)') || colorLower.includes('rgba(0,0,0,0)')));
    
    console.log('setBackground - color:', color, 'length:', color.length, 'isTransparent:', isTransparent);
    
    if (isTransparent) {
      // Прозрачный фон - убираем все фоны и устанавливаем прозрачный clearColor
      this.scene.background = null;
      this.scene.fog = null;
      this.ground.visible = false;
      this.renderer.setClearColor(0x000000, 0);
      // Сохраняем как 'transparent' для надежности
      this.project.background = 'transparent';
      return; // ВАЖНО: выходим сразу, не создаем THREE.Color
    }
    
    // Для непрозрачного цвета - убираем альфа-канал если есть (8 символов -> 6)
    let cleanColor = color;
    if (color.startsWith('#') && color.length === 9) {
      cleanColor = color.slice(0, 7); // Обрезаем альфа-канал
    }
    
    // Теперь безопасно создаем THREE.Color
    this.scene.background = new THREE.Color(cleanColor);
    this.scene.fog = new THREE.FogExp2(0x000000, 0.08);
    this.ground.visible = true;
    const colorObj = new THREE.Color(cleanColor);
    this.renderer.setClearColor(colorObj, 1);
    this.project.background = color;
  }
  
  public setBackgroundGradient(topColor: string, bottomColor: string): void {
    // Создаем градиентную текстуру через Canvas с высоким разрешением
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 2048; // Высокое разрешение для плавности
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, topColor);    // Верх
      gradient.addColorStop(1, bottomColor); // Низ
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearFilter;
      texture.generateMipmaps = false; // Отключаем mipmap для избежания артефактов
      texture.needsUpdate = true;
      
      this.scene.background = texture;
      this.scene.fog = null; // Убираем туман для чистого градиента
      this.ground.visible = false;
      
      // Сохраняем как объект градиента
      this.project.background = { gradient: { top: topColor, bottom: bottomColor } } as any;
    }
  }

  public setCameraFov(fov: number): void {
    const clamped = THREE.MathUtils.clamp(isFinite(fov) ? fov : this.camera.fov, 20, 90);
    
    // Чтобы ощущалось как смена объектива, компенсируем зум движением камеры:
    // сохраняем текущий кадр (видимую высоту) и двигаем камеру ближе/дальше,
    // меняя перспективу (фон «дышит»), но масштаб объекта остаётся примерно тем же.
    const target = this.controls.target.clone();
    const dir = this.camera.position.clone().sub(target);
    const currentDist = dir.length();
    const currentFovRad = THREE.MathUtils.degToRad(this.camera.fov);
    const newFovRad = THREE.MathUtils.degToRad(clamped);
    const denom = Math.tan(newFovRad / 2);
    
    // Если вдруг камера в цели или tan(0) — не двигаем
    if (currentDist > 1e-4 && denom > 1e-6) {
      const newDist = currentDist * Math.tan(currentFovRad / 2) / denom;
      dir.normalize().multiplyScalar(newDist);
      this.camera.position.copy(target.clone().add(dir));
      // Сбрасываем торможение орбита после явного перемещения
      this.controls.update();
    }
    
    this.camera.fov = clamped;
    this.camera.updateProjectionMatrix();
    this.project.cameraFov = clamped;
  }

  public setModelScale(scale: number): void {
    this.modelScale = scale;
    
    if (this.currentModel) {
      this.currentModel.scale.set(scale, scale, scale);
      this.updateGround();
      this.lightManager.placeRig(this.baseSizeY * scale);
    }
  }

  private updateGround(): void {
    const half = (this.baseSizeY * this.modelScale) / 2;
    this.ground.position.y = -half - 0.01;
  }

  public loadModel(file: File): void {
    // Проверка подписки
    const isSubscriptionActive = (window as any).isSubscriptionActive;
    if (isSubscriptionActive && !isSubscriptionActive()) {
      console.warn('⛔ Загрузка модели заблокирована - подписка истекла');
      alert('Загрузка моделей недоступна. Продлите подписку для доступа к полному функционалу.');
      return;
    }
    
    const url = URL.createObjectURL(file);
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    this.project.model.name = file.name;
    this.currentModelPath = file.name;
    this.currentModelFile = file;
    this.project.layers = {};
    this.printCanvasData = {}; // Очищаем кеш canvasData при загрузке новой модели
    
    // ВАЖНО: Очищаем aoMap модели при загрузке новой модели
    // Это предотвращает утечку aoMap от предыдущей модели
    if (this.project.model.aoMap && typeof this.project.model.aoMap !== 'string') {
      const oldAoMap = this.project.model.aoMap as THREE.Texture;
      try { oldAoMap.dispose(); } catch {}
    }
    this.project.model.aoMap = null;
    // AO per UV-group
    if (this.project.model.aoMaps) {
      Object.values(this.project.model.aoMaps).forEach((v) => {
        if (v && typeof v !== 'string') {
          try { (v as THREE.Texture).dispose(); } catch {}
        }
      });
    }
    this.project.model.aoMaps = {};

    // Сбрасываем normalMaps при загрузке новой модели, чтобы не утекали
    // нормал-мап от предыдущей модели на принты новой
    this.project.model.normalMaps = {};
    
    if (this.currentModel) {
      this.scene.remove(this.currentModel);
      this.currentModel = null;
    }

    // Показываем начальную загрузку
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.load.model'), progress: 0 } 
    }));

    const onLoaded = (root: THREE.Object3D) => {
      // Показываем прогресс обработки модели
      window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
        detail: { text: translate('loading.load.model'), progress: 95 } 
      }));
      
      this.onModelReady(root);
      URL.revokeObjectURL(url);
      
      // Финальная инициализация сцены
      window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
        detail: { text: translate('loading.load.model'), progress: 100 } 
      }));
    };

    const onProgress = (event: ProgressEvent) => {
      if (event.lengthComputable) {
        // Прогресс загрузки файла занимает 0-90% общего прогресса
        const loadProgress = (event.loaded / event.total) * 90;
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
          detail: { text: translate('loading.load.model'), progress: loadProgress } 
        }));
      } else {
        // Если размер неизвестен, показываем плавный прогресс-бар
        // Имитируем прогресс на основе времени
        const elapsed = Date.now() - (this as any)._loadStartTime;
        const estimatedProgress = Math.min(85, Math.sqrt(elapsed / 100) * 10);
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
          detail: { text: translate('loading.load.model'), progress: estimatedProgress } 
        }));
      }
    };

    // Сохраняем время начала загрузки
    (this as any)._loadStartTime = Date.now();

    if (extension === 'glb' || extension === 'gltf') {
      new GLTFLoader().load(url, (gltf) => onLoaded(gltf.scene), onProgress, () => alert('Ошибка GLTF'));
    } else if (extension === 'obj') {
      new OBJLoader().load(url, (obj) => onLoaded(obj), onProgress, () => alert('Ошибка OBJ'));
    }
  }

  private onModelReady(root: THREE.Object3D): void {
    this.collectAndEnsure(root);
    const result = centerAndWrap(root);
    this.currentModel = result.wrapper;
    this.baseSizeY = result.sizeY;
    this.currentModel.scale.set(this.modelScale, this.modelScale, this.modelScale);
    this.scene.add(this.currentModel);
    
    // Убеждаемся, что UV режим выключен
    this.disableUVView();
    
    // Передаем текущую модель в MaterialManager
    this.materialManager.setCurrentModel(this.currentModel);
    
    // Применяем глобальную reflectivity к новой модели
    this.materialManager.applyGlobalReflectivity();
    
    this.fitCamera(this.currentModel);
    this.updateGround();
    this.lightManager.placeRig(this.baseSizeY * this.modelScale);
    this.rebuildLayersList();
    this.ensureLayerSelected();
    
    // Сохраняем исходное положение rotation.y при загрузке модели
    if (this.currentModel) {
      this.modelBaseRotationY = this.currentModel.rotation.y;
      this.turntableStartRotationY = this.currentModel.rotation.y;
    }

    // Mini preview rebuild
    this.rebuildMiniPreviewFromCurrentModel();
    
    // Даём время на отрисовку первого кадра перед скрытием загрузки
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Скрываем loading overlay после загрузки модели
        window.dispatchEvent(new Event('modelLoaded'));
      });
    });
  }

  private collectAndEnsure(root: THREE.Object3D): void {
    let totalMeshes = 0;
    // Legacy fallback: первый найденный AO (для старых проектов/логики)
    let modelAoMap: THREE.Texture | null = null;

    // UV-группы: группируем меши по нормал-карте оригинального материала
    // Все меши с одинаковой normal map → одна UV-группа (они разделяют UV-пространство)
    const uvGroupByNormalUuid = new Map<string, string>(); // uuid нормал-карты → id группы
    let uvGroupCounter = 0;
    const foundModelNormalMaps: Record<string, THREE.Texture> = {};
    const foundModelAoMaps: Record<string, THREE.Texture> = {};
    const uvGroupMeshNames: Record<string, string[]> = {};
    const uvGroupDisplayNames: Record<string, string> = {};

    const inferGroupName = (mat: any, ao?: THREE.Texture | null, normal?: THREE.Texture | null): string | null => {
      const clean = (s: any): string | null => {
        if (typeof s !== 'string') return null;
        const t = s.trim();
        return t ? t : null;
      };
      const fromMaterial = clean(mat?.name);
      if (fromMaterial) return fromMaterial;

      const fromTex = (tex?: THREE.Texture | null): string | null => {
        if (!tex) return null;
        // 1) texture.name (редко, но бывает)
        const n1 = clean((tex as any).name);
        if (n1) return n1;
        // 2) image.name (Blender имя изображения)
        const img = (tex as any).image;
        const n2 = clean(img?.name);
        if (n2) return n2;
        // 3) image.src (вырезаем имя файла)
        const src = clean(img?.src);
        if (src) {
          const last = src.split('/').pop() || src;
          const base = last.split('?')[0].split('#')[0];
          const noExt = base.replace(/\.(png|jpg|jpeg|webp|bmp|tga)$/i, '');
          return noExt || base;
        }
        return null;
      };

      return fromTex(ao) || fromTex(normal) || null;
    };
    
    root.traverse((object) => {
      if ((object as any).isMesh) {
        const mesh = object as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        
        // Сохраняем изначальный материал ДО ensurePBR, чтобы не потерять оригинальные текстуры
        if (!(mesh as any).userData.originalMaterial) {
          if (Array.isArray(mesh.material)) {
            (mesh as any).userData.originalMaterial = mesh.material.map(mat => mat.clone());
          } else {
            (mesh as any).userData.originalMaterial = mesh.material.clone();
          }
        }
        
        if (!(mesh.material instanceof Array)) {
          ensurePBR(mesh.material);
          const material = mesh.material as any;
          if (material.aoMap && !modelAoMap) {
            modelAoMap = material.aoMap;
            console.log(`  📦 AO Map обнаружен в материале меша "${mesh.name || 'Unnamed'}" - будет применен ко всей модели`);
          }
        } else {
          mesh.material.forEach(mat => {
            ensurePBR(mat);
            const material = mat as any;
            if (material.aoMap && !modelAoMap) {
              modelAoMap = material.aoMap;
              console.log(`  📦 AO Map обнаружен в материале меша "${mesh.name || 'Unnamed'}" - будет применен ко всей модели`);
            }
          });
        }
        
        // Определяем UV-группу для этого меша (только для не-принт мешей)
        if (!mesh.name.startsWith('Print_')) {
          const origMat = (mesh as any).userData.originalMaterial;
          const firstOrigMat = Array.isArray(origMat) ? origMat[0] : origMat;
          const origNormalMap: THREE.Texture | null = (firstOrigMat as any)?.normalMap || null;
          const origAoMap: THREE.Texture | null = (firstOrigMat as any)?.aoMap || null;

          let groupId: string;
          const lookupKey = origNormalMap ? origNormalMap.uuid : '_no_normal_' + mesh.name;

          if (!uvGroupByNormalUuid.has(lookupKey)) {
            groupId = `uv_group_${uvGroupCounter++}`;
            uvGroupByNormalUuid.set(lookupKey, groupId);
            if (origNormalMap) {
              foundModelNormalMaps[groupId] = origNormalMap;
            }
            if (origAoMap) {
              foundModelAoMaps[groupId] = origAoMap;
            }
            const inferredName = inferGroupName(firstOrigMat, origAoMap, origNormalMap);
            if (inferredName) uvGroupDisplayNames[groupId] = inferredName;
            uvGroupMeshNames[groupId] = [];
          } else {
            groupId = uvGroupByNormalUuid.get(lookupKey)!;
            // Если группа уже есть, но AO ещё не сохранили — берём первый попавшийся
            if (!foundModelAoMaps[groupId] && origAoMap) {
              foundModelAoMaps[groupId] = origAoMap;
            }
            if (!uvGroupDisplayNames[groupId]) {
              const inferredName = inferGroupName(firstOrigMat, origAoMap, origNormalMap);
              if (inferredName) uvGroupDisplayNames[groupId] = inferredName;
            }
          }

          mesh.userData.uvGroup = groupId;
          uvGroupMeshNames[groupId].push(mesh.name || '');
        }
        
        totalMeshes++;
      }
    });

    console.log(`\n📊 Статистика загрузки модели:`);
    console.log(`  • Всего мешей: ${totalMeshes}`);
    if (Object.keys(foundModelAoMaps).length > 0) {
      this.project.model.aoMaps = foundModelAoMaps;
      // Поддерживаем legacy-поле aoMap (первый AO) для старых путей
      if (!this.project.model.aoMap) {
        const firstKey = Object.keys(foundModelAoMaps)[0];
        this.project.model.aoMap = foundModelAoMaps[firstKey];
      }
      console.log(`  ✅ AO Map найден по UV-группам: ${Object.keys(foundModelAoMaps).join(', ')}`);
      setTimeout(() => { this.applyModelAoMap(); }, 100);
    } else if (modelAoMap) {
      // fallback на старое поведение: один AO на всю модель
      this.project.model.aoMap = modelAoMap;
      console.log(`  ✅ AO Map найден (legacy) и будет применен ко всей модели`);
      setTimeout(() => { this.applyModelAoMap(); }, 100);
    } else {
      console.log(`  ⚠️ Модель не содержит AO Map`);
    }

    // Сохраняем нормал-карты модели per-группе
    if (Object.keys(foundModelNormalMaps).length > 0) {
      this.project.model.normalMaps = foundModelNormalMaps;
      const groupNames = Object.keys(foundModelNormalMaps);
      console.log(`  ✅ Нормал-карты модели найдены по группам: ${groupNames.join(', ')}`);
      setTimeout(() => { this.applyModelNormalMaps(); }, 110);
    }

    // Сохраняем информацию об UV-группах в проекте
    if (Object.keys(uvGroupMeshNames).length > 0) {
      this.project.uvGroups = {};
      Object.entries(uvGroupMeshNames).forEach(([gid, meshNames], idx) => {
        this.project.uvGroups![gid] = {
          // Название берём из файла модели (материал/текстура), иначе fallback
          name: uvGroupDisplayNames[gid] || (idx === 0 ? 'Exterior' : `Group ${idx + 1}`),
          meshNames
        };
      });
      console.log(`  ✅ UV-группы: ${JSON.stringify(Object.keys(this.project.uvGroups))}`);
    }
  }
  
  /**
   * Применяет aoMap модели ко всем мешам модели (включая принты).
   * Публичный вызов — для переприменения AO после смены материала на принте (channel/flipY/intensity по UV-группе).
   */
  public async applyModelAoMap(): Promise<void> {
    if (!this.currentModel) return;

    const aoMaps = this.project.model.aoMaps;
    const hasGroupAo = !!aoMaps && Object.keys(aoMaps).length > 0;
    const legacyAo = this.project.model.aoMap;
    if (!hasGroupAo && !legacyAo) return;

    let appliedCount = 0;
    const aoTextureByGroup = new Map<string, THREE.Texture>();
    
    // Берем "эталонные" настройки aoMap из уже загруженной модели (если есть).
    // Это важно для корректного отображения после восстановления из dataURL (flipY/channel/colorSpace/intensity).
    let baseChannel: number | undefined;
    let baseFlipY: boolean | undefined;
    let baseColorSpace: THREE.ColorSpace | undefined;
    let baseIntensity: number | undefined;
    let baseFound = false;
    
    this.currentModel.traverse((object) => {
      if (baseFound) return;
      if (!(object as any).isMesh) return;
      
      const mesh = object as THREE.Mesh;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        const mat = material as any;
        if (mat?.aoMap) {
          const tex = mat.aoMap as THREE.Texture;
          baseChannel = tex.channel;
          baseFlipY = tex.flipY;
          baseColorSpace = tex.colorSpace;
          baseIntensity = mat.aoMapIntensity;
          baseFound = true;
          break;
        }
      }
    });
    
    // Дефолты (на случай если в модели aoMap отсутствует, но в проекте он сохранен)
    const channel = baseChannel ?? 1; // чаще всего AO использует второй UV-set (uv2)
    const flipY = baseFlipY ?? false; // glTF-текстуры обычно flipY=false
    const colorSpace = baseColorSpace ?? THREE.LinearSRGBColorSpace;
    const intensity = baseIntensity ?? 1;

    const resolveAoTexture = async (
      value: THREE.Texture | string | null | undefined,
      groupIdForLog: string
    ): Promise<THREE.Texture | null> => {
      if (!value) return null;
      if (typeof value === 'string') {
        console.log(`  📥 Загружаем AO Map модели (${groupIdForLog}) из dataURL...`);
        try {
          const t = await loadRemoteTexture(value, colorSpace);
          if (!t) {
            console.log(`  ❌ Не удалось загрузить AO Map модели (${groupIdForLog}) из dataURL`);
            return null;
          }
          return t;
        } catch (e) {
          console.error(`  ❌ Ошибка загрузки AO Map модели (${groupIdForLog}):`, e);
          return null;
        }
      }
      if (value instanceof THREE.Texture) return value;
      return null;
    };

    // Подготавливаем AO текстуры по группам (или legacy одну на всё)
    if (hasGroupAo) {
      for (const [gid, value] of Object.entries(aoMaps!)) {
        const t = await resolveAoTexture(value as any, gid);
        if (!t) continue;
        t.channel = channel;
        t.flipY = flipY;
        t.colorSpace = colorSpace;
        t.needsUpdate = true;
        aoTextureByGroup.set(gid, t);
      }
    } else {
      const t = await resolveAoTexture(legacyAo as any, 'legacy');
      if (!t) return;
      t.channel = channel;
      t.flipY = flipY;
      t.colorSpace = colorSpace;
      t.needsUpdate = true;
      aoTextureByGroup.set('__legacy__', t);
    }
    if (aoTextureByGroup.size === 0) return;
    
    // Применяем aoMap ко всем мешам модели (включая принты)
    let regularMeshesCount = 0;
    let printMeshesCount = 0;
    
    this.currentModel.traverse((object) => {
      if ((object as any).isMesh) {
        const mesh = object as THREE.Mesh;
        
        // AO в three.js обычно использует uv2. Если uv2 нет — делаем fallback (uv -> uv2).
        const geometry = mesh.geometry as any;
        if (geometry?.isBufferGeometry) {
          const g = geometry as THREE.BufferGeometry;
          if (!g.attributes.uv2 && g.attributes.uv) {
            g.setAttribute('uv2', g.attributes.uv);
          }
        }
        
        const isPrint = mesh.name.startsWith('Print_');
        const uvGroup = (mesh.userData.uvGroup as string | undefined) || '';
        const groupTexture =
          (hasGroupAo ? aoTextureByGroup.get(uvGroup) : null) ||
          (hasGroupAo ? aoTextureByGroup.values().next().value : null) ||
          aoTextureByGroup.get('__legacy__') ||
          null;
        if (!groupTexture) return;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        
        materials.forEach((material, idx) => {
          // ensurePBR может вернуть НОВЫЙ материал — важно подменить его на меше
          const pbrMaterial = ensurePBR(material) as THREE.Material;
          if (pbrMaterial !== material) {
            if (Array.isArray(mesh.material)) {
              (mesh.material as THREE.Material[])[idx] = pbrMaterial;
            } else {
              mesh.material = pbrMaterial;
            }
          }
          
          const mat = pbrMaterial as any;
          
          // Клонируем текстуру для каждого материала
          const clonedAoMap = groupTexture.clone();
          clonedAoMap.channel = channel;
          clonedAoMap.flipY = flipY;
          clonedAoMap.colorSpace = colorSpace;
          clonedAoMap.needsUpdate = true;
          mat.aoMap = clonedAoMap;
          mat.aoMapIntensity = intensity;
          mat.needsUpdate = true;
          appliedCount++;
          
          if (isPrint) {
            printMeshesCount++;
          } else {
            regularMeshesCount++;
          }
        });
      }
    });
    
    if (appliedCount > 0) {
      console.log(`  ✅ AO Map применен к ${appliedCount} материалам модели`);
      console.log(`    • UV channel: ${channel}`);
      console.log(`    • flipY: ${flipY}`);
      console.log(`    • intensity: ${intensity}`);
      if (regularMeshesCount > 0) {
        console.log(`    • Обычные меши: ${regularMeshesCount}`);
      }
      if (printMeshesCount > 0) {
        console.log(`    • Меши принтов: ${printMeshesCount}`);
      }
    }
  }

  /**
   * Применяет запечённые нормал-карты модели к материалам мешей.
   * Каждый меш получает нормал-карту своей UV-группы через material.userData.modelNormalMap.
   * MaterialManager затем подмешивает её к нормал-карте выбранного материала.
   */
  public applyModelNormalMaps(): void {
    if (!this.currentModel) return;
    const normalMaps = this.project.model.normalMaps;
    if (!normalMaps || Object.keys(normalMaps).length === 0) return;

    let appliedCount = 0;
    this.currentModel.traverse((object) => {
      if (!(object as any).isMesh) return;
      const mesh = object as THREE.Mesh;

      const uvGroup = mesh.userData.uvGroup as string | undefined;
      if (!uvGroup) return;
      const modelNormalMap = normalMaps[uvGroup] as THREE.Texture | null;
      if (!modelNormalMap || typeof modelNormalMap === 'string') return;

      const isPrint = mesh.name.startsWith('Print_');
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach(material => {
        const mat = material as any;
        mat.userData = mat.userData || {};
        mat.userData.modelNormalMap = modelNormalMap;
        if (isPrint) {
          // Принт-меши: нормал-карта модели применяется напрямую — у них нет нормал-карты материала
          mat.normalMap = modelNormalMap;
        }
        mat.needsUpdate = true;
      });
      appliedCount++;
    });

    console.log(`  ✅ Нормал-карты модели распределены по ${appliedCount} мешам`);

    // Уведомляем MaterialManager чтобы он переприменил нормали для выбранных слоёв
    if (this.materialManager) {
      this.materialManager.reapplyModelNormalMaps();
    }
  }

  private fitCamera(model: THREE.Object3D): void {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    const distance = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.4;

    // Ставим камеру строго по оси Z к центру модели, с лёгким подъёмом по Y
    const eye = new THREE.Vector3(
      center.x,
      center.y + size.y * 0.05,
      center.z + distance
    );

    this.camera.position.copy(eye);
    this.controls.target.copy(center);
    this.controls.update();
  }

  public resetCameraToModel(): void {
    if (!this.currentModel) return;
    
    // Сбрасываем поворот модели в исходное положение
    if (this.isModelRotating) {
      this.isModelRotating = false;
    }
    // ВАЖНО: центрирование должно приводить к одному и тому же виду
    // и в режиме поворота сцены, и в режиме поворота модели.
    this.currentModel.rotation.set(0, this.modelBaseRotationY, 0);
    this.turntableStartRotationY = this.currentModel.rotation.y;
    // Если шло "ручное" вращение с инерцией — останавливаем
    if (this.modelRotateInertiaRaf) {
      cancelAnimationFrame(this.modelRotateInertiaRaf);
      this.modelRotateInertiaRaf = 0;
    }
    this.modelRotateDragging = false;
    this.modelRotatePointerId = null;
    
    // Останавливаем любые анимации камеры
    this.camAnimState = null;
    this.turntableState = null;
    
    // Сбрасываем инерцию OrbitControls (damping)
    // Временно отключаем и включаем controls чтобы сбросить внутреннее состояние
    const wasDamping = this.controls.enableDamping;
    this.controls.enableDamping = false;
    this.controls.update();
    this.controls.enableDamping = wasDamping;
    
    // Центрируем камеру
    this.fitCamera(this.currentModel);
  }

  /**
   * Поворачивает МОДЕЛЬ в один из предустановленных ракурсов (камера выравнивается, но зум сохраняется)
   * 1 = Спереди, 2 = Спереди 45° (слева), 3 = Сбоку (слева), 4 = Сзади 45° (слева),
   * 5 = Сзади, 6 = Сзади 45° (справа), 7 = Сбоку (справа), 8 = Спереди 45° (справа)
   */
  public setModelAnglePreset(angle: number): void {
    if (!this.currentModel) return;
    
    // ВАЖНО: После centerAndWrap центр модели находится в (0, 0, 0)
    // Используем фиксированный центр для исключения погрешностей bounding box
    const centerY = 0; // Центр модели по Y
    const heightOffset = this.baseSizeY * this.modelScale * 0.05; // Небольшой подъём камеры
    
    // Сохраняем текущее расстояние от камеры до цели (зум)
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    
    // Целевая позиция камеры:
    // - Сохраняем расстояние (зум)
    // - Выравниваем по оси Z (ровно спереди)
    // - Добавляем небольшой подъём по Y
    const targetCameraPos = new THREE.Vector3(
      0,
      centerY + heightOffset,
      currentDistance
    );
    
    // Целевая точка фокуса - центр модели
    const targetCenter = new THREE.Vector3(0, centerY, 0);
    
    // Углы поворота модели для разных пресетов (в радианах)
    const angles: Record<number, number> = {
      1: 0,                      // Спереди (исходное положение)
      2: -Math.PI / 4,           // Спереди 45° (слева)
      3: -Math.PI / 2,           // Сбоку (слева)
      4: -Math.PI * 3 / 4,       // Сзади 45° (слева)
      5: Math.PI,                // Сзади
      6: Math.PI * 3 / 4,        // Сзади 45° (справа)
      7: Math.PI / 2,            // Сбоку (справа)
      8: Math.PI / 4             // Спереди 45° (справа)
    };
    
    const targetAngle = this.modelBaseRotationY + (angles[angle] ?? 0);
    
    // Нормализуем целевой угол к [-PI, PI]
    let normalizedTarget = targetAngle % (Math.PI * 2);
    if (normalizedTarget > Math.PI) normalizedTarget -= Math.PI * 2;
    if (normalizedTarget < -Math.PI) normalizedTarget += Math.PI * 2;
    
    // Нормализуем текущий угол
    let currentAngle = this.currentModel.rotation.y % (Math.PI * 2);
    if (currentAngle > Math.PI) currentAngle -= Math.PI * 2;
    if (currentAngle < -Math.PI) currentAngle += Math.PI * 2;
    
    // Проверяем, нужна ли анимация
    const angleDiff = Math.abs(normalizedTarget - currentAngle);
    const cameraPosDiff = this.camera.position.distanceTo(targetCameraPos);
    const targetDiff = this.controls.target.distanceTo(targetCenter);
    
    // Отключаем damping для точного позиционирования
    const wasDampingEnabled = this.controls.enableDamping;
    this.controls.enableDamping = false;
    
    // Если уже в нужном положении - устанавливаем точно и выходим
    if (angleDiff < 0.001 && cameraPosDiff < 0.01 && targetDiff < 0.01) {
      this.currentModel.rotation.y = normalizedTarget;
      this.camera.position.copy(targetCameraPos);
      this.controls.target.copy(targetCenter);
      this.controls.update();
      this.controls.enableDamping = wasDampingEnabled;
      return;
    }
    
    // Вычисляем кратчайший путь поворота
    let delta = normalizedTarget - currentAngle;
    if (delta > Math.PI) delta -= Math.PI * 2;
    if (delta < -Math.PI) delta += Math.PI * 2;
    
    const finalAngle = currentAngle + delta;
    
    const startCameraPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const startRot = this.currentModel.rotation.clone();
    const targetRot = new THREE.Euler(0, normalizedTarget, 0);
    
    const duration = 400;
    const startTime = performance.now();
    
    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      
      const lerpAngle = (a: number, b: number, tt: number): number => {
        let d = b - a;
        if (d > Math.PI) d -= Math.PI * 2;
        if (d < -Math.PI) d += Math.PI * 2;
        return a + d * tt;
      };
      this.currentModel!.rotation.set(
        lerpAngle(startRot.x, targetRot.x, eased),
        startRot.y + (finalAngle - startRot.y) * eased,
        lerpAngle(startRot.z, targetRot.z, eased)
      );
      this.camera.position.lerpVectors(startCameraPos, targetCameraPos, eased);
      this.controls.target.lerpVectors(startTarget, targetCenter, eased);
      this.controls.update();
      
      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        // Финальное состояние - точные значения
        this.currentModel!.rotation.set(0, normalizedTarget, 0);
        this.camera.position.copy(targetCameraPos);
        this.controls.target.copy(targetCenter);
        this.controls.update();
        // Восстанавливаем damping
        this.controls.enableDamping = wasDampingEnabled;
      }
    };
    
    animate();
  }
  
  /**
   * Сбрасывает поворот модели в исходное положение
   */
  public resetModelAngle(): void {
    if (!this.currentModel) return;
    
    const targetAngle = this.modelBaseRotationY;
    
    // Нормализуем целевой угол к [-PI, PI]
    let normalizedTarget = targetAngle % (Math.PI * 2);
    if (normalizedTarget > Math.PI) normalizedTarget -= Math.PI * 2;
    if (normalizedTarget < -Math.PI) normalizedTarget += Math.PI * 2;
    
    // Нормализуем текущий угол
    let currentAngle = this.currentModel.rotation.y % (Math.PI * 2);
    if (currentAngle > Math.PI) currentAngle -= Math.PI * 2;
    if (currentAngle < -Math.PI) currentAngle += Math.PI * 2;
    
    // Проверяем, нужна ли анимация
    const angleDiff = Math.abs(normalizedTarget - currentAngle);
    if (angleDiff < 0.001) {
      this.currentModel.rotation.y = normalizedTarget;
      return;
    }
    
    // Вычисляем кратчайший путь
    let delta = normalizedTarget - currentAngle;
    if (delta > Math.PI) delta -= Math.PI * 2;
    if (delta < -Math.PI) delta += Math.PI * 2;
    
    const finalAngle = currentAngle + delta;
    const startRot = this.currentModel.rotation.clone();
    const duration = 400;
    const startTime = performance.now();
    
    const animateModel = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      
      const lerpAngle = (a: number, b: number, tt: number): number => {
        let d = b - a;
        if (d > Math.PI) d -= Math.PI * 2;
        if (d < -Math.PI) d += Math.PI * 2;
        return a + d * tt;
      };
      this.currentModel!.rotation.set(
        lerpAngle(startRot.x, 0, eased),
        startRot.y + (finalAngle - startRot.y) * eased,
        lerpAngle(startRot.z, 0, eased)
      );
      
      if (t < 1) {
        requestAnimationFrame(animateModel);
      } else {
        this.currentModel!.rotation.set(0, normalizedTarget, 0);
      }
    };
    
    animateModel();
  }

  public setCameraPoseStart(): void {
    this.camPoseStart = {
      position: this.camera.position.clone(),
      target: this.controls.target.clone(),
      modelRotation: this.currentModel ? this.currentModel.rotation.clone() : new THREE.Euler(0, 0, 0)
    };
    this.showToolbarMessage(translate('camera.start.set'));
  }
  
  public setCameraPoseEnd(): void {
    this.camPoseEnd = {
      position: this.camera.position.clone(),
      target: this.controls.target.clone(),
      modelRotation: this.currentModel ? this.currentModel.rotation.clone() : new THREE.Euler(0, 0, 0)
    };
    this.showToolbarMessage(translate('camera.end.set'));
  }

  public setCameraArcMode(longArc: boolean): void {
    this.camArcLong = longArc;
    console.log(`🎯 Camera arc mode: ${longArc ? 'long' : 'short'}`);
  }

  public setTurntableMode(enabled: boolean): void {
    this.turntableMode = enabled;
    console.log(`🔄 Turntable mode: ${enabled ? 'enabled' : 'disabled'}`);
  }

  public setAnimDuration(ms: number): void {
    this.animDuration = ms;
    console.log(`⏱️ Animation duration: ${ms}ms`);
  }

  public gotoStart(): void {
    if (this.camPoseStart) {
      this.camera.position.copy(this.camPoseStart.position);
      this.controls.target.copy(this.camPoseStart.target);
      if (this.currentModel) {
        this.currentModel.rotation.copy(this.camPoseStart.modelRotation);
        this.turntableStartRotationY = this.currentModel.rotation.y;
      }
      this.controls.update();
    }
  }

  public gotoEnd(): void {
    if (this.camPoseEnd) {
      this.camera.position.copy(this.camPoseEnd.position);
      this.controls.target.copy(this.camPoseEnd.target);
      if (this.currentModel) {
        this.currentModel.rotation.copy(this.camPoseEnd.modelRotation);
        this.turntableStartRotationY = this.currentModel.rotation.y;
      }
      this.controls.update();
    }
  }

  /**
   * Проверяет, можно ли экспортировать видео
   * Для turntable mode кадры не нужны, для обычного режима нужны оба кадра
   */
  public canExportVideo(): boolean {
    // Turntable mode - кадры не нужны
    if (this.turntableMode) {
      return true;
    }
    // Обычный режим - нужны оба кадра
    return !!(this.camPoseStart && this.camPoseEnd);
  }
  
  public cancelVideoRender(): void {
    this.cancelVideoRecording = true;
  }
  
  public isAnimationPlaying(): boolean {
    return !!(this.camAnimState || this.turntableState);
  }

  /**
   * Показывает сообщение об ошибке, если кадры не установлены
   */
  public showNoFramesError(): void {
    if (!this.turntableMode && (!this.camPoseStart || !this.camPoseEnd)) {
      this.showToolbarMessage(translate('camera.error.noframes'));
    }
  }

  public playCameraAnimation(recordAndSave: boolean, aspectRatio: string = 'free'): void {
    // Если анимация уже идёт, не запускаем новую
    if (this.camAnimState || this.turntableState) {
      return;
    }
    
    // Сохраняем aspectRatio для использования при записи
    this.videoAspectRatio = aspectRatio;
    
    // Turntable mode - вращение модели вокруг оси
    if (this.turntableMode) {
      if (recordAndSave) {
        this.recordTurntableVideo();
      } else {
        // Просмотр turntable анимации (одно полное вращение)
        this.startTurntablePreview();
      }
      return;
    }
    
    if (!this.camPoseStart || !this.camPoseEnd) {
      this.showToolbarMessage(translate('camera.error.noframes'));
      return;
    }
    if (this.isRecordingVideo) {
      alert('Запись уже идёт');
      return;
    }
    if (recordAndSave) {
      this.recordCameraAnimVideo();
      return;
    }
    
    // Уведомляем UI что анимация началась
    window.dispatchEvent(new CustomEvent('animationStarted'));
    
    // Просмотр без записи
    this.camAnimState = {
      startTime: performance.now(),
      duration: this.animDuration,
      startPos: this.camPoseStart.position.clone(),
      endPos: this.camPoseEnd.position.clone(),
      pivot: this.camPoseStart.target.clone().add(this.camPoseEnd.target).multiplyScalar(0.5),
      startDir: new THREE.Vector3(),
      axis: new THREE.Vector3(),
      angle: 0,
      startLen: 0,
      endLen: 0,
      startTarget: this.camPoseStart.target.clone(),
      endTarget: this.camPoseEnd.target.clone(),
      startModelRot: this.camPoseStart.modelRotation.clone(),
      endModelRot: this.camPoseEnd.modelRotation.clone(),
      recording: false
    };
    // Заполняем векторы/длины для дуги
    const pivot = this.camAnimState.pivot;
    const sDir = this.camPoseStart.position.clone().sub(pivot);
    const eDir = this.camPoseEnd.position.clone().sub(pivot);
    this.camAnimState.startLen = sDir.length();
    this.camAnimState.endLen = eDir.length();
    this.camAnimState.startDir = sDir.normalize();
    const eDirNorm = eDir.normalize();
    const dot = THREE.MathUtils.clamp(this.camAnimState.startDir.dot(eDirNorm), -1, 1);
    const axis = new THREE.Vector3().crossVectors(this.camAnimState.startDir, eDirNorm);
    if (axis.lengthSq() < 1e-6) {
      // Если почти сонаправлены — выбираем любую перпендикулярную ось
      axis.set(0, 1, 0).cross(this.camAnimState.startDir);
      if (axis.lengthSq() < 1e-6) axis.set(1, 0, 0); // последний шанс
    }
    axis.normalize();
    const angleShort = Math.acos(dot);
    const angle = this.camArcLong ? (angleShort - 2 * Math.PI) : angleShort;
    this.camAnimState.axis = axis;
    this.camAnimState.angle = angle;
  }

  private startTurntablePreview(): void {
    if (!this.currentModel) {
      alert('Сначала загрузите модель');
      return;
    }
    
    // Если анимация уже идёт, не запускаем новую
    if (this.turntableState) {
      console.log('Turntable animation already playing');
      return;
    }
    
    // Сохраняем начальное вращение модели
    const initialRotationY = this.currentModel.rotation.y;
    
    // Устанавливаем состояние turntable для отслеживания
    // Используем initialAngle для хранения начального вращения модели
    this.turntableState = {
      startTime: performance.now(),
      duration: this.animDuration,
      target: new THREE.Vector3(), // не используется для вращения модели
      radius: 0, // не используется
      height: 0, // не используется  
      initialAngle: initialRotationY // начальное вращение модели
    };
    
    // Уведомляем UI что анимация началась
    window.dispatchEvent(new CustomEvent('animationStarted'));
  }

  /**
   * Проверяет поддержку WebCodecs API для надёжного экспорта видео
   */
  private isWebCodecsSupported(): boolean {
    return 'VideoEncoder' in window && 'VideoFrame' in window;
  }

  private getVideoResolution(): { width: number; height: number } {
    const aspectRatios: Record<string, number> = {
      'free': 16 / 9, // по умолчанию 16:9
      '1:1': 1,
      '16:9': 16 / 9,
      '9:16': 9 / 16
    };
    
    const aspect = aspectRatios[this.videoAspectRatio] || (16 / 9);
    
    // Используем 1440p как базовое разрешение для высокого качества
    const baseHeight = 1440;
    
    if (aspect >= 1) {
      // Горизонтальный или квадратный
      return { width: Math.round(baseHeight * aspect), height: baseHeight };
    } else {
      // Вертикальный
      return { width: baseHeight, height: Math.round(baseHeight / aspect) };
    }
  }

  public async recordTurntableVideo(): Promise<void> {
    if (!this.currentModel) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Сначала загрузите модель');
      return;
    }
    if (this.isRecordingVideo) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Запись уже идёт');
      return;
    }
    
    this.isRecordingVideo = true;
    this.cancelVideoRecording = false;
    
    // Показываем кнопку отмены
    window.dispatchEvent(new CustomEvent('showCancelButton'));
    
    // Параметры видео - 60 FPS для плавности
    const fps = 60;
    const durationSec = this.animDuration / 1000; // Используем настроенную длительность
    const totalFrames = Math.round(fps * durationSec);
    const frameDurationMicros = 1_000_000 / fps; // Длительность кадра в микросекундах
    
    // Сохраняем состояние
    const prevSize = { w: this.renderer.getSize(new THREE.Vector2()).x, h: this.renderer.getSize(new THREE.Vector2()).y };
    const prevPixelRatio = this.renderer.getPixelRatio();
    const prevControlsEnabled = this.controls.enabled;
    const prevCamPos = this.camera.position.clone();
    const prevTarget = this.controls.target.clone();
    const prevAspect = this.camera.aspect;
    this.controls.enabled = false;
    
    // Устанавливаем размер в соответствии с выбранным aspect ratio
    const { width, height } = this.getVideoResolution();
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    // Сохраняем начальное вращение модели
    const initialModelRotation = this.currentModel.rotation.y;
    
    // Показываем прогресс через overlay
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: 'Рендеринг видео…', progress: 0 } 
    }));
    
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    
    // Пробуем WebCodecs (надёжный 60fps независимо от производительности)
    if (this.isWebCodecsSupported()) {
      try {
        await this.recordWithWebCodecs(
          width, height, fps, totalFrames, frameDurationMicros,
          (frame, total) => {
            const t = frame / total;
            // Вращаем МОДЕЛЬ на полный оборот
            this.currentModel!.rotation.y = initialModelRotation + t * Math.PI * 2;
          },
          `turntable-${this.videoAspectRatio}-${Date.now()}.mp4`
        );
        if (isDemoMode()) consumeDemoQuota('video');
        // Возвращаем модель к начальному углу
        this.currentModel.rotation.y = initialModelRotation;
        this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
        return;
      } catch (e) {
        console.warn('WebCodecs failed, falling back to MediaRecorder:', e);
      }
    }
    
    // Fallback на MediaRecorder
    const stream = canvas.captureStream(fps);
    if (!stream) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Браузер не поддерживает запись canvas');
      this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
      return;
    }
    
    const chunks: BlobPart[] = [];
    const mimeTypes = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ];
    
    let recorder: MediaRecorder | null = null;
    for (const m of mimeTypes) {
      if (MediaRecorder.isTypeSupported(m)) {
        recorder = new MediaRecorder(stream, { mimeType: m, videoBitsPerSecond: 20_000_000 });
        break;
      }
    }
    
    if (!recorder) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Не удалось инициализировать запись видео');
      this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
      return;
    }
    
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    
    const frameDelay = 1000 / fps;
    recorder.start();
    
    for (let frame = 0; frame < totalFrames; frame++) {
      // Проверяем флаг отмены
      if (this.cancelVideoRecording) {
        recorder.stop();
        stream.getTracks().forEach(track => track.stop());
        this.currentModel.rotation.y = initialModelRotation;
        window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
        this.showToolbarMessage(translate('message.render.cancelled'));
        this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
        return;
      }
      
      const t = frame / totalFrames;
      // Вращаем МОДЕЛЬ на полный оборот
      this.currentModel.rotation.y = initialModelRotation + t * Math.PI * 2;
      this.renderFrame();
      
      await new Promise(resolve => setTimeout(resolve, frameDelay));
      
      if (frame % Math.floor(totalFrames / 10) === 0) {
        const progress = Math.round((frame / totalFrames) * 100);
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
          detail: { text: translate('loading.render.video'), progress } 
        }));
      }
    }
    
    // Возвращаем модель к начальному углу
    this.currentModel.rotation.y = initialModelRotation;
    
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.video'), progress: 100 } 
    }));
    
    const donePromise = new Promise<void>((resolve) => {
      recorder!.onstop = () => {
        const blob = new Blob(chunks, { type: recorder!.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `turntable-${this.videoAspectRatio}-${Date.now()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        resolve();
      };
    });
    
    recorder.stop();
    await donePromise;
    
    // Скрываем overlay
    window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
    
    this.showToolbarMessage('Видео сохранено!');
    if (isDemoMode()) consumeDemoQuota('video');
    
    this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
  }
  
  /**
   * Надёжный экспорт видео через WebCodecs + mp4-muxer
   * Гарантированные 60fps независимо от производительности рендеринга
   * Формат: MP4 с H.264 кодеком - максимальная совместимость
   * Supersampling 2x для антиалиасинга
   */
  private async recordWithWebCodecs(
    width: number,
    height: number,
    fps: number,
    totalFrames: number,
    frameDurationMicros: number,
    updateScene: (frame: number, total: number) => void,
    filename: string
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      let encoderError: Error | null = null;
      
      // Supersampling: рендерим в 2x разрешении для антиалиасинга
      const ssaaFactor = 2;
      const renderWidth = width * ssaaFactor;
      const renderHeight = height * ssaaFactor;
      
      // Создаём offscreen canvas для даунскейла
      const downsampleCanvas = document.createElement('canvas');
      downsampleCanvas.width = width;
      downsampleCanvas.height = height;
      const ctx = downsampleCanvas.getContext('2d', { 
        alpha: false,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      }) as CanvasRenderingContext2D | null;
      
      if (!ctx) {
        throw new Error('Не удалось создать 2D контекст для экспорта видео');
      }
      
      // Создаём MP4 muxer
      const muxer = new Mp4Muxer({
        target: new ArrayBufferTarget(),
        video: {
          codec: 'avc',
          width,
          height
        },
        fastStart: 'in-memory'
      });
      
      // Создаём видео энкодер с H.264
      const encoder = new (window as any).VideoEncoder({
        output: (chunk: any, meta: any) => {
          muxer.addVideoChunk(chunk, meta);
        },
        error: (e: Error) => {
          console.error('Encoder error:', e);
          encoderError = e;
        }
      });
      
      try {
        // Выбираем уровень H.264 в зависимости от разрешения
        const pixels = width * height;
        const level = pixels > 2_097_152 ? '33' : '28';
        
        encoder.configure({
          codec: `avc1.6400${level}`,
          width,
          height,
          bitrate: 20_000_000,
          framerate: fps,
          avc: { format: 'avc' }
        });
      } catch (e) {
        reject(e);
        return;
      }
      
      const glCanvas = this.renderer.domElement as HTMLCanvasElement;
      
      // Сохраняем оригинальный размер рендерера
      const origSize = this.renderer.getSize(new THREE.Vector2());
      const origPixelRatio = this.renderer.getPixelRatio();
      const origAspect = this.camera.aspect;
      
      // Устанавливаем размер для supersampling
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(renderWidth, renderHeight, false);
      this.updateComposerSizeFromRenderer();
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      
      // Время для расчета ETA
      const renderStartTime = performance.now();
      let lastEtaText = '';
      
      try {
        for (let frame = 0; frame < totalFrames; frame++) {
          // Проверяем флаг отмены
          if (this.cancelVideoRecording) {
            encoder.close();
            throw new Error('Запись отменена пользователем');
          }
          
          if (encoderError) {
            throw encoderError;
          }
          
          // Обновляем сцену
          updateScene(frame, totalFrames);
          
          // Рендерим в высоком разрешении
          this.renderFrame();
          
          // Даунскейл с высококачественной интерполяцией (антиалиасинг)
          ctx.drawImage(glCanvas, 0, 0, renderWidth, renderHeight, 0, 0, width, height);
          
          // Создаём VideoFrame из уменьшенного canvas
          const videoFrame = new (window as any).VideoFrame(downsampleCanvas, {
            timestamp: frame * frameDurationMicros,
            duration: frameDurationMicros
          });
          
          const isKeyFrame = frame % 30 === 0;
          encoder.encode(videoFrame, { keyFrame: isKeyFrame });
          videoFrame.close();
          
          // Контроль очереди
          while (encoder.encodeQueueSize > 2) {
            await new Promise(r => setTimeout(r, 5));
          }
          
          // Прогресс с ETA
          if (frame % Math.max(1, Math.floor(totalFrames / 100)) === 0) {
            const percent = Math.round((frame / totalFrames) * 100);
            
            // Расчет примерного времени (после первых 10 кадров)
            if (frame > 10) {
              const now = performance.now();
              const elapsed = now - renderStartTime;
              const avgTimePerFrame = elapsed / frame;
              const remainingFrames = totalFrames - frame;
              const etaMs = avgTimePerFrame * remainingFrames;
              const etaSec = Math.ceil(etaMs / 1000);
              
              if (etaSec > 60) {
                const minutes = Math.floor(etaSec / 60);
                const seconds = etaSec % 60;
                lastEtaText = `${minutes}:${seconds.toString().padStart(2, '0')}`;
              } else {
                lastEtaText = `${etaSec} сек`;
              }
            }
            
            window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
              detail: { 
                text: translate('loading.render.video'), 
                progress: percent,
                eta: lastEtaText,
                warning: translate('loading.video.warning')
              } 
            }));
            await new Promise(r => setTimeout(r, 0));
          }
        }
        
        await encoder.flush();
        encoder.close();
        
        if (encoderError) {
          throw encoderError;
        }
        
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
          detail: { text: translate('loading.build.mp4'), progress: 100 } 
        }));
        
        muxer.finalize();
        const { buffer } = muxer.target as ArrayBufferTarget;
        
        const blob = new Blob([buffer], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        
        // Скрываем overlay
        window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
        
        this.showToolbarMessage('Видео сохранено!');
        setTimeout(() => this.hideToolbarMessage(), 2000);
        
        resolve();
      } catch (e) {
        try {
          if (encoder.state !== 'closed') {
            encoder.close();
          }
        } catch {}
        reject(e);
      } finally {
        // Восстанавливаем размер рендерера
        this.renderer.setPixelRatio(origPixelRatio);
        this.renderer.setSize(origSize.x, origSize.y, false);
        this.camera.aspect = origAspect;
        this.camera.updateProjectionMatrix();
      }
    });
  }
  
  private restoreAfterRecording(
    prevSize: { w: number; h: number },
    prevPixelRatio: number,
    prevAspect: number,
    prevControlsEnabled: boolean,
    prevCamPos: THREE.Vector3,
    prevTarget: THREE.Vector3
  ): void {
    this.renderer.setSize(prevSize.w, prevSize.h, false);
    this.renderer.setPixelRatio(prevPixelRatio);
    this.camera.aspect = prevAspect || (prevSize.w / prevSize.h);
    this.camera.updateProjectionMatrix();
    this.controls.enabled = prevControlsEnabled;
    this.camera.position.copy(prevCamPos);
    this.controls.target.copy(prevTarget);
    this.controls.update();
    this.isRecordingVideo = false;
  }

  private rebuildLayersList(): void {
    // Ищем новый контейнер или старый
    const layersBox = document.getElementById('layers-container') || document.getElementById('layers');
    if (!layersBox) return;
    
    // Закрываем палитру цвета, если список слоёв пересобирается
    this.hidePrintLayerColorPalette();

    layersBox.innerHTML = '';
    const items: THREE.Mesh[] = [];
    
    this.currentModel?.traverse((object) => {
      if ((object as any).isMesh) {
        items.push(object as THREE.Mesh);
      }
    });

    // Группируем принты по printId в userData
    const printMeshes = items.filter(mesh => mesh.name.startsWith('Print_'));
    const regularMeshes = items.filter(mesh => !mesh.name.startsWith('Print_'));
    const printGroups: Record<string, THREE.Mesh[]> = {};
    printMeshes.forEach(mesh => {
      const pid = (mesh as any).userData?.printId;
      if (!pid) return;
      (printGroups[pid] ||= []).push(mesh);
    });
    
    // Очищаем кеш слоёв для мультивыбора
    this.allLayerElements = [];
    this.allLayerMeshes.clear();
    this.lastAnchorIndex = -1;

    // Чтобы окраска и материал применялись по мешу, а не по общему материалу GLTF,
    // даём каждому мешу свой экземпляр материала (клонируем, если материал общий).
    const materialUseCount = new Map<THREE.Material, number>();
    regularMeshes.forEach(mesh => {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((m: THREE.Material) => materialUseCount.set(m, (materialUseCount.get(m) ?? 0) + 1));
    });
    regularMeshes.forEach(mesh => {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const shared = mats.some((m: THREE.Material) => (materialUseCount.get(m) ?? 0) > 1);
      if (!shared) return;
      if (Array.isArray(mesh.material)) {
        mesh.material = (mesh.material as THREE.Material[]).map((m: THREE.Material) =>
          (materialUseCount.get(m) ?? 0) > 1 ? m.clone() : m
        );
      } else {
        mesh.material = (materialUseCount.get(mesh.material as THREE.Material) ?? 0) > 1
          ? (mesh.material as THREE.Material).clone()
          : mesh.material;
      }
    });

    // Добавляем обычные меши
    regularMeshes.forEach((mesh, index) => {
      const element = document.createElement('div');
      element.className = 'layer';
      element.dataset.id = mesh.uuid;
      
      const name = mesh.name || `Mesh ${index + 1}`;
      
      element.innerHTML = `
        <span style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${name}</span>
      `;
      
      element.onclick = (e) => this.selectLayer(mesh, element, e.shiftKey, e.ctrlKey || e.metaKey);
      layersBox.appendChild(element);
      
      // Сохраняем для мультивыбора
      this.allLayerElements.push(element);
      this.allLayerMeshes.set(mesh.uuid, mesh);
      
      if (!this.project.layers[mesh.uuid]) {
        // Извлекаем текстуры из исходного материала модели (БЕЗ aoMap - он для всей модели)
        const maps: Record<string, THREE.Texture | null> = {};
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        
        if (materials.length > 0) {
          const material = ensurePBR(materials[0]) as any;
          
          // Сохраняем все PBR карты из исходного материала модели (кроме aoMap)
          if (material.map) {
            maps.color = material.map;
            maps.map = material.map; // Для обратной совместимости
          }
          if (material.normalMap) {
            maps.normal = material.normalMap;
          }
          if (material.roughnessMap) {
            maps.roughness = material.roughnessMap;
          }
          // aoMap НЕ сохраняем здесь - он для всей модели, не для отдельных слоев
          if (material.metalnessMap) {
            maps.metalness = material.metalnessMap;
          }
          if ((material as any).alphaMap) {
            maps.opacity = (material as any).alphaMap;
          }
        }
        
        const layerData: LayerData = {
          name: name,
          maps: maps,
          params: {},
          preset: null,
          type: 'layer'
        };
        this.project.layers[mesh.uuid] = layerData;
      }
    });
    
    // Добавляем отдельный слой для каждого принта из printGroups (принты с мешами в сцене)
    let printIndex = 0;
    console.log(`🔄 rebuildLayersList: printGroups keys:`, Object.keys(printGroups));
    Object.keys(printGroups).forEach((printId, idx) => {
      console.log(`🔄 rebuildLayersList: обрабатываем printId=${printId}, hasCanvasData=${!!this.project.layers[printId]?.canvasData}`);
      const element = document.createElement('div');
      element.className = 'layer print-design';
      element.dataset.id = printId;

      const fallbackName = idx === 0 ? 'Print Design' : `Print Design ${idx + 1}`;
      const layerData = this.ensurePrintLayerData(printId, fallbackName);

      // Используем имя из project.layers если оно есть
      const printName = layerData.name || fallbackName;
      // Проверяем видимость слоя (по умолчанию видимый)
      const isVisible = layerData.visible !== false;
      const labelColor = (layerData as any).labelColor || this.defaultPrintLabelColor(printId);
      element.innerHTML = `
        <div class="print-layer-left">
          <div class="print-layer-color-dot" style="background:${labelColor};"></div>
          <span class="print-layer-name" style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${!isVisible ? 'opacity:0.5' : ''}">${printName}</span>
        </div>
        <div class="print-icon" style="cursor: pointer; margin-left: 8px;"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.8571 0.996623L12.3507 1.85893C13.8387 2.71804 14.3486 4.62106 13.4895 6.10908L10.0437 12.0774C9.71624 12.6446 9.2222 13.0916 8.62667 13.3443C7.80097 13.6945 6.62766 14.1736 5.60021 14.5277C5.08502 14.7052 4.61846 14.8474 4.25584 14.9265C4.0739 14.9662 3.93137 14.9866 3.8293 14.9914C3.80681 14.9925 3.78817 14.9902 3.77383 14.9899C3.76643 14.9776 3.75584 14.963 3.74557 14.9431C3.69868 14.8523 3.64523 14.7188 3.58856 14.5412C3.47573 14.1876 3.3648 13.7122 3.26092 13.1771C3.05383 12.1102 2.88204 10.8546 2.77254 9.96438C2.69357 9.32232 2.83371 8.67094 3.16117 8.10375L6.60697 2.13545C7.46608 0.647426 9.36911 0.137513 10.8571 0.996623Z" fill="#7F7F7F" stroke="#7F7F7F" stroke-width="0.889"/></svg></div>
        <button class="visibility-btn" data-action="visibility" title="${isVisible ? 'Скрыть слой' : 'Показать слой'}" style="margin-left: 8px; padding: 0; background: none; border: none; cursor: pointer; display: flex; align-items: center;">
          ${isVisible 
            ? '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.024 3c3.344 0 5.642 1.83 6.895 3.201.784.858.82 2.118.056 2.994-1.193 1.367-3.43 3.163-6.951 3.163s-5.758-1.796-6.95-3.163c-.765-.876-.73-2.136.054-2.994C2.381 4.831 4.68 3.001 8.024 3m.004 2.006a2.674 2.674 0 1 0 0 5.348 2.674 2.674 0 0 0 0-5.348" fill="#7f7f7f"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.62 6.054a.435.435 0 0 1 .728.468l-.01.018-.021.04-.08.143c-.071.12-.177.29-.318.49a9 9 0 0 1-.913 1.096l.476.477a.61.61 0 0 1-.86.861l-.53-.528c-.4.308-.856.604-1.374.861l.47.814a.61.61 0 0 1-1.055.61l-.557-.963a8.4 8.4 0 0 1-2.004.371v1.014a.61.61 0 0 1-1.218 0V10.81a8.4 8.4 0 0 1-1.817-.323l-.528.917a.61.61 0 0 1-1.056-.61l.43-.746A8.3 8.3 0 0 1 2.727 9.01l-.637.638a.609.609 0 0 1-.861-.861l.602-.604a9 9 0 0 1-.79-.969 7 7 0 0 1-.398-.632L.62 6.54l-.008-.016v-.001a.436.436 0 0 1 .728-.468l.002.003.016.019.07.08c.066.071.168.176.305.303.273.255.687.599 1.24.943C4.082 8.09 5.749 8.78 7.98 8.78s3.898-.691 5.005-1.378a8 8 0 0 0 1.24-.943 6 6 0 0 0 .376-.383l.015-.019z" fill="#7f7f7f"/></svg>'
          }
        </button>
        <button class="delete-print-btn" data-action="delete" style="margin-left: 8px; padding: 0; background: none; border: none; cursor: pointer; display: flex; align-items: center;">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.5 2a1.5 1.5 0 0 1 0 3H13v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5h-.5a1.5 1.5 0 1 1 0-3zM6 6a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V7a1 1 0 0 0-1-1m4 0a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V7a1 1 0 0 0-1-1" fill="#7f7f7f"/></svg>
        </button>
      `;

      // Инлайн-редактирование имени по двойному клику (только принты)
      const nameSpan = element.querySelector('.print-layer-name') as HTMLElement | null;
      if (nameSpan) {
        nameSpan.addEventListener('dblclick', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.startPrintLayerNameEdit(printId, element);
        });
      }

      // Выбор цвета кружка (только принты) — мини-панель с готовыми цветами
      const colorDot = element.querySelector('.print-layer-color-dot') as HTMLElement | null;
      if (colorDot) {
        colorDot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.showPrintLayerColorPalette(printId, colorDot);
        });
      }

      element.onclick = (e) => {
        const target = e.target as HTMLElement;
        // Если клик по кнопке видимости - только переключаем видимость (без выделения слоя)
        if (target.classList.contains('visibility-btn') || target.closest('.visibility-btn')) {
          e.stopPropagation();
          this.togglePrintVisibility(printId);
          return;
        }
        // Если клик по кнопке удаления
        if (target.classList.contains('delete-print-btn') || target.closest('.delete-print-btn')) {
          e.stopPropagation();
          this.deleteLayer(printId);
          return;
        }
        // Если клик по кружку/палитре/инлайн-редактору — не меняем выделение
        if (target.closest('.print-layer-color-dot') || target.closest('.print-layer-color-palette') || target.classList.contains('print-layer-name-input')) {
          e.stopPropagation();
          return;
        }
        // Если клик по иконке - открываем редактор
        if (target.classList.contains('print-icon') || target.closest('.print-icon') || target.closest('svg:not(.visibility-btn svg)')) {
          e.stopPropagation();
          this.selectPrintLayer(element, e.shiftKey, e.ctrlKey || e.metaKey);
          window.dispatchEvent(new CustomEvent('editPrint'));
        } else {
          this.selectPrintLayer(element, e.shiftKey, e.ctrlKey || e.metaKey);
        }
      };
      layersBox.appendChild(element);
      
      // Добавляем в массивы для мультивыбора
      this.allLayerElements.push(element);
      const firstPrintMesh = printGroups[printId][0];
      if (firstPrintMesh) {
        this.allLayerMeshes.set(printId, firstPrintMesh);
      }

      // Создаём запись слоя, если отсутствует
      if (!this.project.layers[printId]) {
        this.project.layers[printId] = {
          name: printName,
          maps: {},
          params: {},
          preset: null,
          type: 'print'
        } as any;
      }
      
      printIndex++;
    });
    
    // Также добавляем принты из project.layers, которых нет в сцене (восстанавливаемые)
    Object.keys(this.project.layers).forEach((layerId) => {
      const layerData = this.project.layers[layerId];
      if (layerData.type === 'print' && !printGroups[layerId] && !document.querySelector(`[data-id="${layerId}"]`)) {
        // Принт есть в project.layers, но нет мешей в сцене - добавляем в список
        const element = document.createElement('div');
        element.className = 'layer print-design';
        element.dataset.id = layerId;

        const ensured = this.ensurePrintLayerData(layerId, layerData.name || 'Print Design');
        const printName = ensured.name || 'Print Design';
        // Проверяем видимость слоя (по умолчанию видимый)
        const isVisible = ensured.visible !== false;
        const labelColor = (ensured as any).labelColor || this.defaultPrintLabelColor(layerId);
        element.innerHTML = `
          <div class="print-layer-left">
            <div class="print-layer-color-dot" style="background:${labelColor};"></div>
            <span class="print-layer-name" style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${!isVisible ? 'opacity:0.5' : ''}">${printName}</span>
          </div>
          <div class="print-icon" style="cursor: pointer; margin-left: 8px;"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.8571 0.996623L12.3507 1.85893C13.8387 2.71804 14.3486 4.62106 13.4895 6.10908L10.0437 12.0774C9.71624 12.6446 9.2222 13.0916 8.62667 13.3443C7.80097 13.6945 6.62766 14.1736 5.60021 14.5277C5.08502 14.7052 4.61846 14.8474 4.25584 14.9265C4.0739 14.9662 3.93137 14.9866 3.8293 14.9914C3.80681 14.9925 3.78817 14.9902 3.77383 14.9899C3.76643 14.9776 3.75584 14.963 3.74557 14.9431C3.69868 14.8523 3.64523 14.7188 3.58856 14.5412C3.47573 14.1876 3.3648 13.7122 3.26092 13.1771C3.05383 12.1102 2.88204 10.8546 2.77254 9.96438C2.69357 9.32232 2.83371 8.67094 3.16117 8.10375L6.60697 2.13545C7.46608 0.647426 9.36911 0.137513 10.8571 0.996623Z" fill="#7F7F7F" stroke="#7F7F7F" stroke-width="0.889"/></svg></div>
          <button class="visibility-btn" data-action="visibility" title="${isVisible ? 'Скрыть слой' : 'Показать слой'}" style="margin-left: 8px; padding: 0; background: none; border: none; cursor: pointer; display: flex; align-items: center;">
            ${isVisible 
              ? '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.024 3c3.344 0 5.642 1.83 6.895 3.201.784.858.82 2.118.056 2.994-1.193 1.367-3.43 3.163-6.951 3.163s-5.758-1.796-6.95-3.163c-.765-.876-.73-2.136.054-2.994C2.381 4.831 4.68 3.001 8.024 3m.004 2.006a2.674 2.674 0 1 0 0 5.348 2.674 2.674 0 0 0 0-5.348" fill="#7f7f7f"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.62 6.054a.435.435 0 0 1 .728.468l-.01.018-.021.04-.08.143c-.071.12-.177.29-.318.49a9 9 0 0 1-.913 1.096l.476.477a.61.61 0 0 1-.86.861l-.53-.528c-.4.308-.856.604-1.374.861l.47.814a.61.61 0 0 1-1.055.61l-.557-.963a8.4 8.4 0 0 1-2.004.371v1.014a.61.61 0 0 1-1.218 0V10.81a8.4 8.4 0 0 1-1.817-.323l-.528.917a.61.61 0 0 1-1.056-.61l.43-.746A8.3 8.3 0 0 1 2.727 9.01l-.637.638a.609.609 0 0 1-.861-.861l.602-.604a9 9 0 0 1-.79-.969 7 7 0 0 1-.398-.632L.62 6.54l-.008-.016v-.001a.436.436 0 0 1 .728-.468l.002.003.016.019.07.08c.066.071.168.176.305.303.273.255.687.599 1.24.943C4.082 8.09 5.749 8.78 7.98 8.78s3.898-.691 5.005-1.378a8 8 0 0 0 1.24-.943 6 6 0 0 0 .376-.383l.015-.019z" fill="#7f7f7f"/></svg>'
            }
          </button>
          <button class="delete-print-btn" data-action="delete" style="margin-left: 8px; padding: 0; background: none; border: none; cursor: pointer; display: flex; align-items: center;">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.5 2a1.5 1.5 0 0 1 0 3H13v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5h-.5a1.5 1.5 0 1 1 0-3zM6 6a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V7a1 1 0 0 0-1-1m4 0a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V7a1 1 0 0 0-1-1" fill="#7f7f7f"/></svg>
          </button>
        `;

        // Инлайн-редактирование имени по двойному клику (только принты)
        const nameSpan = element.querySelector('.print-layer-name') as HTMLElement | null;
        if (nameSpan) {
          nameSpan.addEventListener('dblclick', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.startPrintLayerNameEdit(layerId, element);
          });
        }

        // Выбор цвета кружка (только принты) — мини-панель с готовыми цветами
        const colorDot = element.querySelector('.print-layer-color-dot') as HTMLElement | null;
        if (colorDot) {
          colorDot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.showPrintLayerColorPalette(layerId, colorDot);
          });
        }

        element.onclick = (e) => {
          const target = e.target as HTMLElement;
          // Если клик по кнопке видимости - только переключаем видимость (без выделения слоя)
          if (target.classList.contains('visibility-btn') || target.closest('.visibility-btn')) {
            e.stopPropagation();
            this.togglePrintVisibility(layerId);
            return;
          }
          if (target.classList.contains('delete-print-btn') || target.closest('.delete-print-btn')) {
            e.stopPropagation();
            this.deleteLayer(layerId);
            return;
          }
          // Если клик по кружку/палитре/инлайн-редактору — не меняем выделение
          if (target.closest('.print-layer-color-dot') || target.closest('.print-layer-color-palette') || target.classList.contains('print-layer-name-input')) {
            e.stopPropagation();
            return;
          }
          if (target.classList.contains('print-icon') || target.closest('.print-icon') || target.closest('svg:not(.visibility-btn svg)')) {
            e.stopPropagation();
            this.selectPrintLayer(element, e.shiftKey, e.ctrlKey || e.metaKey);
            window.dispatchEvent(new CustomEvent('editPrint'));
          } else {
            this.selectPrintLayer(element, e.shiftKey, e.ctrlKey || e.metaKey);
          }
        };
        layersBox.appendChild(element);
      }
    });
    
    // Автоматически выделяем верхний слой, если ничего не выделено
    this.ensureLayerSelected();
  }

  private hslToHex(h: number, s: number, l: number): string {
    // h: 0..360, s/l: 0..100
    const hh = ((h % 360) + 360) % 360;
    const ss = Math.max(0, Math.min(100, s)) / 100;
    const ll = Math.max(0, Math.min(100, l)) / 100;

    const c = (1 - Math.abs(2 * ll - 1)) * ss;
    const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
    const m = ll - c / 2;

    let r = 0, g = 0, b = 0;
    if (hh < 60) { r = c; g = x; b = 0; }
    else if (hh < 120) { r = x; g = c; b = 0; }
    else if (hh < 180) { r = 0; g = c; b = x; }
    else if (hh < 240) { r = 0; g = x; b = c; }
    else if (hh < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }

    const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private defaultPrintLabelColor(printId: string): string {
    // Детерминированный цвет по ID, чтобы разные принты визуально отличались
    let hash = 0;
    for (let i = 0; i < printId.length; i++) {
      hash = (hash * 31 + printId.charCodeAt(i)) >>> 0;
    }
    const hue = hash % 360;
    return this.hslToHex(hue, 68, 56);
  }

  private ensurePrintLayerData(printId: string, fallbackName: string): LayerData {
    const existing = this.project.layers[printId] as any;
    if (!existing) {
      const created: LayerData = {
        name: fallbackName,
        maps: {},
        params: {},
        preset: null,
        type: 'print',
        visible: true,
        labelColor: this.defaultPrintLabelColor(printId),
      };
      this.project.layers[printId] = created;
      return created;
    }

    if (!existing.type) existing.type = 'print';
    if (!existing.name) existing.name = fallbackName;
    if (existing.visible === undefined) existing.visible = true;
    if (!existing.labelColor) existing.labelColor = this.defaultPrintLabelColor(printId);
    // Нормализуем uvGroupIds для старых проектов
    if (!existing.uvGroupIds || !Array.isArray(existing.uvGroupIds)) {
      if (typeof existing.uvGroupId === 'string' && existing.uvGroupId) {
        existing.uvGroupIds = [existing.uvGroupId];
      }
    }
    // Авто-инференс uvGroupIds, если принт-меши уже есть
    if ((!existing.uvGroupIds || existing.uvGroupIds.length === 0) && this.currentModel) {
      const allMeshes = this.getPrintMeshesById(printId);
      const meshes = allMeshes.filter((m) => m.visible);
      const sourceMeshes = meshes.length > 0 ? meshes : allMeshes;
      const groups = new Set<string>();
      sourceMeshes.forEach((m) => {
        const gid = (m as any).userData?.uvGroup;
        if (typeof gid === 'string' && gid) groups.add(gid);
      });
      existing.uvGroupIds = Array.from(groups);
      if (!existing.uvGroupId && existing.uvGroupIds.length === 1) {
        existing.uvGroupId = existing.uvGroupIds[0];
      }
    }
    return existing as LayerData;
  }

  private startPrintLayerNameEdit(printId: string, element: HTMLElement): void {
    const layer = this.project.layers[printId];
    if (!layer || layer.type !== 'print') return;

    const left = element.querySelector('.print-layer-left') as HTMLElement | null;
    const nameSpan = element.querySelector('.print-layer-name') as HTMLElement | null;
    if (!left || !nameSpan) return;

    // Уже редактируем
    if (left.querySelector('input.print-layer-name-input')) return;

    const currentName = (layer.name || nameSpan.textContent || '').toString();
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'print-layer-name-input';
    input.value = currentName;
    input.autocomplete = 'off';
    input.spellcheck = false;

    // Визуально и по ширине как текст
    input.style.flex = '1';
    input.style.minWidth = '0';
    input.style.background = 'rgba(255,255,255,0.06)';
    input.style.border = '1px solid rgba(255,255,255,0.14)';
    input.style.borderRadius = '6px';
    input.style.padding = '2px 6px';
    input.style.color = 'inherit';
    input.style.font = 'inherit';
    input.style.outline = 'none';
    input.style.lineHeight = '1rem';

    // Если слой скрыт — сохраняем ту же полупрозрачность
    const opacity = (nameSpan as any).style?.opacity;
    if (opacity) input.style.opacity = opacity;

    nameSpan.style.display = 'none';
    left.appendChild(input);

    const finish = (save: boolean) => {
      const raw = input.value ?? '';
      const next = raw.trim();
      if (save) {
        const finalName = next.length > 0 ? next : currentName || 'Print Design';
        layer.name = finalName;
        nameSpan.textContent = finalName;
      }
      input.remove();
      nameSpan.style.display = '';
    };

    input.addEventListener('click', (e) => e.stopPropagation());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        finish(true);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        finish(false);
      }
    });
    input.addEventListener('blur', () => finish(true));

    // Фокус после вставки в DOM
    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
  }

  private hidePrintLayerColorPalette(): void {
    const existing = document.querySelector('.print-layer-color-palette') as HTMLElement | null;
    if (existing) {
      const cleanup = (existing as any)._cleanup as (() => void) | undefined;
      if (cleanup) cleanup();
      existing.remove();
    }
  }

  private showPrintLayerColorPalette(printId: string, anchorDot: HTMLElement): void {
    const layer = this.project.layers[printId];
    if (!layer || layer.type !== 'print') return;

    // Если палитра уже открыта — закрываем
    const prev = document.querySelector('.print-layer-color-palette') as HTMLElement | null;
    if (prev) prev.remove();

    const COLORS = [
      '#000000',
      '#00DDFA',
      '#6B3EFF',
      '#B4E600',
      '#C1C1C1',
      '#F5FF3E',
      '#FF3E3E',
      '#FF3EB2',
      '#FFC53E',
    ] as const;

    const palette = document.createElement('div');
    palette.className = 'print-layer-color-palette';
    palette.setAttribute('role', 'dialog');
    palette.setAttribute('aria-label', 'Print layer colors');

    const current = ((layer as any).labelColor || '').toString().toUpperCase();

    COLORS.forEach((c) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'print-layer-color-swatch';
      btn.style.background = c;
      btn.title = c;
      btn.setAttribute('aria-label', c);
      if (c.toUpperCase() === current) {
        btn.classList.add('active');
      }

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        (layer as any).labelColor = c;
        anchorDot.style.backgroundColor = c;
        this.hidePrintLayerColorPalette();
      });

      palette.appendChild(btn);
    });

    // Позиционирование рядом с кружком
    const r = anchorDot.getBoundingClientRect();
    palette.style.position = 'fixed';
    palette.style.left = `${Math.round(r.left)}px`;
    palette.style.top = `${Math.round(r.bottom + 6)}px`;
    palette.style.zIndex = '10050';

    // Вставляем в DOM, затем корректируем, чтобы не вылезала за экран
    document.body.appendChild(palette);

    const pr = palette.getBoundingClientRect();
    let left = pr.left;
    let top = pr.top;
    const pad = 8;
    if (pr.right > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - pad - pr.width);
    }
    if (pr.bottom > window.innerHeight - pad) {
      top = Math.max(pad, r.top - 6 - pr.height);
    }
    palette.style.left = `${Math.round(left)}px`;
    palette.style.top = `${Math.round(top)}px`;

    // Закрытие по клику вне палитры или Esc
    const onDocPointerDown = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t === anchorDot || t.closest('.print-layer-color-palette') || t.closest('.print-layer-color-dot')) return;
      this.hidePrintLayerColorPalette();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hidePrintLayerColorPalette();
      }
    };

    // Важно: ставим на capture, чтобы сработало до обработчика клика по слою
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);

    // Чтобы корректно снимать обработчики при любом способе закрытия (выбор цвета/перестройка списка)
    (palette as any)._cleanup = () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }

  private selectLayer(mesh: THREE.Mesh, element: HTMLElement, shiftKey: boolean = false, ctrlKey: boolean = false): void {
    const layerId = mesh.uuid;
    const currentIndex = this.allLayerElements.indexOf(element);
    
    if (shiftKey && this.lastAnchorIndex >= 0 && currentIndex >= 0) {
      // Shift+клик - выделяем диапазон от якоря до текущего
      this.clearLayerSelection();
      
      const startIdx = Math.min(this.lastAnchorIndex, currentIndex);
      const endIdx = Math.max(this.lastAnchorIndex, currentIndex);
      
      for (let i = startIdx; i <= endIdx; i++) {
        const el = this.allLayerElements[i];
        const id = el.dataset.id;
        if (id) {
          const m = this.allLayerMeshes.get(id);
          if (m) {
            this.selectedLayers.add(id);
            this.selectedMeshes.push(m);
            el.classList.add('active');
          }
        }
      }
      
      // UI синхронизируем с первым выбранным
      if (this.selectedMeshes.length > 0) {
        const firstMesh = this.selectedMeshes[0];
        const firstLayerId = firstMesh.uuid;
        if (this.project.layers[firstLayerId]) {
          const layerState = this.project.layers[firstLayerId];
          this.materialManager.syncLayerState(firstLayerId, layerState);
        }
        // Сначала передаём список, затем selectLayer
        this.materialManager.setSelectedMeshes(this.selectedMeshes);
        this.materialManager.selectLayer(firstMesh);
      }
    } else if (ctrlKey) {
      // Cmd/Ctrl+клик - точечный мультивыбор (добавить/убрать из выделения)
      if (this.selectedLayers.has(layerId)) {
        // Убираем из выделения
        this.selectedLayers.delete(layerId);
        this.selectedMeshes = this.selectedMeshes.filter(m => m.uuid !== layerId);
        element.classList.remove('active');
      } else {
        // Добавляем в выделение
        this.selectedLayers.add(layerId);
        this.selectedMeshes.push(mesh);
        element.classList.add('active');
      }
      // Обновляем якорь
      this.lastAnchorIndex = currentIndex;
      
      // Синхронизируем UI с последним добавленным или первым в списке
      if (this.selectedMeshes.length > 0) {
        const targetMesh = this.selectedLayers.has(layerId) ? mesh : this.selectedMeshes[0];
        const targetLayerId = targetMesh.uuid;
        if (this.project.layers[targetLayerId]) {
          const layerState = this.project.layers[targetLayerId];
          this.materialManager.syncLayerState(targetLayerId, layerState);
        }
        this.materialManager.setSelectedMeshes(this.selectedMeshes);
        this.materialManager.selectLayer(targetMesh);
      }
    } else {
      // Обычный клик - заменяем выбор и устанавливаем якорь
      this.clearLayerSelection();
      this.lastAnchorIndex = currentIndex;
      
      this.selectedLayers.add(layerId);
      this.selectedMeshes.push(mesh);
    this.highlightSelectedLayer(element);
    
    // Синхронизируем состояние слоя перед выбором
      if (!this.project.layers[layerId]) {
        // Создаём запись если её нет
        this.project.layers[layerId] = {
          name: mesh.name || '',
          maps: {},
          params: {},
          preset: null,
          type: 'layer'
        };
      }
      const layerState = this.project.layers[layerId];
      this.materialManager.syncLayerState(layerId, layerState);
      if (layerState.preset) {
        window.dispatchEvent(new CustomEvent('materialPresetSelect', { detail: { id: layerState.preset } }));
      } else {
        window.dispatchEvent(new CustomEvent('materialPresetSelect', { detail: { id: null } }));
    }
    
      // Сначала передаём список выбранных мешей, затем selectLayer
      this.materialManager.setSelectedMeshes(this.selectedMeshes);
    this.materialManager.selectLayer(mesh);
  }
  }
  
  private clearLayerSelection(): void {
    this.selectedLayers.clear();
    this.selectedMeshes = [];
    // Убираем класс active со всех слоёв
    document.querySelectorAll('.layer.active').forEach(el => el.classList.remove('active'));
  }
  
  public getSelectedMeshes(): THREE.Mesh[] {
    return this.selectedMeshes;
  }

  private selectPrintLayer(element: HTMLElement, shiftKey: boolean = false, ctrlKey: boolean = false): void {
    const layerId = element.dataset.id || '';
    const currentIndex = this.allLayerElements.indexOf(element);
    
    if (shiftKey && this.lastAnchorIndex >= 0 && currentIndex >= 0) {
      // Shift+клик - выделяем диапазон
      this.clearLayerSelection();
      
      const startIdx = Math.min(this.lastAnchorIndex, currentIndex);
      const endIdx = Math.max(this.lastAnchorIndex, currentIndex);
      
      for (let i = startIdx; i <= endIdx; i++) {
        const el = this.allLayerElements[i];
        const id = el.dataset.id;
        if (id) {
          const m = this.allLayerMeshes.get(id);
          if (m) {
            this.selectedLayers.add(id);
            this.selectedMeshes.push(m);
            el.classList.add('active');
          }
        }
      }
      
      this.materialManager.setSelectedMeshes(this.selectedMeshes);
      // Сохраняем ID для редактирования (последний в диапазоне)
      this.setCurrentEditingPrintId(layerId);
    } else if (ctrlKey) {
      // Cmd/Ctrl+клик - точечный мультивыбор (добавить/убрать из выделения)
      const printMeshes = this.getPrintMeshesById(layerId);
      const firstPrintMesh = printMeshes.length > 0 ? printMeshes[0] : null;
      
      if (this.selectedLayers.has(layerId)) {
        // Убираем из выделения
        this.selectedLayers.delete(layerId);
        if (firstPrintMesh) {
          this.selectedMeshes = this.selectedMeshes.filter(m => (m as any).userData?.printId !== layerId);
        }
        element.classList.remove('active');
      } else {
        // Добавляем в выделение
        this.selectedLayers.add(layerId);
        if (firstPrintMesh) {
          this.selectedMeshes.push(firstPrintMesh);
        }
        element.classList.add('active');
      }
      // Обновляем якорь
      this.lastAnchorIndex = currentIndex;
      
      // Сохраняем ID для редактирования
      this.setCurrentEditingPrintId(layerId);
      
      // Синхронизируем MaterialManager
      this.materialManager.setSelectedMeshes(this.selectedMeshes);
      if (this.selectedMeshes.length > 0) {
        const targetMesh = this.selectedLayers.has(layerId) && firstPrintMesh ? firstPrintMesh : this.selectedMeshes[0];
        this.materialManager.selectLayer(targetMesh);
      }
    } else {
      // Обычный клик
      this.clearLayerSelection();
      this.lastAnchorIndex = currentIndex;
      this.highlightSelectedLayer(element);
      
      // Сохраняем ID выбранного принта для редактирования
      this.setCurrentEditingPrintId(layerId);
    
      // Находим первый меш принта для этого слоя
      const printMeshes = this.getPrintMeshesById(layerId);
      if (printMeshes.length > 0) {
        // Синхронизируем состояние конкретного принт-слоя ПЕРЕД selectLayer
        const printLayerData = this.project.layers[layerId] || {
          name: layerId,
          type: 'print',
          maps: {},
          params: {},
          preset: null
        };
        this.materialManager.syncLayerState(layerId, printLayerData);
      
        // Используем первый меш принта как представитель слоя
        const firstPrintMesh = printMeshes[0];
      
        // ВАЖНО: Добавляем меш в selectedMeshes и обновляем MaterialManager
        this.selectedLayers.add(layerId);
        this.selectedMeshes.push(firstPrintMesh);
      
        // Сначала передаём список выбранных мешей, затем вызываем selectLayer
        this.materialManager.setSelectedMeshes(this.selectedMeshes);
        this.materialManager.selectLayer(firstPrintMesh);
      
        // Обновляем превью материала по presetId конкретного принт-слоя
        if (printLayerData.preset) {
          window.dispatchEvent(new CustomEvent('materialPresetSelect', { detail: { id: printLayerData.preset } }));
        } else {
          window.dispatchEvent(new CustomEvent('materialPresetSelect', { detail: { id: null } }));
        }
      }
    }
  }



  public async loadLightPresets(): Promise<void> {
    // Устанавливаем начальные настройки света
    const initialLightSettings = {
      top: {
        position: { x: 0, y: 3.8, z: -0.5 },
        intensity: 0.6,
        width: 4,
        height: 4
      },
      bottom: {
        position: { x: 0, y: -3.4, z: 0 },
        intensity: 0,
        width: 4,
        height: 4
      },
      left: {
        position: { x: -2.5, y: 1.5, z: -3 },
        intensity: 7
      },
      right: {
        position: { x: 2.5, y: 1.5, z: -3 },
        intensity: 7
      }
    };
    
    // Применяем настройки
    this.lightManager.applySettings(initialLightSettings);
        
        // Применяем позиции ламп согласно настройкам
        const lights = this.lightManager.getLights();
        // Верхний плоский свет
        lights.top.position.set(0, 3.8, -0.5);
        lights.top.lookAt(0, 0, 0);
        // Нижний плоский свет
    lights.bottom.position.set(0, -3.4, 0);
        lights.bottom.lookAt(0, 0, 0);
    // Контровые источники
    if (lights.left) {
      lights.left.position.set(-2.5, 1.5, -3);
      lights.left.intensity = 7;
      }
    if (lights.right) {
      lights.right.position.set(2.5, 1.5, -3);
      lights.right.intensity = 7;
    }
    
    // Инициализируем интерфейс управления светом
    this.initLightControl();
  }

  private selectedLight: string = 'top'; // Выбранная лампа: top, bottom, left, right

  private initLightControl(): void {
    const gridView = document.getElementById('light-grid-view');
    const topCircle = document.getElementById('light-top-circle');
    const bottomCircle = document.getElementById('light-bottom-circle');
    const leftCircle = document.getElementById('light-left-circle');
    const rightCircle = document.getElementById('light-right-circle');
    const distanceInput = document.getElementById('light-distance') as HTMLInputElement;
    const distanceValue = document.getElementById('light-distance-value') as HTMLElement | null;
    const intensityInput = document.getElementById('light-intensity') as HTMLInputElement;
    const intensityValue = document.getElementById('light-intensity-value') as HTMLElement | null;
    
    if (!gridView || !topCircle || !bottomCircle || !distanceInput || !intensityInput) {
      console.warn('Light control elements not found');
      return;
    }

    const circles: Record<string, HTMLElement | null> = {
      top: topCircle,
      bottom: bottomCircle,
      left: leftCircle,
      right: rightCircle
    };

    // ==== Единая математика миникарты (без лишних вычислений) ====
    // Координаты на миникарте живут в "minimap space":
    // x ∈ [-0.85..0.85], z ∈ [-0.85..0.15], а в 3D умножаем на scale=3 (см. LightManager).
    const MIN_X = -0.85;
    const MAX_X = 0.85;
    const MIN_Z = -0.85;
    const MAX_Z = 0.15;
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const clampXZ = (x: number, z: number) => ({ x: clamp(x, MIN_X, MAX_X), z: clamp(z, MIN_Z, MAX_Z) });

    // Safe-area = миникарта без зоны ползунков (light-controls-bottom) и с отступами
    const getSafeArea = () => {
      const rect = gridView.getBoundingClientRect();
      const controls = gridView.querySelector('.light-controls-bottom') as HTMLElement | null;
      let maxY = rect.height;
      if (controls) {
        const cr = controls.getBoundingClientRect();
        maxY = Math.max(0, cr.top - rect.top);
      }
      const pad = 10; // совпадает с .light-controls-bottom bottom/left/right=10px
      const left = pad;
      const top = pad;
      const width = Math.max(1, rect.width - pad * 2);
      const height = Math.max(1, Math.min(rect.height, maxY) - pad * 2);
      return { rect, left, top, width, height };
    };

    // Таймер для скрытия метки
    let labelHideTimeout: ReturnType<typeof setTimeout> | null = null;
    
    // Функция выбора лампы
    const selectLight = (lightName: string) => {
      this.selectedLight = lightName;
      
      // Обновляем визуальное выделение
      Object.entries(circles).forEach(([name, circle]) => {
        if (circle) {
          circle.classList.toggle('selected', name === lightName);
        }
      });
      
      // Обновляем и показываем метку выбранной лампы с анимацией
      const lightLabel = document.getElementById('selected-light-label');
      if (lightLabel) {
        const i18nKey = `panel.lighting.${lightName}`;
        lightLabel.setAttribute('data-i18n', i18nKey);
        lightLabel.textContent = translate(i18nKey);
        
        // Очищаем предыдущий таймер
        if (labelHideTimeout) {
          clearTimeout(labelHideTimeout);
        }
        
        // Показываем метку
        lightLabel.classList.add('visible');
        
        // Скрываем через 2 секунды
        labelHideTimeout = setTimeout(() => {
          lightLabel.classList.remove('visible');
        }, 2000);
      }
      
      // Обновляем ползунки для выбранной лампы
      updateSlidersForSelectedLight();
    };

    // Вспомогательная функция для обновления визуального состояния ползунка
    const updateSliderVisual = (input: HTMLInputElement) => {
      const min = parseFloat(input.min);
      const max = parseFloat(input.max);
      const val = parseFloat(input.value);
      const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
      input.style.setProperty('--slider-value', pct + '%');
    };

    // Обновить ползунки для выбранной лампы
    const updateSlidersForSelectedLight = () => {
      const settings = this.lightManager.getLightSettings() as any;
      const lightData = settings[this.selectedLight];
      
      if (lightData) {
        // Distance (высота Y)
        const distance = lightData.position?.y ?? 3.8;
        distanceInput.value = distance.toString();
        if (distanceValue) distanceValue.textContent = distance.toFixed(1);
        updateSliderVisual(distanceInput);
        
        // Intensity
        const intensity = lightData.intensity ?? 2.2;
        intensityInput.value = intensity.toString();
        if (intensityValue) intensityValue.textContent = intensity.toFixed(1);
        updateSliderVisual(intensityInput);
      }
    };

    const setLightXZ = (lightName: string, x: number, z: number) => {
      const clamped = clampXZ(x, z);
      const settings = this.lightManager.getLightSettings() as any;
      if (lightName === 'top') {
        const y = settings.top?.position?.y ?? 3.8;
        this.lightManager.setTopLightPosition(clamped.x, clamped.z, y);
      } else if (lightName === 'bottom') {
        const y = settings.bottom?.position?.y ?? -3.4;
        this.lightManager.setBottomLightPosition(clamped.x, clamped.z, y);
      } else if (lightName === 'left' || lightName === 'right') {
        const y = settings[lightName]?.position?.y ?? 1.5;
        this.lightManager.setRimLightPosition(lightName as any, clamped.x, clamped.z, y);
      }
      // Единый источник истины — позиции в 3D. Обновляем миникарту из LightManager.
      this.updateMinimapLightPositions();
    };

    // Drag: pointer events (и мышь, и тач одним кодом)
    let draggingLight: string | null = null;
    let activePointerId: number | null = null;

    const pointerToXZ = (clientX: number, clientY: number, circle: HTMLElement) => {
      const area = getSafeArea();
      const r = (circle.offsetWidth || 18) / 2;
      const px = clamp(clientX - area.rect.left, area.left + r, area.left + area.width - r);
      const py = clamp(clientY - area.rect.top, area.top + r, area.top + area.height - r);
      const nx = (px - area.left) / area.width;
      const nz = (py - area.top) / area.height;
      const x = MIN_X + nx * (MAX_X - MIN_X);
      const z = MIN_Z + nz * (MAX_Z - MIN_Z);
      return { x, z };
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!draggingLight || activePointerId !== e.pointerId) return;
      const circle = circles[draggingLight];
      if (!circle) return;
      const { x, z } = pointerToXZ(e.clientX, e.clientY, circle);
      setLightXZ(draggingLight, x, z);
      e.preventDefault();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (activePointerId !== e.pointerId) return;
      activePointerId = null;
      draggingLight = null;
    };

    Object.entries(circles).forEach(([name, circle]) => {
      if (!circle) return;
      circle.addEventListener('pointerdown', (e: PointerEvent) => {
        activePointerId = e.pointerId;
        draggingLight = name;
        selectLight(name);
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        // Сразу применяем позицию под курсором (без "скачка")
        const { x, z } = pointerToXZ(e.clientX, e.clientY, circle);
        setLightXZ(name, x, z);
        e.preventDefault();
        e.stopPropagation();
      });
    });

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    // Слайдер Distance - изменяет высоту выбранной лампы
    distanceInput.addEventListener('input', () => {
      const value = parseFloat(distanceInput.value);
      if (distanceValue) distanceValue.textContent = value.toFixed(1);
      updateSliderVisual(distanceInput);
      
      if (this.selectedLight === 'top') {
        this.lightManager.setTopLightHeight(value);
      } else if (this.selectedLight === 'bottom') {
        this.lightManager.setBottomLightHeight(value);
      } else if (this.selectedLight === 'left' || this.selectedLight === 'right') {
        this.lightManager.setRimLightHeight(this.selectedLight as any, value);
      }
      this.updateMinimapLightPositions();
    });

    // Слайдер Intensity - изменяет интенсивность выбранной лампы
    intensityInput.addEventListener('input', () => {
      const value = parseFloat(intensityInput.value);
      if (intensityValue) intensityValue.textContent = value.toFixed(1);
      updateSliderVisual(intensityInput);
      
      this.lightManager.setLightIntensity(this.selectedLight, value);
    });

    // Color picker - изменяет цвет всех ламп одновременно
    const lightColorInput = document.getElementById('light-color') as HTMLInputElement;
    if (lightColorInput) {
      // Устанавливаем начальное значение
      lightColorInput.value = this.lightManager.getLightColor();
      
      // Обработчик изменения цвета
      lightColorInput.addEventListener('input', () => {
        const color = lightColorInput.value;
        this.lightManager.setAllLightsColor(color);
      });
    }

    // Инициализация UI из текущих позиций ламп (единый источник истины)
      selectLight('top');
    // Небольшая задержка, чтобы размеры миникарты/ползунков были посчитаны, затем выставляем позиции
    setTimeout(() => {
      this.updateMinimapLightPositions();
    }, 0);

    // Кнопка сброса освещения
    const resetLightingBtn = document.getElementById('reset-lighting-btn');
    if (resetLightingBtn) {
      resetLightingBtn.addEventListener('click', () => {
        // Сбрасываем настройки освещения к значениям по умолчанию
        this.lightManager.resetToDefaults(1);
        
        // Обновляем UI миникарты
        this.updateMinimapLightPositions();
        
        // Обновляем ползунки для текущей выбранной лампы
        updateSlidersForSelectedLight();
        
        // Обновляем цвет в UI
        if (lightColorInput) {
          lightColorInput.value = this.lightManager.getLightColor();
        }
      });
    }
  }

  public async applyLightPreset(item: PresetItem): Promise<void> {
    try {
      const presetPath = item.preset || `${item.path || ''}${item.id || ''}.json`;
      const preset = await fetchJSON(`/presets/${presetPath}`);
      
      if (preset.lights) {
        this.lightManager.applySettings(preset.lights);
        console.log(`✅ Применён световой пресет: ${item.name}`);
      }
    } catch (error) {
      console.error('Ошибка применения светового пресета:', error);
      alert('Ошибка загрузки светового пресета');
    }
  }

  public async applyServerPreset(item: PresetItem): Promise<void> {
    const selectedMesh = this.materialManager.getSelectedMesh();
    if (!selectedMesh) {
      alert('Сначала выберите слой слева');
      return;
    }

    try {
      const manifest = await fetchJSON(`/presets/${item.preset || item.path + 'preset.json'}`);
      const baseURL = item.path ? `/presets/${item.path}` : '/presets/';
      await this.applyManifestToSelected(manifest, baseURL);
      
      // Сохраняем preset для всех выбранных слоёв
      const meshesToUpdate = this.selectedMeshes.length > 0 ? this.selectedMeshes : [selectedMesh];
      for (const mesh of meshesToUpdate) {
        const isPrint = mesh.name?.startsWith('Print_');
        const layerId = isPrint ? (mesh as any).userData?.printId : mesh.uuid;
      const state = this.project.layers[layerId];
      if (state) {
        state.preset = item.id || item.name || null;
        }
      }
    } catch (error) {
      alert('Ошибка пресета: ' + (error as Error).message);
    }
  }

  private async applyManifestToSelected(manifest: PresetManifest, baseURL: string): Promise<void> {
    const selectedMesh = this.materialManager.getSelectedMesh();
    if (!selectedMesh) return;
    
    // Получаем все выбранные меши (мультивыбор)
    const selectedMeshes = this.selectedMeshes.length > 0 ? this.selectedMeshes : [selectedMesh];
    
    const maps = manifest.maps || {};
    
    // Загружаем все PBR карты
    const loadedTextures: Record<string, THREE.Texture | null> = {};
    
    // Функция для попытки загрузки текстуры (возвращает null если файл не найден)
    const tryLoadTexture = async (url: string, colorSpace: THREE.ColorSpace): Promise<THREE.Texture | null> => {
      try {
        // Проверяем существование файла (тихо, без ошибок в консоли)
        const response = await fetch(url, { method: 'HEAD' }).catch(() => null);
        if (!response || !response.ok) return null;
        return await loadRemoteTexture(url, colorSpace);
      } catch {
        return null;
      }
    };
    
    // Стандартные имена файлов для автоопределения
    const standardNames = {
      color: ['color.jpg', 'color.png', 'albedo.jpg', 'albedo.png', 'diffuse.jpg', 'diffuse.png', 'basecolor.jpg', 'basecolor.png'],
      normal: ['normal.jpg', 'normal.png', 'normal.webp', 'normalgl.jpg', 'normalgl.png'],
      roughness: ['roughness.jpg', 'roughness.png', 'rough.jpg', 'rough.png'],
      ao: ['ao.jpg', 'ao.png', 'ambient.jpg', 'ambient.png', 'occlusion.jpg', 'occlusion.png'],
      metalness: ['metalness.jpg', 'metalness.png', 'metal.jpg', 'metal.png', 'metallic.jpg', 'metallic.png'],
      opacity: ['opacity.jpg', 'opacity.png', 'alpha.jpg', 'alpha.png']
    };
    
    // Загружаем карты из manifest (автоопределение только если manifest пустой)
    const hasAnyMapsInManifest = maps.normal || maps.color || maps.roughness || maps.ao || maps.metalness || maps.opacity;
    
    for (const [mapType, filenames] of Object.entries(standardNames)) {
      const colorSpace = mapType === 'color' ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace;
      
      // Если карта указана в manifest - загружаем её
      if ((maps as any)[mapType]) {
        loadedTextures[mapType] = await loadRemoteTexture(baseURL + (maps as any)[mapType], colorSpace);
      } 
      // Автоопределение только если в manifest вообще нет карт (старый формат)
      else if (!hasAnyMapsInManifest) {
        for (const filename of filenames) {
          const tex = await tryLoadTexture(baseURL + filename, colorSpace);
          if (tex) {
            loadedTextures[mapType] = tex;
            break;
          }
        }
      }
    }
    
    // Применяем ко всем выбранным слоям
    for (const mesh of selectedMeshes) {
      const isPrint = mesh.name?.startsWith('Print_');
      const layerId = isPrint ? (mesh as any).userData?.printId : mesh.uuid;
    
    const state = this.project.layers[layerId] ?? (this.project.layers[layerId] = {
        name: mesh.name || 'Layer',
      maps: {},
      params: {},
      preset: null,
      type: isPrint ? 'print' : 'layer'
    });
    
    // Для принтов - сохраняем существующую текстуру принта
    if (isPrint && !state.maps.map) {
        const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (material && (material as any).map) {
        state.maps.map = (material as any).map;
      }
    }
    
      // Освобождаем предыдущие текстуры
      for (const key of ['normal', 'color', 'roughness', 'ao', 'metalness', 'opacity'] as const) {
        if (state.maps[key] && state.maps[key] !== loadedTextures[key]) {
          try { (state.maps[key] as any)?.dispose?.(); } catch {}
        }
      }
      
      // ВАЖНО: Сначала удаляем все старые текстуры из state.maps, которых нет в новом пресете
      // Это предотвращает "перемешивание" текстур от предыдущих материалов
      const textureKeys: Array<'normal' | 'color' | 'roughness' | 'ao' | 'metalness' | 'opacity'> = ['normal', 'color', 'roughness', 'ao', 'metalness', 'opacity'];
      for (const key of textureKeys) {
        // Если текстуры нет в новом пресете - удаляем её из state.maps
        if (!loadedTextures[key]) {
          delete state.maps[key];
        }
      }
      
      // Клонируем текстуры для каждого слоя чтобы можно было менять tiling независимо
      for (const [key, texture] of Object.entries(loadedTextures)) {
        if (texture) {
          (state.maps as any)[key] = texture.clone();
          (state.maps as any)[key].needsUpdate = true;
        }
      }
      
      // Синхронизируем состояние слоя
      this.materialManager.syncLayerState(layerId, state as any);
    }
    
    // Применяем параметры к UI (один раз), НО сохраняем текущий цвет и отражение
    if (manifest.params) {
      const uiControls = this.materialManager['uiControls'];
      // НЕ меняем цвет - оставляем текущий: if (manifest.params.baseColor) ...
      // НЕ меняем отражение (env) - это глобальная настройка, не свойство материала
      if (manifest.params.tiling) uiControls.tiling.value = manifest.params.tiling.toString();
      if (manifest.params.rotationDeg !== undefined) uiControls.rot.value = manifest.params.rotationDeg.toString();
      if (manifest.params.roughness !== undefined) uiControls.rough.value = manifest.params.roughness.toString();
      if (manifest.params.roughnessMul !== undefined) uiControls.roughTex.value = manifest.params.roughnessMul.toString();
      if (manifest.params.metalness !== undefined) uiControls.metal.value = manifest.params.metalness.toString();
      if (manifest.params.normalScale !== undefined) uiControls.norm.value = manifest.params.normalScale.toString();
    }
    
    // Синхронизируем список выбранных слоёв с MaterialManager
    this.materialManager.setSelectedMeshes(selectedMeshes);
    
    // Применяем материал ко всем выбранным
    // ВАЖНО: при применении пресета на мультивыбор не перетираем уже настроенные цвета элементов
    this.materialManager.applyMaterialUI({ preserveColors: this.selectedMeshes.length > 0 });
  }

  public async addPrintLayerFromDataURL(
    dataURL: string,
    existingPrintId?: string,
    uvGroupIds?: string[]
  ): Promise<string> {
    if (!this.currentModel) {
      alert('Сначала загрузите модель');
      return '';
    }
    // Используем существующий ID если он передан, иначе создаем новый
    const printLayerId = existingPrintId || ('print_' + Date.now());
    
    // Устанавливаем ID сразу, чтобы его можно было получить
    this.setCurrentEditingPrintId(printLayerId);
    
    // Вызываем асинхронный метод - ID уже установлен
    await this.applyPrintToWholeModelWithId(dataURL, printLayerId, uvGroupIds);
    
    return printLayerId;
  }

  // Храним ID последнего запроса на обновление текстуры для каждого printId
  private pendingTextureUpdates: Map<string, number> = new Map();

  public updatePrintLayerTexture(dataURL: string, printId: string): void {
    if (!this.currentModel) return;
    
    // Генерируем уникальный ID для этого запроса обновления
    const updateId = Date.now() + Math.random();
    this.pendingTextureUpdates.set(printId, updateId);
    
    const loader = new THREE.TextureLoader();
    loader.load(dataURL, (texture) => {
      // Проверяем, что это все еще актуальное обновление
      // Если пришел более новый запрос, игнорируем этот
      if (this.pendingTextureUpdates.get(printId) !== updateId) {
        console.log(`🚫 Ignoring outdated texture update for print ${printId}`);
        texture.dispose(); // Освобождаем память от устаревшей текстуры
        return;
      }
      
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      
      // Сохраняем aoMap модели перед обновлением текстуры принта
      const modelAoMaps = this.project.model.aoMaps;
      const legacyModelAoMap =
        this.project.model.aoMap && typeof this.project.model.aoMap !== 'string'
          ? (this.project.model.aoMap as THREE.Texture)
          : null;
      
      // Обновляем текстуру у всех мешей принта
      const printMeshes = this.getPrintMeshesById(printId);
      printMeshes.forEach((mesh: THREE.Mesh) => {
        const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        meshMaterials.forEach((material: THREE.Material) => {
          const mat = material as any;
          
          // Сохраняем aoMap перед обновлением текстуры
          const existingAoMap = mat.aoMap;
          
          // Освобождаем старую текстуру принта перед заменой
          const oldMap = mat.map;
          if (oldMap && oldMap !== texture) {
            try {
              oldMap.dispose();
            } catch (e) {
              console.warn('Failed to dispose old texture:', e);
            }
          }
          
          // Обновляем текстуру принта
          mat.map = texture;
          mat.map.needsUpdate = true;
          mat.needsUpdate = true;
          
          // Восстанавливаем aoMap модели (если был сохранен или есть в проекте)
          if (existingAoMap) {
            // Если aoMap уже был в материале — оставляем его (важно сохранить channel/flipY/intensity)
            mat.aoMap = existingAoMap;
          } else {
            // Иначе пробуем восстановить AO по uvGroup (или legacy один AO)
            const uvGroup = (mesh.userData.uvGroup as string | undefined) || '';
            const groupTex = (modelAoMaps && modelAoMaps[uvGroup] && typeof modelAoMaps[uvGroup] !== 'string'
              ? (modelAoMaps[uvGroup] as THREE.Texture)
              : null) || legacyModelAoMap;
            if (groupTex) {
              const clonedAoMap = groupTex.clone();
              clonedAoMap.channel = groupTex.channel;
              clonedAoMap.flipY = groupTex.flipY;
              clonedAoMap.colorSpace = groupTex.colorSpace;
              clonedAoMap.needsUpdate = true;
              mat.aoMap = clonedAoMap;
            }
          }
        });
      });
      
      // Обновляем текстуру в project.layers
      if (this.project.layers[printId]) {
        this.project.layers[printId].maps.map = texture;
      }
      
      // Удаляем запись о pending update после успешного применения
      if (this.pendingTextureUpdates.get(printId) === updateId) {
        this.pendingTextureUpdates.delete(printId);
      }
    }, undefined, (error) => {
      // Обработка ошибки загрузки
      console.error(`Failed to load texture for print ${printId}:`, error);
      // Удаляем запись о pending update при ошибке
      if (this.pendingTextureUpdates.get(printId) === updateId) {
        this.pendingTextureUpdates.delete(printId);
      }
    });
  }

  public setPrintOffset(offset: number): void {
    this.printOffset = Math.max(0, offset);
    // Обновляем uniforms у всех принт-материалов
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        const mesh = obj as THREE.Mesh;
        const mat = mesh.material as THREE.Material;
        const u = (mat as any).userData?.printOffsetUniform;
        if (u) u.value = this.printOffset;
      }
    });
  }

  private removeAllPrints(): void {
    if (!this.currentModel) return;
    
    // Удаляем все меши принтов
    const printMeshes: THREE.Mesh[] = [];
    this.currentModel.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        printMeshes.push(obj as THREE.Mesh);
      }
    });
    
    printMeshes.forEach(mesh => {
      // Удаляем из сцены
      mesh.parent?.remove(mesh);
      
      // Очищаем геометрию и материалы
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(mat => mat.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    });
    
    // Удаляем все слои принтов из проекта
    Object.keys(this.project.layers).forEach(key => {
      if (this.project.layers[key].type === 'print') {
        delete this.project.layers[key];
      }
    });
  }

  public deletePrintLayer(printId: string): void {
    if (!this.currentModel) return;
    if (!this.project.layers[printId]) return;

    // Удаляем все принт-меши этой группы
    const toRemove: THREE.Mesh[] = [];
    this.currentModel.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        const m = obj as THREE.Mesh;
        if ((m as any).userData?.printId === printId) toRemove.push(m);
      }
    });
    toRemove.forEach(m => {
      m.parent?.remove(m);
      if (m.geometry) m.geometry.dispose();
      if (m.material) {
        if (Array.isArray(m.material)) m.material.forEach(mm => mm.dispose());
        else m.material.dispose();
      }
    });
    delete this.project.layers[printId];
    this.rebuildLayersList();
  }

  /**
   * Переключает видимость слоя принта
   */
  public togglePrintVisibility(printId: string): void {
    if (!this.currentModel) return;
    
    const layerData = this.project.layers[printId];
    if (!layerData || layerData.type !== 'print') return;
    
    // Переключаем состояние видимости
    const newVisible = layerData.visible === false ? true : false;
    layerData.visible = newVisible;
    
    // Применяем видимость ко всем мешам принта с учётом uvGroupIds (лекало)
    const printMeshes = this.getPrintMeshesById(printId);
    const activeGroups: string[] = Array.from(
      new Set(
        (
          (layerData as any).uvGroupIds ||
          ((layerData as any).uvGroupId ? [(layerData as any).uvGroupId] : [])
        ).filter(Boolean)
      )
    );
    printMeshes.forEach(mesh => {
      if (!newVisible) {
        mesh.visible = false;
      } else if (activeGroups.length > 0) {
        const gid = (mesh as any).userData?.uvGroup as string | undefined;
        mesh.visible = !!gid && activeGroups.includes(gid);
      } else {
        mesh.visible = true;
      }
    });
    
    // Обновляем только UI элемент без перестройки всего списка
    const element = document.querySelector(`[data-id="${printId}"]`) as HTMLElement;
    if (element) {
      // Обновляем иконку глаза - заменяем весь SVG
      const visibilityBtn = element.querySelector('.visibility-btn') as HTMLElement;
      if (visibilityBtn) {
        visibilityBtn.title = newVisible ? 'Скрыть слой' : 'Показать слой';
        visibilityBtn.innerHTML = newVisible 
          ? '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.024 3c3.344 0 5.642 1.83 6.895 3.201.784.858.82 2.118.056 2.994-1.193 1.367-3.43 3.163-6.951 3.163s-5.758-1.796-6.95-3.163c-.765-.876-.73-2.136.054-2.994C2.381 4.831 4.68 3.001 8.024 3m.004 2.006a2.674 2.674 0 1 0 0 5.348 2.674 2.674 0 0 0 0-5.348" fill="#7f7f7f"/></svg>'
          : '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.62 6.054a.435.435 0 0 1 .728.468l-.01.018-.021.04-.08.143c-.071.12-.177.29-.318.49a9 9 0 0 1-.913 1.096l.476.477a.61.61 0 0 1-.86.861l-.53-.528c-.4.308-.856.604-1.374.861l.47.814a.61.61 0 0 1-1.055.61l-.557-.963a8.4 8.4 0 0 1-2.004.371v1.014a.61.61 0 0 1-1.218 0V10.81a8.4 8.4 0 0 1-1.817-.323l-.528.917a.61.61 0 0 1-1.056-.61l.43-.746A8.3 8.3 0 0 1 2.727 9.01l-.637.638a.609.609 0 0 1-.861-.861l.602-.604a9 9 0 0 1-.79-.969 7 7 0 0 1-.398-.632L.62 6.54l-.008-.016v-.001a.436.436 0 0 1 .728-.468l.002.003.016.019.07.08c.066.071.168.176.305.303.273.255.687.599 1.24.943C4.082 8.09 5.749 8.78 7.98 8.78s3.898-.691 5.005-1.378a8 8 0 0 0 1.24-.943 6 6 0 0 0 .376-.383l.015-.019z" fill="#7f7f7f"/></svg>';
      }
      // Обновляем прозрачность названия
      const nameSpan = (element.querySelector('.print-layer-name') || element.querySelector('span')) as HTMLElement;
      if (nameSpan) {
        nameSpan.style.opacity = newVisible ? '' : '0.5';
      }
    }
  }

  public async createTempPrintLayer(dataURL: string, uvGroupIds?: string[]): Promise<string> {
    // Создаем принт и возвращаем его ID
    const printId = await this.addPrintLayerFromDataURL(dataURL, undefined, uvGroupIds);
    return printId || '';
  }

  /**
   * Быстро обновляет активные UV-группы принта:
   * - скрывает/показывает уже существующие Print_* меши по userData.uvGroup
   * - доздаёт меши для новых групп (используя уже загруженную текстуру принта)
   *
   * Это вызывается из редактора принтов при клике по фильтрам.
   */
  public async setPrintLayerUVGroups(printId: string, uvGroupIds: string[]): Promise<void> {
    if (!this.currentModel) return;
    const layer = this.project.layers[printId] as any;
    if (!layer) return;

    const active = Array.from(new Set((uvGroupIds || []).filter(Boolean)));
    layer.uvGroupIds = active;
    layer.uvGroupId = active.length === 1 ? active[0] : undefined;

    const printMeshes = this.getPrintMeshesById(printId);
    const hasMeshForGroup = (gid: string): boolean =>
      printMeshes.some((m) => (m as any).userData?.uvGroup === gid);

    // 1) Тогглим видимость существующих мешей (учитываем visible-флаг слоя!)
    const layerVisible = layer.visible !== false;
    printMeshes.forEach((m) => {
      const gid = (m as any).userData?.uvGroup as string | undefined;
      m.visible = layerVisible && !!gid && active.includes(gid);
    });

    if (active.length === 0) return;

    // 2) Доздаём меши для групп, которых ещё нет
    const mapVal = layer.maps?.map;
    if (!mapVal) return;
    if (typeof mapVal === 'string') {
      // fallback: если по какой-то причине map ещё не THREE.Texture — делаем полный rebuild через loader
      await this.addPrintLayerFromDataURL(mapVal, printId, active);
      return;
    }
    const texture = mapVal as THREE.Texture;

    // template material: берём любой существующий материал принта (чтобы сохранить настройки)
    const templateMatAny = printMeshes[0]?.material;
    const templateMat = Array.isArray(templateMatAny) ? (templateMatAny[0] as any) : (templateMatAny as any);

    const normalMaps = this.project.model.normalMaps || {};
    const aoMaps = this.project.model.aoMaps || {};
    const legacyAo = this.project.model.aoMap && typeof this.project.model.aoMap !== 'string'
      ? (this.project.model.aoMap as THREE.Texture)
      : null;

    const makeMaterialForGroup = (gid: string): THREE.MeshPhysicalMaterial => {
      const mat = (templateMat && templateMat.clone) ? (templateMat.clone() as THREE.MeshPhysicalMaterial) : new THREE.MeshPhysicalMaterial({
        map: texture,
        transparent: true,
        opacity: 1.0,
        color: 0xffffff,
        roughness: 0.5,
        metalness: 0.0,
        envMapIntensity: 0.5,
        clearcoat: 0.3,
        clearcoatRoughness: 0.4,
        depthTest: true,
        depthWrite: true,
        polygonOffset: true,
        polygonOffsetFactor: -0.2,
        polygonOffsetUnits: 0,
        side: THREE.FrontSide,
        alphaTest: 0.1
      });
      mat.map = texture;

      // model normal
      const modelNormal = normalMaps[gid] as any;
      if (modelNormal && typeof modelNormal !== 'string') {
        (mat as any).normalMap = modelNormal;
        (mat as any).userData = { ...(mat as any).userData, modelNormalMap: modelNormal };
      }

      // model AO
      const modelAo = (aoMaps[gid] && typeof aoMaps[gid] !== 'string' ? (aoMaps[gid] as THREE.Texture) : null) || legacyAo;
      if (modelAo) {
        const cloned = modelAo.clone();
        cloned.channel = modelAo.channel;
        cloned.flipY = modelAo.flipY;
        cloned.colorSpace = modelAo.colorSpace;
        cloned.needsUpdate = true;
        (mat as any).aoMap = cloned;
        (mat as any).aoMapIntensity = (mat as any).aoMapIntensity ?? 1;
      }

      mat.needsUpdate = true;
      return mat;
    };

    for (const gid of active) {
      if (hasMeshForGroup(gid)) continue;
      // создаём меши для всех базовых мешей этой группы
      this.currentModel.traverse((obj) => {
        if (!(obj as any).isMesh) return;
        const baseMesh = obj as THREE.Mesh;
        if (baseMesh.name.startsWith('Print_')) return;
        const baseGid = (baseMesh.userData.uvGroup as string) || '';
        if (baseGid !== gid) return;

        // ensure uv2 for AO
        const gAny = baseMesh.geometry as any;
        if (gAny?.isBufferGeometry) {
          const g = baseMesh.geometry as THREE.BufferGeometry;
          if (!g.attributes.uv2 && g.attributes.uv) g.setAttribute('uv2', g.attributes.uv);
        }

        const geometry = baseMesh.geometry.clone();
        const overlay = new THREE.Mesh(geometry, makeMaterialForGroup(gid));
        overlay.name = 'Print_' + baseMesh.name + '_' + Date.now();
        overlay.userData.printId = printId;
        overlay.userData.uvGroup = gid;
        overlay.castShadow = true;
        overlay.receiveShadow = true;
        overlay.position.copy(baseMesh.position);
        overlay.quaternion.copy(baseMesh.quaternion);
        overlay.scale.copy(baseMesh.scale);
        overlay.renderOrder = 1000;
        if (baseMesh.parent) baseMesh.parent.add(overlay);
        else this.currentModel!.add(overlay);
      });
    }
  }

  private deleteLayer(id: string): void {
    // Показываем модальное окно подтверждения
    this.showDeleteConfirmDialog(() => {
      this.performDeleteLayer(id);
    });
  }
  
  private performDeleteLayer(id: string): void {
    if (!this.currentModel) return;
    if (!this.project.layers[id]) return;

    // Удаляем все принт-меши этой группы
    if (id.startsWith('print_')) {
      const toRemove: THREE.Mesh[] = [];
      this.currentModel.traverse((obj) => {
        if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
          const m = obj as THREE.Mesh;
          if ((m as any).userData?.printId === id) toRemove.push(m);
        }
      });
      toRemove.forEach(m => {
        m.parent?.remove(m);
        if (m.geometry) m.geometry.dispose();
        if (m.material) {
          if (Array.isArray(m.material)) m.material.forEach(mm => mm.dispose());
          else m.material.dispose();
        }
      });
      delete this.project.layers[id];
      this.rebuildLayersList();
    }
  }
  
  private showDeleteConfirmDialog(onConfirm: () => void): void {
    // Создаем overlay
    const overlay = document.createElement('div');
    overlay.id = 'delete-confirm-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      z-index: 10001;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    // Создаем модальное окно
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: rgb(22, 22, 22);
      border-radius: 12px;
      padding: 24px;
      max-width: 400px;
    `;
    
    // Текст сообщения
    const message = document.createElement('div');
    message.innerHTML = `${translate('dialog.delete.title')}<br><span style="color: rgba(255,255,255,0.5); font-size: 0.85rem;">${translate('dialog.delete.message')}</span>`;
    message.style.cssText = `
      color: #FFFFFF;
      font-size: 1rem;
      font-weight: 300;
      margin-bottom: 24px;
      text-align: center;
      line-height: 1.6;
    `;
    
    // Кнопки
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;
    
    const cancelButton = document.createElement('button');
    cancelButton.textContent = translate('dialog.delete.cancel');
    cancelButton.style.cssText = `
      padding: 10px 24px;
      border: none;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.1);
      color: #FFFFFF;
      font-size: 0.875rem;
      font-weight: 300;
      cursor: pointer;
      transition: background 0.2s;
    `;
    
    const deleteButton = document.createElement('button');
    deleteButton.textContent = translate('dialog.delete.confirm');
    deleteButton.style.cssText = `
      padding: 10px 24px;
      border: none;
      border-radius: 8px;
      background: #ef4444;
      color: #FFFFFF;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s;
    `;
    
    // Обработчики
    const closeModal = () => {
      overlay.remove();
    };
    
    cancelButton.onclick = closeModal;
    
    deleteButton.onclick = () => {
      closeModal();
      onConfirm();
    };
    
    overlay.onclick = (e) => {
      if (e.target === overlay) closeModal();
    };
    
    buttonsContainer.appendChild(cancelButton);
    buttonsContainer.appendChild(deleteButton);
    modal.appendChild(message);
    modal.appendChild(buttonsContainer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  }

  private async applyPrintToWholeModelWithId(
    url: string,
    printLayerId: string,
    uvGroupIds?: string[]
  ): Promise<void> {
    // Не трогаем другие print-слои, но перегенерируем меши этого printLayerId
    if (this.currentModel) {
      const toRemove: THREE.Mesh[] = [];
      this.currentModel.traverse((obj) => {
        if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
          const m = obj as THREE.Mesh;
          if ((m as any).userData?.printId === printLayerId) toRemove.push(m);
        }
      });
      toRemove.forEach((m) => {
        m.parent?.remove(m);
        try { m.geometry?.dispose(); } catch {}
        try {
          const mat = (m as any).material;
          if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
          else mat?.dispose();
        } catch {}
      });
    }

    const loader = new THREE.TextureLoader();
    const texture = await new Promise<THREE.Texture | null>((resolve) => {
      loader.load(url, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        resolve(tex);
      }, undefined, () => resolve(null));
    });

    if (!texture) {
      alert('Ошибка загрузки принта');
      return;
    }

    // Создаём материалы для принта — по одному на каждую UV-группу,
    // чтобы каждая группа получила правильную запечённую нормал-карту модели.
    const normalMaps = this.project.model.normalMaps || {};
    const printMaterialByGroup = new Map<string, THREE.MeshPhysicalMaterial>();

    const getOrCreatePrintMaterial = (uvGroup: string): THREE.MeshPhysicalMaterial => {
      const key = uvGroup || '__default__';
      if (printMaterialByGroup.has(key)) return printMaterialByGroup.get(key)!;
      const mat = new THREE.MeshPhysicalMaterial({
        map: texture,
        transparent: true,
        opacity: 1.0,
        color: 0xffffff,
        roughness: 0.5,
        metalness: 0.0,
        envMapIntensity: 0.5,
        clearcoat: 0.3,
        clearcoatRoughness: 0.4,
        depthTest: true,
        depthWrite: true,
        polygonOffset: true,
        polygonOffsetFactor: -0.2,
        polygonOffsetUnits: 0,
        side: THREE.FrontSide,
        alphaTest: 0.1
      });
      // Применяем запечённую нормал-карту модели для данной UV-группы
      const modelNormalMap = normalMaps[key] as THREE.Texture | null | undefined;
      if (modelNormalMap && typeof modelNormalMap !== 'string') {
        mat.normalMap = modelNormalMap;
        (mat as any).userData = { modelNormalMap };
      }
      printMaterialByGroup.set(key, mat);
      return mat;
    };

    const timestamp = parseInt(printLayerId.replace('print_', '')) || Date.now();
    let printCount = 0;

    console.log(`🎨 Создаем принт-меши для ${printLayerId}, timestamp: ${timestamp}`);

    // Создаем принт-меши для каждого меша модели
    this.currentModel?.traverse((object) => {
      if ((object as any).isMesh && !object.name.startsWith('Print_')) {
        const baseMesh = object as THREE.Mesh;
        const uvGroup = (baseMesh.userData.uvGroup as string) || '';
        // Если задан фильтр групп — создаём принт только для мешей выбранных групп
        if (uvGroupIds && uvGroupIds.length > 0 && !uvGroupIds.includes(uvGroup)) return;
        const geometry = baseMesh.geometry.clone();
        const overlay = new THREE.Mesh(geometry, getOrCreatePrintMaterial(uvGroup));
        overlay.name = 'Print_' + baseMesh.name + '_' + timestamp;
        overlay.userData.printId = printLayerId;
        overlay.userData.uvGroup = uvGroup;
        overlay.castShadow = true; // Принты отбрасывают тени
        overlay.receiveShadow = true; // Принты принимают тени
        overlay.position.copy(baseMesh.position);
        overlay.quaternion.copy(baseMesh.quaternion);
        overlay.scale.copy(baseMesh.scale);
        overlay.renderOrder = 1000;

        // Добавляем в ту же группу, что и базовый меш
        if (baseMesh.parent) {
          baseMesh.parent.add(overlay);
        } else {
          this.currentModel!.add(overlay);
        }

        console.log(`  ✓ Создан меш принта: ${overlay.name}, printId: ${printLayerId}, uvGroup: ${uvGroup}`);
        printCount++;
      }
    });

    console.log(`✅ Создано ${printCount} мешей принта для ${printLayerId}`);

    // Регистрируем ОТДЕЛЬНЫЙ слой принта (только если еще не существует, чтобы сохранить canvasData при восстановлении)
    if (!this.project.layers[printLayerId]) {
    this.project.layers[printLayerId] = {
      name: 'Print Layer',
      type: 'print',
      visible: true,
      labelColor: this.defaultPrintLabelColor(printLayerId),
      uvGroupIds: uvGroupIds && uvGroupIds.length > 0 ? Array.from(new Set(uvGroupIds)) : undefined,
      uvGroupId: undefined,
      maps: { map: texture },
      params: {
        baseColor: '#ffffff',
        tiling: 1,
        rotationDeg: 0,
        opacity: 1,
        roughness: 0.7,
        metalness: 0.15,
        envIntensity: 0.3,
        normalScale: 2
      },
      preset: null
    } as any;
    } else {
      // Если слой уже существует (при восстановлении), обновляем только maps и сохраняем остальные данные
      this.project.layers[printLayerId].maps = { ...this.project.layers[printLayerId].maps, map: texture };
      // Обновляем uvGroupIds если передан (например, при переносе/смене фильтров)
      if (uvGroupIds && uvGroupIds.length > 0) {
        (this.project.layers[printLayerId] as any).uvGroupIds = Array.from(new Set(uvGroupIds));
        (this.project.layers[printLayerId] as any).uvGroupId = undefined;
      }
      // И гарантируем дефолты UI-полей
      if ((this.project.layers[printLayerId] as any).visible === undefined) (this.project.layers[printLayerId] as any).visible = true;
      if (!(this.project.layers[printLayerId] as any).labelColor) (this.project.layers[printLayerId] as any).labelColor = this.defaultPrintLabelColor(printLayerId);
    }

    this.rebuildLayersList();
    
    // Автоматически выделяем созданный слой принта
    this.selectPrintLayerById(printLayerId);
  }
  
  // updatePrintTexture method removed - editing prints is disabled


  private async loadPrintTexture(url: string, baseMesh: THREE.Mesh): Promise<void> {
    const loader = new THREE.TextureLoader();
    const texture = await new Promise<THREE.Texture | null>((resolve) => {
      loader.load(url, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        resolve(tex);
      }, undefined, () => resolve(null));
    });

    if (!texture) {
      alert('Ошибка загрузки принта');
      return;
    }

    // Создаем дубликат геометрии базового меша
    const geometry = baseMesh.geometry.clone();
    
    // Создаем PBR материал для принта с поддержкой всех настроек
    const material = new THREE.MeshPhysicalMaterial({
      map: texture,
      transparent: true,
      opacity: 1.0,
      color: 0xffffff,
      roughness: 0.5,
      metalness: 0.0,
      envMapIntensity: 0.5,
      clearcoat: 0.3,
      clearcoatRoughness: 0.4,
      depthTest: true,
      depthWrite: true,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
      side: THREE.FrontSide,
      alphaTest: 0.1
    });
    // Применяем запечённую нормал-карту для UV-группы этого меша
    const baseUvGroup = baseMesh.userData.uvGroup as string | undefined;
    const normalMapsPool = this.project.model.normalMaps || {};
    if (baseUvGroup && normalMapsPool[baseUvGroup]) {
      const modelNormalMap = normalMapsPool[baseUvGroup] as THREE.Texture | null;
      if (modelNormalMap && typeof modelNormalMap !== 'string') {
        material.normalMap = modelNormalMap;
        (material as any).userData = { modelNormalMap };
      }
    }

    const printMesh = new THREE.Mesh(geometry, material);
    printMesh.name = 'Print_' + baseMesh.name + '_' + Date.now();
    printMesh.userData.uvGroup = baseUvGroup || '';
    printMesh.castShadow = true;  // Принты отбрасывают тени
    printMesh.receiveShadow = true; // Принты принимают тени
    
    // Копируем трансформации базового меша (position и rotation)
    printMesh.position.copy(baseMesh.position);
    printMesh.rotation.copy(baseMesh.rotation);
    
    // Копируем масштаб базового меша без дополнительного увеличения
    printMesh.scale.copy(baseMesh.scale);
    
    // Добавляем в ту же группу, что и базовый меш
    if (baseMesh.parent) {
      baseMesh.parent.add(printMesh);
    } else {
      this.currentModel!.add(printMesh);
    }
    
    // КРИТИЧНО: Устанавливаем максимальный renderOrder для гарантированной отрисовки поверх
    printMesh.renderOrder = 1000;

    this.project.layers[printMesh.uuid] = {
      name: printMesh.name,
      type: 'print',
      maps: { map: texture },
      params: { 
        baseColor: '#ffffff',
        tiling: 1,
        rotationDeg: 0,
        opacity: 1, 
        roughness: 0.5, 
        metalness: 0.0, 
        envIntensity: 0.5,
        normalScale: 1.0
      },
      preset: null
    };

    this.rebuildLayersList();
    const element = document.querySelector(`.layer[data-id="${printMesh.uuid}"]`);
    if (element) this.selectLayer(printMesh, element as HTMLElement);

    URL.revokeObjectURL(url);
  }

  private createDefaultModel(): void {
    // Пробуем загрузить стартовый проект base.fabp
    const fabpPath = '/model/base.fabp';
    document.body.classList.add('loading-material');
    fetch(fabpPath)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        const file = new File([blob], 'base.fabp', { type: 'application/octet-stream' });
        return this.loadProjectFromFile(file);
      })
      .then(() => {
        document.body.classList.remove('loading-material');
      })
      .catch(err => {
        document.body.classList.remove('loading-material');
        console.warn('base.fabp не найден или ошибка загрузки, fallback на base model:', err);
        this.loadFallbackGlb();
      });
  }

  private loadFallbackGlb(): void {
    const loader = new GLTFLoader();
    const modelPaths = ['/model/base.glb', '/model/base.gltf'];
    const tryLoad = (idx: number) => {
      const modelPath = modelPaths[idx];
      if (!modelPath) {
        const geometry = new THREE.BoxGeometry(1.2, 1.5, 0.3);
        const material = new THREE.MeshStandardMaterial({ color: 0xeeeeee });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        const result = centerAndWrap(mesh);
        this.currentModel = result.wrapper;
        this.baseSizeY = result.sizeY;
        this.scene.add(this.currentModel);

        this.rebuildMiniPreviewFromCurrentModel();
        this.rebuildLayersList();
        const first = document.querySelector('.layer');
        if (first) (first as HTMLElement).click();
        return;
      }

      this.project.model.name = modelPath.split('/').pop() || 'base-model';
      this.currentModelPath = modelPath;
      loader.load(modelPath, (gltf) => {
        this.onModelReady(gltf.scene);
      }, undefined, (error) => {
        console.error(`Failed to load default model ${modelPath}:`, error);
        tryLoad(idx + 1);
      });
    };

    tryLoad(0);
  }

  private async captureScreenshotBlob(aspectRatio: string = 'free'): Promise<Blob> {
    // Сохраняем текущее состояние
    const currentBg = this.scene.background;
    const currentFog = this.scene.fog;
    const groundVisible = this.ground.visible;
    const currentClearColor = this.renderer.getClearColor(new THREE.Color());
    const currentClearAlpha = this.renderer.getClearAlpha();
    const prevSize = this.renderer.getSize(new THREE.Vector2());
    const prevPixelRatio = this.renderer.getPixelRatio();

    // Экспорт фото должен быть в полном качестве (независимо от performance mode).
    // Временно возвращаем "полный" pixel ratio для захвата кадра.
    // Важно: updateStyle=false, чтобы не менять CSS-габариты канваса.
    this.renderer.setPixelRatio(this.getDefaultPixelRatio());
    this.renderer.setSize(prevSize.x, prevSize.y, false);
    
    try {
      // Проверяем прозрачный ли фон - используем режим из UI
      const bgMode = (window as any).getBgMode?.() || 'transparent';
      const isTransparent = bgMode === 'transparent';
      
      if (isTransparent) {
        // Убираем фон, туман и ground для прозрачного скриншота
        this.scene.background = null;
        this.scene.fog = null;
        this.ground.visible = false;
        // Устанавливаем прозрачный clearColor ПЕРЕД рендером
        this.renderer.setClearColor(0x000000, 0);
      } else {
        // Убеждаемся, что для непрозрачного фона clearColor установлен правильно
        const bgColorObj = this.scene.background as THREE.Color;
        if (bgColorObj) {
          this.renderer.setClearColor(bgColorObj, 1);
        }
      }
      
      // Очищаем и рендерим
      this.renderer.clear();
    this.updateComposerSizeFromRenderer();
    this.renderFrame();
      
      // Для сохранения с прозрачностью используем 2D canvas
      const glCanvas = this.renderer.domElement;
      
      // Вычисляем размеры кропа в зависимости от aspect ratio
      let cropWidth = glCanvas.width;
      let cropHeight = glCanvas.height;
      let cropX = 0;
      let cropY = 0;
      
      if (aspectRatio !== 'free') {
        const aspectRatios: Record<string, number> = {
          '1:1': 1,
          '16:9': 16 / 9,
          '9:16': 9 / 16
        };
        
        const targetAspect = aspectRatios[aspectRatio];
        if (targetAspect) {
          const currentAspect = glCanvas.width / glCanvas.height;
          
          if (currentAspect > targetAspect) {
            // Шире чем нужно - обрезаем по ширине
            cropWidth = glCanvas.height * targetAspect;
            cropX = (glCanvas.width - cropWidth) / 2;
          } else {
            // Выше чем нужно - обрезаем по высоте
            cropHeight = glCanvas.width / targetAspect;
            cropY = (glCanvas.height - cropHeight) / 2;
          }
        }
      }
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = cropWidth;
      tempCanvas.height = cropHeight;
      const ctx = tempCanvas.getContext('2d');
      
      if (ctx) {
        // Если фон прозрачный, просто копируем содержимое WebGL canvas
        if (isTransparent) {
          ctx.drawImage(glCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        } else {
          // Для непрозрачного фона проверяем тип фона
          const bg = this.scene.background;
          
          // Если фон - это цвет, заливаем его перед копированием
          if (bg instanceof THREE.Color) {
            ctx.fillStyle = `#${bg.getHexString()}`;
            ctx.fillRect(0, 0, cropWidth, cropHeight);
          }
          // Если фон - текстура (градиент), то он уже отрендерен в WebGL canvas
          
          ctx.drawImage(glCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        }
      }
      
      const blob: Blob = await new Promise((resolve, reject) => {
        tempCanvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create PNG blob'));
        }, 'image/png');
      });
      
      return blob;
    } finally {
      // Восстанавливаем состояние сцены/рендера
      this.scene.background = currentBg;
      this.scene.fog = currentFog;
      this.ground.visible = groundVisible;
      this.renderer.setClearColor(currentClearColor, currentClearAlpha);
      
      // Возвращаем интерактивные настройки
      this.renderer.setPixelRatio(prevPixelRatio);
      this.renderer.setSize(prevSize.x, prevSize.y, false);
      this.applyInteractivePixelRatio();
    }
  }

  public async takeScreenshot(aspectRatio: string = 'free', fileName?: string): Promise<void> {
    const blob = await this.captureScreenshotBlob(aspectRatio);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = fileName || `photo-${aspectRatio}-${Date.now()}.png`;
    a.href = url;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    if (isDemoMode()) consumeDemoQuota('photo');
  }

  public async getScreenshotBlob(aspectRatio: string = 'free'): Promise<Blob> {
    return await this.captureScreenshotBlob(aspectRatio);
  }

  public async exportProject(): Promise<ProjectData> {
    this.project.lights = this.lightManager.exportSettings();
    
    // Сохраняем позиции ламп
    const topPos = this.lightManager.getTopLightPosition();
    const bottomPos = this.lightManager.getBottomLightPosition();
    
    // Получаем глобальную reflectivity
    const envControl = this.materialManager.getUIControls()?.env;
    const globalReflectivity = envControl ? parseFloat(envControl.value || '0') : 0;
    
    // Сохраняем canvasData для принтов
    const layers: Record<string, LayerData> = {};
    
    // Сохраняем все слои из project.layers (rebuildLayersList НЕ нужен - он сбрасывает выделение)
    // Попутно кешируем исходный canvasData для принтов, чтобы не потерять его
    const prevPrintCanvasData = { ...this.printCanvasData };
    this.printCanvasData = {};
    for (const [uuid, state] of Object.entries(this.project.layers)) {
      // Конвертируем текстуры в dataURL для надежного сохранения
      const maps = await this.mapURLsAsync(state.maps);
      
      const normalizedCanvas = this.normalizePrintCanvasData(
        (state as any).canvasData ?? prevPrintCanvasData[uuid]
      );

      layers[uuid] = {
        name: state.name || '',
        preset: state.preset || null,
        params: state.params ? { ...state.params } : {}, // Копируем все параметры
        maps: maps,
        type: state.type || 'layer',
        uvGroupIds: (state as any).uvGroupIds,
        uvGroupId: (state as any).uvGroupId,
        canvasData: normalizedCanvas || undefined, // Сохраняем canvasData для принтов
        visible: state.visible, // Видимость (для принтов)
        labelColor: (state as any).labelColor, // UI-цвет кружка (для принтов)
      };

      if (state.type === 'print' && normalizedCanvas) {
        this.printCanvasData[uuid] = normalizedCanvas;
      }
    }
    
    console.log(`💾 Сохранено слоев: ${Object.keys(layers).length}`, layers);
    
    // Конвертируем модель в base64 если есть файл
    let modelDataURL: string | undefined;
    if (this.currentModelFile) {
      // Если есть сохраненный файл, используем его
      modelDataURL = await this.fileToDataURL(this.currentModelFile);
    } else if (this.currentModel && this.project.model.name !== 'Default') {
      // Если модель загружена, но файл не сохранен, пытаемся загрузить по пути
      try {
        const modelPath = this.currentModelPath || `/Afina/${this.project.model.name}`;
        const response = await fetch(modelPath);
        if (response.ok) {
          const blob = await response.blob();
          const file = new File([blob], this.project.model.name);
          modelDataURL = await this.fileToDataURL(file);
        }
      } catch (error) {
        console.warn('Не удалось загрузить модель для сохранения:', error);
      }
    }
    
    // Конвертируем aoMap модели в dataURL для сохранения
    let modelAoMapDataURL: string | undefined;
    if (this.project.model.aoMap && typeof this.project.model.aoMap !== 'string') {
      try {
        const aoMapTexture = this.project.model.aoMap as THREE.Texture;
        if (aoMapTexture.image) {
          const canvas = document.createElement('canvas');
          canvas.width = aoMapTexture.image.width || 512;
          canvas.height = aoMapTexture.image.height || 512;
          const ctx = canvas.getContext('2d');
          if (ctx && aoMapTexture.image) {
            ctx.drawImage(aoMapTexture.image, 0, 0);
            modelAoMapDataURL = canvas.toDataURL('image/png');
            console.log(`  ✅ AO Map модели конвертирован в dataURL для сохранения`);
          }
        }
      } catch (e) {
        console.warn(`  ⚠️ Не удалось конвертировать AO Map модели:`, e);
      }
    } else if (typeof this.project.model.aoMap === 'string') {
      modelAoMapDataURL = this.project.model.aoMap;
    }

    // Конвертируем aoMaps (per UV-group) в dataURL
    let modelAoMapsDataURL: Record<string, string | null> | undefined;
    if (this.project.model.aoMaps && Object.keys(this.project.model.aoMaps).length > 0) {
      modelAoMapsDataURL = {};
      for (const [gid, v] of Object.entries(this.project.model.aoMaps)) {
        if (!v) {
          modelAoMapsDataURL[gid] = null;
          continue;
        }
        if (typeof v === 'string') {
          modelAoMapsDataURL[gid] = v;
          continue;
        }
        try {
          const tex = v as THREE.Texture;
          if (tex.image) {
            const canvas = document.createElement('canvas');
            canvas.width = tex.image.width || 512;
            canvas.height = tex.image.height || 512;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(tex.image, 0, 0);
              modelAoMapsDataURL[gid] = canvas.toDataURL('image/png');
              continue;
            }
          }
        } catch (e) {
          console.warn(`  ⚠️ Не удалось конвертировать AO Map модели для группы ${gid}:`, e);
        }
        modelAoMapsDataURL[gid] = null;
      }
    }
    
    return {
      version: '1.0',
      model: {
        name: this.project.model.name,
        path: this.currentModelPath || undefined,
        dataURL: modelDataURL,
        aoMap: modelAoMapDataURL,
        aoMaps: modelAoMapsDataURL
      },
      environment: this.project.environment,
      background: this.project.background,
      cameraFov: this.camera.fov,
      lights: this.project.lights,
      lightPositions: {
        top: topPos,
        bottom: bottomPos
      },
      globalReflectivity: globalReflectivity,
      layers: layers,
      totalWorkTime: (this.project as any).totalWorkTime || 0,
      ui: (this.project as any).ui || undefined
    };
  }

  private fileToDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Экспорт проекта в FABP v2 формат (ZIP-архив)
   * Оптимизированный формат с разделением данных и сжатием
   */
  public async exportProjectV2(options?: FabpExportOptions): Promise<Blob> {
    // Этап 1: Сбор данных (0-30%)
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.project'), progress: 10 } 
    }));
    
    // Получаем данные проекта в старом формате
    const projectData = await this.exportProject();
    
    // Этап 2: Подготовка к экспорту (30-50%)
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.project'), progress: 40 } 
    }));
    
    // Конвертируем в новый ZIP формат
    const blob = await FabpFormat.export(projectData, {
      textureQuality: 0.85,
      textureFormat: 'webp',
      includeThumbnail: true,
      thumbnailSize: 256,
      ...options,
    });
    
    // Этап 3: Финализация (90-100%)
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.project'), progress: 95 } 
    }));
    
    console.log(`📦 Проект экспортирован в FABP v2 (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
    
    // Завершаем прогресс
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.project'), progress: 100 } 
    }));
    
    return blob;
  }

  /**
   * Загрузка проекта из файла (поддержка v1 JSON и v2 ZIP)
   */
  public async loadProjectFromFile(file: File): Promise<void> {
    try {
      // Этап 1: Чтение файла (0-30%)
      window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
        detail: { text: translate('loading.load.project'), progress: 15 } 
      }));
      
      const result = await FabpFormat.import(file);
      
      // Этап 2: Парсинг данных (30-50%)
      window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
        detail: { text: translate('loading.load.project'), progress: 40 } 
      }));
      
      console.log(`📂 Загружен проект формата ${result.formatVersion}`);
      
      if (result.metadata) {
        console.log(`   Название: ${result.metadata.name || 'Без названия'}`);
        console.log(`   Автор: ${result.metadata.author || 'Не указан'}`);
      }
      
      // Этап 3: Загрузка данных проекта (50-100%)
      window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
        detail: { text: translate('loading.load.project'), progress: 60 } 
      }));
      
      // Загружаем данные проекта
      await this.loadProject(result.projectData);
      
    } catch (error) {
      console.error('Ошибка загрузки проекта:', error);
      throw error;
    }
  }

  public async loadProject(projectData: ProjectData): Promise<void> {
    // Проверка подписки
    const isSubscriptionActive = (window as any).isSubscriptionActive;
    if (isSubscriptionActive && !isSubscriptionActive()) {
      console.warn('⛔ Загрузка проекта заблокирована - подписка истекла');
      alert('Загрузка проектов недоступна. Продлите подписку для доступа к полному функционалу.');
      return;
    }
    
    try {
      // ВАЖНО: Очищаем aoMap модели перед загрузкой нового проекта
      // Это предотвращает утечку aoMap от предыдущей модели
      if (this.project.model.aoMap && typeof this.project.model.aoMap !== 'string') {
        const oldAoMap = this.project.model.aoMap as THREE.Texture;
        try { oldAoMap.dispose(); } catch {}
      }
      this.project.model.aoMap = null;
      if (this.project.model.aoMaps) {
        Object.values(this.project.model.aoMaps).forEach((v) => {
          if (v && typeof v !== 'string') {
            try { (v as THREE.Texture).dispose(); } catch {}
          }
        });
      }
      this.project.model.aoMaps = {};
      
      // Восстанавливаем время работы (если есть)
      if (projectData.totalWorkTime !== undefined) {
        (this.project as any).totalWorkTime = projectData.totalWorkTime;
      }
      
      // UI state (persisted in project). Default "Вращать курсором" = Модель for new/legacy projects.
      (this.project as any).ui = projectData.ui || (this.project as any).ui || { fpsPanelDismissed: false };
      const rotMode = (projectData as any)?.ui?.rotationControlMode;
      const effectiveRotMode = (rotMode === 'scene' || rotMode === 'model') ? rotMode : 'model';
      if (!(this.project as any).ui.rotationControlMode) (this.project as any).ui.rotationControlMode = effectiveRotMode;
      this.setRotationControlMode(effectiveRotMode);
      window.dispatchEvent(new CustomEvent('projectUiLoaded', { detail: (this.project as any).ui }));
      
      let modelLoaded = false;
      
      // Загружаем модель
      if (projectData.model.dataURL) {
        try {
          // Конвертируем dataURL в File
          const file = await this.dataURLToFile(projectData.model.dataURL, projectData.model.name);
          this.loadModel(file);
          modelLoaded = true;
        } catch (error) {
          console.error('Ошибка конвертации модели:', error);
          alert('Ошибка загрузки модели из проекта. Модель не может быть восстановлена.');
          return;
        }
      } else if (projectData.model.path) {
        // Если есть только путь, пытаемся загрузить по пути
        try {
          const response = await fetch(projectData.model.path);
          if (!response.ok) {
            throw new Error(`Файл модели не найден: ${projectData.model.path}`);
          }
          const blob = await response.blob();
          const file = new File([blob], projectData.model.name);
          this.loadModel(file);
          modelLoaded = true;
        } catch (error) {
          const errorMessage = (error as Error).message;
          console.error('Ошибка загрузки модели по пути:', errorMessage);
          alert(`Модель не найдена!\n\nПуть: ${projectData.model.path}\n\nПожалуйста, загрузите модель вручную через меню Import → Модель.`);
          // Продолжаем загрузку остальных настроек даже если модель не найдена
        }
      } else {
        alert('В проекте не сохранена модель. Загрузите модель вручную через меню Import → Модель.');
      }
      
      // Ждем полной загрузки модели (увеличиваем время ожидания)
      if (modelLoaded) {
        // Ждем пока модель загрузится и обработается
        let attempts = 0;
        while (!this.currentModel && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (!this.currentModel) {
          console.warn('Модель не загрузилась в ожидаемое время, продолжаем загрузку настроек...');
        } else {
          // Загружаем и применяем aoMap модели (если есть)
          if ((projectData.model as any).aoMaps) {
            this.project.model.aoMaps = (projectData.model as any).aoMaps;
            // legacy fallback
            if (projectData.model.aoMap) this.project.model.aoMap = projectData.model.aoMap;
            console.log(`  📥 Загружаем AO Map модели (по UV-группам)...`);
            await this.applyModelAoMap();
          } else if (projectData.model.aoMap) {
            this.project.model.aoMap = projectData.model.aoMap;
            console.log(`  📥 Загружаем AO Map модели (legacy)...`);
            await this.applyModelAoMap();
          } else {
            console.log(`  ⚠️ AO Map модели отсутствует в проекте`);
          }
        }
      }
      
      // Применяем настройки освещения
      if (projectData.lights) {
        this.lightManager.applySettings(projectData.lights);
      }
      
      // Позиции ламп берём из projectData.lights (там world coords).
      // lightPositions используем только как fallback для старых проектов, где в lights не было position.
      const hasTopPos = !!(projectData.lights as any)?.top?.position;
      const hasBottomPos = !!(projectData.lights as any)?.bottom?.position;
      if ((!hasTopPos || !hasBottomPos) && projectData.lightPositions) {
        const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
        const MIN_X = -0.85, MAX_X = 0.85, MIN_Z = -0.85, MAX_Z = 0.15;
        if (!hasTopPos && projectData.lightPositions.top) {
          const top = projectData.lightPositions.top;
          this.lightManager.setTopLightPosition(clamp(top.x, MIN_X, MAX_X), clamp(top.z, MIN_Z, MAX_Z), top.y);
        }
        if (!hasBottomPos && projectData.lightPositions.bottom) {
          const bottom = projectData.lightPositions.bottom;
          this.lightManager.setBottomLightPosition(clamp(bottom.x, MIN_X, MAX_X), clamp(bottom.z, MIN_Z, MAX_Z), bottom.y);
        }
      }
      
      // Обновляем UI элементов управления светом (включая миникарту)
      // Ждем немного, чтобы миникарта успела инициализироваться
      setTimeout(() => {
      this.updateLightControlUI();
        // Также обновляем позиции кругов на миникарте напрямую
        this.updateMinimapLightPositions();
      }, 200);
      
      // Применяем HDRI
      if (projectData.environment) {
        this.setHDRI(projectData.environment);
        // Обновляем UI селекта HDRI
        const envSelect = document.getElementById('env-select') as HTMLSelectElement;
        if (envSelect) {
          envSelect.value = projectData.environment;
          // Обновляем кастомный dropdown
          const text = document.getElementById('env-select-text');
          const menu = document.getElementById('env-select-menu');
          if (text && menu) {
            const option = Array.from(envSelect.options).find(opt => opt.value === projectData.environment);
            if (option) {
              text.textContent = option.textContent || '';
              // Обновляем визуальное выделение
              menu.querySelectorAll('.custom-dropdown-item').forEach(item => {
                item.classList.remove('selected');
                if (item.getAttribute('data-value') === projectData.environment) {
                  item.classList.add('selected');
                }
              });
            }
          }
        }
      }
      
      // Применяем фон (с поддержкой градиента)
      if (projectData.background) {
        const bg = projectData.background as any;
        
        // Функция для обновления превью цвета в Coloris
        const updateColorPreview = (inputId: string, color: string) => {
          const input = document.getElementById(inputId) as HTMLInputElement;
          if (!input) return;
          input.value = color;
          
          // Coloris использует style.color на wrapper (.clr-field) для превью
          const updateWrapper = () => {
            const wrapper = input.parentElement;
            if (wrapper?.classList.contains('clr-field')) {
              wrapper.style.color = color;
              return true;
            }
            return false;
          };
          
          // Пробуем сразу, если не получилось - повторяем с задержкой
          if (!updateWrapper()) {
            setTimeout(updateWrapper, 100);
            setTimeout(updateWrapper, 300);
          }
        };
        
        if (bg.gradient) {
          this.setBackgroundGradient(bg.gradient.top, bg.gradient.bottom);
          // Сначала обновляем значения инпутов
          updateColorPreview('bg-color-top', bg.gradient.top);
          updateColorPreview('bg-color-bottom', bg.gradient.bottom);
          // Переключаем режим UI через setBgMode (обновляет переменную bgMode)
          // Используем задержку, чтобы убедиться, что инпуты обновлены
          setTimeout(() => {
            if (typeof (window as any).setBgMode === 'function') {
              // Устанавливаем режим - это обновит bgMode и UI, и применит фон с текущими значениями инпутов
              (window as any).setBgMode('gradient');
            } else {
              // Fallback если setBgMode еще не загружен
            const bgGradientControls = document.getElementById('bg-gradient-controls');
            const bgSolidControls = document.getElementById('bg-solid-controls');
            bgGradientControls?.classList.remove('hidden');
            bgSolidControls?.classList.add('hidden');
            document.getElementById('bg-mode-gradient')?.classList.add('active');
            document.getElementById('bg-mode-solid')?.classList.remove('active');
            document.getElementById('bg-mode-transparent')?.classList.remove('active');
            }
          }, 100);
        } else if (bg === 'transparent' || (typeof bg === 'string' && bg.includes('transparent'))) {
          this.setBackground('transparent');
          setTimeout(() => {
            if (typeof (window as any).setBgMode === 'function') {
              (window as any).setBgMode('transparent');
            } else {
              // Fallback если setBgMode еще не загружен
            const bgGradientControls = document.getElementById('bg-gradient-controls');
            const bgSolidControls = document.getElementById('bg-solid-controls');
            bgGradientControls?.classList.add('hidden');
            bgSolidControls?.classList.add('hidden');
            document.getElementById('bg-mode-transparent')?.classList.add('active');
            document.getElementById('bg-mode-solid')?.classList.remove('active');
            document.getElementById('bg-mode-gradient')?.classList.remove('active');
            }
          }, 50);
        } else {
          // Обычный цвет
          this.setBackground(bg);
          // Сначала обновляем значение инпута
          updateColorPreview('bg-color', bg);
          // Переключаем режим UI через setBgMode (обновляет переменную bgMode)
          setTimeout(() => {
            if (typeof (window as any).setBgMode === 'function') {
              (window as any).setBgMode('solid');
            } else {
              // Fallback если setBgMode еще не загружен
            const bgGradientControls = document.getElementById('bg-gradient-controls');
            const bgSolidControls = document.getElementById('bg-solid-controls');
            bgSolidControls?.classList.remove('hidden');
            bgGradientControls?.classList.add('hidden');
            document.getElementById('bg-mode-solid')?.classList.add('active');
            document.getElementById('bg-mode-gradient')?.classList.remove('active');
            document.getElementById('bg-mode-transparent')?.classList.remove('active');
            }
          }, 50);
        }
      }
      
      // Применяем глобальную reflectivity
      if (projectData.globalReflectivity !== undefined) {
        const envControl = this.materialManager.getUIControls()?.env;
        if (envControl) {
          envControl.value = projectData.globalReflectivity.toString();
          this.materialManager.applyGlobalReflectivity();
        }
      }
      
      // Применяем FOV камеры
      if (projectData.cameraFov !== undefined) {
        this.setCameraFov(projectData.cameraFov);
        const fovInput = document.getElementById('u-fov') as HTMLInputElement;
        const fovValue = document.getElementById('u-fov-value');
        const cinemaFovSlider = document.getElementById('cinema-fov-slider') as HTMLInputElement;
        const cinemaFovValue = document.getElementById('cinema-fov-value');
        const fovStr = Math.round(this.camera.fov).toString();
        
        if (fovInput) {
          fovInput.value = fovStr;
        }
        if (fovValue) {
          fovValue.textContent = `${fovStr}°`;
        }
        if (cinemaFovSlider) {
          cinemaFovSlider.value = fovStr;
          // Update fill
          const min = parseFloat(cinemaFovSlider.min) || 20;
          const max = parseFloat(cinemaFovSlider.max) || 90;
          const percent = ((this.camera.fov - min) / (max - min)) * 100;
          cinemaFovSlider.style.setProperty('--slider-value', percent + '%');
        }
        if (cinemaFovValue) {
          cinemaFovValue.textContent = fovStr;
        }
        
        // Photo FOV slider sync
        const photoFovSlider = document.getElementById('photo-fov-slider') as HTMLInputElement;
        const photoFovValue = document.getElementById('photo-fov-value');
        if (photoFovSlider) {
          photoFovSlider.value = fovStr;
          const min = parseFloat(photoFovSlider.min) || 20;
          const max = parseFloat(photoFovSlider.max) || 90;
          const percent = ((this.camera.fov - min) / (max - min)) * 100;
          photoFovSlider.style.setProperty('--slider-value', percent + '%');
        }
        if (photoFovValue) {
          photoFovValue.textContent = fovStr;
        }
      }
      
      // Применяем материалы и принты (только если модель загружена)
      if (projectData.layers && this.currentModel) {
        console.log(`📦 Загружаем слои: ${Object.keys(projectData.layers).length}`, projectData.layers);
        
        // Сначала обновляем список слоев модели
        this.rebuildLayersList();
        
        // Затем восстанавливаем данные слоев
        this.project.layers = { ...projectData.layers };
        // Кешируем исходный canvasData для принтов
        this.printCanvasData = {};
        Object.entries(projectData.layers).forEach(([id, layer]) => {
          if ((layer as any).type === 'print' && (layer as any).canvasData) {
            this.printCanvasData[id] = (layer as any).canvasData;
          }
        });
        this.materialManager.setProjectLayers({ ...projectData.layers });
        
        // Восстанавливаем время работы
        if (projectData.totalWorkTime !== undefined) {
          (this.project as any).totalWorkTime = projectData.totalWorkTime;
        }
        
        // Восстанавливаем принты и материалы для всех слоев
        await this.restoreLayers(projectData.layers);
        
        // Обновляем UI слоев после восстановления (важно делать это после восстановления всех принтов)
        this.rebuildLayersList();
        
        // Убеждаемся, что все принты в project.layers
        for (const [layerId, layerData] of Object.entries(projectData.layers)) {
          if (layerData.type === 'print' && !this.project.layers[layerId]) {
            this.project.layers[layerId] = { ...layerData };
          }
        }
        
        // Снова обновляем UI после добавления всех слоев
        this.rebuildLayersList();
        
        // Повторно применяем globalReflectivity ко всем материалам после восстановления
        this.materialManager.applyGlobalReflectivity();
        
        // Выбираем первый слой и синхронизируем UI
        await new Promise(resolve => setTimeout(resolve, 500));
        const firstLayer = document.querySelector('.layer') as HTMLElement;
        if (firstLayer) {
          firstLayer.click();
          
          // Явно обновляем превьюшку материала после небольшой задержки
          // чтобы убедиться, что все компоненты инициализированы
          setTimeout(() => {
            const layerId = firstLayer.dataset.id;
            if (layerId) {
              const layerData = this.project.layers[layerId];
              if (layerData) {
                window.dispatchEvent(new CustomEvent('materialPresetSelect', { 
                  detail: { id: layerData.preset || null } 
                }));
              }
            }
          }, 200);
        } else {
          this.ensureLayerSelected();
        }
      }
      
      console.log('✅ Проект загружен');
    } catch (error) {
      console.error('Ошибка загрузки проекта:', error);
      alert('Ошибка загрузки проекта: ' + (error as Error).message);
    }
  }

  private updateLightControlUI(): void {
    // Обновляем позиции кругов на сетке
    const topPos = this.lightManager.getTopLightPosition();
    const bottomPos = this.lightManager.getBottomLightPosition();
    
    const topCircle = document.getElementById('light-top-circle');
    const bottomCircle = document.getElementById('light-bottom-circle');
    
    // Преобразуем координаты с учетом ограничений границ (-0.85 до 0.85 по x, -0.85 до 0.15 по z)
    const minX = -0.85, maxX = 0.85;
    const minZ = -0.85, maxZ = 0.15;
    
    if (topCircle) {
      const percentX = ((topPos.x - minX) / (maxX - minX)) * 100;
      const percentZ = ((topPos.z - minZ) / (maxZ - minZ)) * 100;
      topCircle.style.left = `${Math.max(0, Math.min(100, percentX))}%`;
      topCircle.style.top = `${Math.max(0, Math.min(100, percentZ))}%`;
    }
    
    if (bottomCircle) {
      const percentX = ((bottomPos.x - minX) / (maxX - minX)) * 100;
      const percentZ = ((bottomPos.z - minZ) / (maxZ - minZ)) * 100;
      bottomCircle.style.left = `${Math.max(0, Math.min(100, percentX))}%`;
      bottomCircle.style.top = `${Math.max(0, Math.min(100, percentZ))}%`;
    }
    
    // Обновляем слайдер Distance для выбранной лампы (если есть)
    const distanceInput = document.getElementById('light-distance') as HTMLInputElement;
    const distanceValue = document.getElementById('light-distance-value') as HTMLElement | null;
    if (distanceInput && this.selectedLight) {
      const currentPos = this.selectedLight === 'top' ? topPos : bottomPos;
      distanceInput.value = currentPos.y.toString();
      if (distanceValue) {
        distanceValue.textContent = currentPos.y.toFixed(1);
      }
    }
    
    // Обновляем цвет света (значение и превьюшку Coloris)
    const lightColorInput = document.getElementById('light-color') as HTMLInputElement;
    if (lightColorInput) {
      const color = this.lightManager.getLightColor();
      lightColorInput.value = color;
      
      // Обновляем превьюшку Coloris (.clr-field)
      const updateWrapper = () => {
        const wrapper = lightColorInput.parentElement;
        if (wrapper?.classList.contains('clr-field')) {
          wrapper.style.color = color;
          return true;
        }
        return false;
      };
      
      // Пробуем сразу, если не получилось - повторяем с задержкой
      if (!updateWrapper()) {
        setTimeout(updateWrapper, 100);
        setTimeout(updateWrapper, 300);
      }
    }
  }
  
  private updateMinimapLightPositions(): void {
    const gridView = document.getElementById('light-grid-view');
    if (!gridView) return;

    // Обновляем позиции кругов на миникарте на основе текущих позиций ламп (единственный источник истины)
    const topPos = this.lightManager.getTopLightPosition();
    const bottomPos = this.lightManager.getBottomLightPosition();
    const leftPos = this.lightManager.getRimLightPosition('left');
    const rightPos = this.lightManager.getRimLightPosition('right');
    
    const topCircle = document.getElementById('light-top-circle');
    const bottomCircle = document.getElementById('light-bottom-circle');
    const leftCircle = document.getElementById('light-left-circle');
    const rightCircle = document.getElementById('light-right-circle');
    
    const MIN_X = -0.85;
    const MAX_X = 0.85;
    const MIN_Z = -0.85;
    const MAX_Z = 0.15;
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    const rect = gridView.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const controls = gridView.querySelector('.light-controls-bottom') as HTMLElement | null;
    let maxY = rect.height;
    if (controls) {
      const cr = controls.getBoundingClientRect();
      maxY = Math.max(0, cr.top - rect.top);
    }
    const pad = 10;
    const left = pad;
    const top = pad;
    const width = Math.max(1, rect.width - pad * 2);
    const height = Math.max(1, Math.min(rect.height, maxY) - pad * 2);

    const place = (circle: HTMLElement | null, x: number, z: number) => {
      if (!circle) return;
      const r = (circle.offsetWidth || 18) / 2;
      const nx = (x - MIN_X) / (MAX_X - MIN_X);
      const nz = (z - MIN_Z) / (MAX_Z - MIN_Z);
      let px = left + nx * width;
      let py = top + nz * height;
      px = clamp(px, left + r, left + width - r);
      py = clamp(py, top + r, top + height - r);
      circle.style.left = `${px}px`;
      circle.style.top = `${py}px`;
    };

    place(topCircle, topPos.x, topPos.z);
    place(bottomCircle, bottomPos.x, bottomPos.z);
    place(leftCircle, leftPos.x, leftPos.z);
    place(rightCircle, rightPos.x, rightPos.z);
  }

  private dataURLToFile(dataURL: string, filename: string): Promise<File> {
    return new Promise((resolve, reject) => {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      resolve(new File([u8arr], filename, { type: mime }));
    });
  }

  private async restoreLayers(layers: Record<string, LayerData>): Promise<void> {
    // Сначала восстанавливаем принты (они должны быть созданы перед применением материалов)
    const printLayers: Array<[string, LayerData]> = [];
    const regularLayers: Array<[string, LayerData]> = [];
    
    for (const [layerId, layerData] of Object.entries(layers)) {
      if (layerData.type === 'print') {
        printLayers.push([layerId, layerData]);
      } else {
        regularLayers.push([layerId, layerData]);
      }
    }
    
    // Восстанавливаем принты
    for (const [layerId, layerData] of printLayers) {
      console.log(`🖼️ Восстанавливаем принт ${layerId}:`, layerData);
      
      // Проверяем, существует ли принт
      let printMeshes = this.getPrintMeshesById(layerId);
      
      // Если принт не существует, создаем его
      if (printMeshes.length === 0) {
        console.log(`  Создаем новый принт с ID: ${layerId}...`);
        let dataURL: string | null = null;
        
        // Для создания принт-мешей используем запечённую карту (map),
        // а canvasData храним отдельно для редактора.
        if (layerData.maps?.map) {
          const mapUrl: string | null = typeof layerData.maps.map === 'string' ? layerData.maps.map : null;
          if (mapUrl) {
            const urlStr = String(mapUrl);
            if (urlStr.startsWith('data:') || urlStr.startsWith('blob:') || urlStr.startsWith('http')) {
              dataURL = mapUrl;
              console.log(`  Используем maps.map как источник текстуры для мешей: ${urlStr.substring(0, 50)}...`);
            }
          }
        }
        
        if (dataURL) {
          console.log(`  Найден dataURL, создаем принт: ${String(dataURL).substring(0, 50)}...`);
          console.log(`  🔑 Передаём layerId=${layerId} в addPrintLayerFromDataURL`);
          // Создаем принт из сохраненного dataURL с использованием сохраненного ID
          const createdPrintId = await this.addPrintLayerFromDataURL(
            dataURL,
            layerId,
            (layerData as any).uvGroupIds || ((layerData as any).uvGroupId ? [(layerData as any).uvGroupId] : undefined)
          );
          console.log(`  🔑 Получили createdPrintId=${createdPrintId} (должен совпадать с ${layerId})`);
          console.log(`  🔑 Слой существует в project.layers? ${!!this.project.layers[createdPrintId]}`);
          
          // Обновляем данные слоя после создания принта
          // ВАЖНО: сохраняем текстуру, которую создал addPrintLayerFromDataURL
          if (this.project.layers[createdPrintId]) {
            const currentTexture = this.project.layers[createdPrintId].maps.map;
            // Копируем все данные из layerData, но сохраняем текущую текстуру
            this.project.layers[createdPrintId] = { 
              ...layerData,
              maps: { 
                ...layerData.maps, 
                map: currentTexture // Сохраняем текстуру Three.js
              }
            };
            // Сохраняем canvasData отдельно для надёжности
            // ВАЖНО: используем savePrintCanvasData для кеширования в обоих местах
            if (layerData.canvasData) {
              this.savePrintCanvasData(createdPrintId, layerData.canvasData);
              console.log(`🖼️ loadProject: Сохранён canvasData для ${createdPrintId}:`, {
                hasObjects: !!layerData.canvasData.objects,
                objectsCount: layerData.canvasData.objects?.length,
                firstObjectType: layerData.canvasData.objects?.[0]?.type,
                firstObjectSrcLen: layerData.canvasData.objects?.[0]?.src?.length
              });
            } else {
              console.warn(`🖼️ loadProject: НЕТ canvasData для ${createdPrintId}`);
            }
          }
          
          // Ждем создания мешей принта
          await new Promise(resolve => setTimeout(resolve, 500));
          printMeshes = this.getPrintMeshesById(createdPrintId);
          console.log(`  Найдено мешей принта: ${printMeshes.length}`);
          if (printMeshes.length === 0) {
            console.warn(`  ⚠️ Меши принта не найдены! Ищем в сцене...`);
            this.currentModel?.traverse((obj) => {
              if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
                const mesh = obj as THREE.Mesh;
                const meshPrintId = (mesh as any).userData?.printId;
                console.log(`    Найден меш: ${mesh.name}, printId: ${meshPrintId}, ищем: ${createdPrintId}`);
              }
            });
          }
        } else {
          console.warn(`  ⚠️ Нет dataURL для создания принта. canvasData:`, layerData.canvasData, `maps.map:`, layerData.maps?.map);
        }
      } else {
        console.log(`  Принт уже существует, обновляем текстуру...`);
        // Принт уже существует, обновляем текстуру
        // ВАЖНО: Также сохраняем canvasData для редактирования
        if (layerData.canvasData) {
          this.savePrintCanvasData(layerId, layerData.canvasData);
          console.log(`🖼️ restoreLayers: Сохранён canvasData для существующего принта ${layerId}:`, {
            objectsCount: layerData.canvasData.objects?.length
          });
        }
        
        // Обновляем текстуру на модели из запечённой карты (maps.map);
        // canvasData остаётся для редактора и не перезаписывается.
        if (layerData.maps?.map) {
          const textureUrl = typeof layerData.maps.map === 'string' ? layerData.maps.map : null;
          if (textureUrl) {
            this.updatePrintLayerTexture(textureUrl, layerId);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      }
      
      // Применяем параметры материала для принта
      if (printMeshes.length > 0) {
        console.log(`  ✅ Найдено ${printMeshes.length} мешей принта, применяем материалы...`);
        if (layerData.params) {
          const mesh = printMeshes[0];
          await this.applyLayerData(mesh, layerData);
          
          // Применяем aoMap модели к принту после применения материалов
          if ((this.project.model.aoMaps && Object.keys(this.project.model.aoMaps).length > 0) || this.project.model.aoMap) {
            await this.applyModelAoMap();
          }
        }
        
        // Убеждаемся, что все меши принта имеют правильную видимость и их материалы обновлены
        // Учитываем сохранённое состояние видимости (по умолчанию видимый)
        const shouldBeVisible = layerData.visible !== false;
        printMeshes.forEach((mesh: THREE.Mesh) => {
          mesh.visible = shouldBeVisible;
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach((material: THREE.Material) => {
            if ((material as any).map) {
              (material as any).map.needsUpdate = true;
            }
            material.needsUpdate = true;
            console.log(`    ✓ Материал обновлен для меша: ${mesh.name}, видимый: ${mesh.visible}`);
          });
        });
      } else {
        console.warn(`  ⚠️ Не удалось найти меши принта для применения материалов (ID: ${layerId})`);
        // Пробуем найти все принт-меши в сцене для отладки
        const allPrintMeshes: THREE.Mesh[] = [];
        this.currentModel?.traverse((obj) => {
          if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
            allPrintMeshes.push(obj as THREE.Mesh);
          }
        });
        console.log(`  Всего принт-мешей в сцене: ${allPrintMeshes.length}`);
        allPrintMeshes.forEach(mesh => {
          const meshPrintId = (mesh as any).userData?.printId;
          console.log(`    - ${mesh.name}, printId: ${meshPrintId}, видимый: ${mesh.visible}`);
        });
      }
      
      // Убеждаемся, что слой принта зарегистрирован в project.layers
      if (!this.project.layers[layerId]) {
        this.project.layers[layerId] = { ...layerData };
        console.log(`  ✅ Слой принта зарегистрирован в project.layers: ${layerId}`);
      }

      // После создания/обновления — применяем фильтры UV-групп принта (если они сохранены)
      const savedGroups =
        (layerData as any).uvGroupIds ||
        ((layerData as any).uvGroupId ? [(layerData as any).uvGroupId] : null);
      if (savedGroups && Array.isArray(savedGroups) && savedGroups.length > 0) {
        await this.setPrintLayerUVGroups(layerId, savedGroups);
      }
    }

    // Повторно рендерим принты из canvasData, чтобы позиции совпадали с редактором
    // (при загрузке из maps.map картинка может отличаться по размеру/трансформу)
    for (const [layerId, layerData] of printLayers) {
      const canvasData = this.getPrintCanvasData(layerId) ?? (layerData as any).canvasData;
      if (canvasData?.objects?.length) {
        try {
          const dataUrl = await CanvasEditor.renderCanvasDataToDataURL(canvasData);
          if (dataUrl) {
            this.updatePrintLayerTexture(dataUrl, layerId);
          }
        } catch (e) {
          console.warn('Re-render print from canvasData failed for', layerId, e);
        }
      }
    }
    
    // Обновляем список слоев после восстановления всех принтов
    console.log(`📋 Обновляем список слоев после восстановления принтов...`);
    console.log(`📋 project.layers keys:`, Object.keys(this.project.layers));
    console.log(`📋 Слои с canvasData:`, Object.entries(this.project.layers)
      .filter(([_, v]) => (v as any).canvasData)
      .map(([k, v]) => ({ id: k, objectsCount: (v as any).canvasData?.objects?.length })));
    this.rebuildLayersList();
    
    // Затем восстанавливаем материалы для обычных слоев
    for (const [layerId, layerData] of regularLayers) {
      const mesh = this.findMeshByUuid(layerId);
      if (mesh) {
        console.log(`✅ Восстанавливаем слой ${layerData.name} (${layerId})`, layerData);
        await this.applyLayerData(mesh, layerData);
      } else {
        console.warn(`⚠️ Меш не найден для слоя ${layerData.name} (${layerId})`);
        // Пытаемся найти по имени
        if (layerData.name && this.currentModel) {
          this.currentModel.traverse((obj) => {
            if ((obj as any).isMesh && obj.name === layerData.name) {
              const foundMesh = obj as THREE.Mesh;
              console.log(`✅ Найден меш по имени: ${layerData.name}, новый UUID: ${foundMesh.uuid}`);
              // Обновляем UUID в project.layers
              this.project.layers[foundMesh.uuid] = { ...layerData };
              delete this.project.layers[layerId];
              this.materialManager.setProjectLayers(this.project.layers);
              // Применяем материалы
              this.applyLayerData(foundMesh, layerData);
            }
          });
        }
      }
    }
  }

  private findMeshByUuid(uuid: string): THREE.Mesh | null {
    if (!this.currentModel) return null;
    let found: THREE.Mesh | null = null;
    
    // Сначала пытаемся найти по UUID
    this.currentModel.traverse((obj) => {
      if ((obj as any).isMesh && obj.uuid === uuid) {
        found = obj as THREE.Mesh;
      }
    });
    
    // Если не нашли по UUID, пытаемся найти по имени из сохраненных данных
    if (!found) {
      const layerData = this.project.layers[uuid];
      if (layerData && layerData.name) {
        this.currentModel.traverse((obj) => {
          if ((obj as any).isMesh && obj.name === layerData.name && !found) {
            found = obj as THREE.Mesh;
            // Обновляем UUID в project.layers для будущих обращений
            if (found) {
              const newUuid = found.uuid;
              if (newUuid !== uuid) {
                this.project.layers[newUuid] = this.project.layers[uuid];
                delete this.project.layers[uuid];
                this.materialManager.setProjectLayers(this.project.layers);
              }
            }
          }
        });
      }
    }
    
    return found;
  }

  private async applyLayerData(mesh: THREE.Mesh, layerData: LayerData): Promise<void> {
    console.log(`🎨 Применяем материалы к ${layerData.name} (меш: ${mesh.name}, UUID: ${mesh.uuid}):`, {
      type: layerData.type,
      preset: layerData.preset,
      params: {
        baseColor: layerData.params.baseColor,
        tiling: layerData.params.tiling,
        rotationDeg: layerData.params.rotationDeg,
        roughness: layerData.params.roughness,
        metalness: layerData.params.metalness,
        normalScale: layerData.params.normalScale
      },
      maps: {
        hasMap: !!layerData.maps?.map,
        hasNormal: !!layerData.maps?.normal,
        hasRoughness: !!layerData.maps?.roughness,
        hasMetalness: !!layerData.maps?.metalness
      }
    });
    
    // Определяем все меши для этого слоя
    let meshesToApply: THREE.Mesh[] = [mesh];
    
    if (layerData.type === 'print') {
      // Для принтов применяем ко всем мешам принта
      const printId = (mesh as any).userData?.printId;
      if (printId) {
        meshesToApply = this.getPrintMeshesById(printId);
        console.log(`  Найдено мешей принта: ${meshesToApply.length}`);
      }
    }
    
    // Если есть preset - загружаем текстуры С СЕРВЕРА (и для принтов тоже: normal, roughness и т.д.)
    // Для принтов не трогаем map — это текстура дизайна принта
    if (layerData.preset) {
      console.log(`  📦 Загружаем пресет "${layerData.preset}" с сервера${layerData.type === 'print' ? ' (принт: сохраняем map)' : ''}...`);
      
      // Устанавливаем курсор загрузки
      document.body.classList.add('loading-material');
      
      // Показываем индикатор загрузки материала
      const toastId = 'material-loading';
      window.dispatchEvent(new CustomEvent('showLoadingToast', { 
        detail: { id: toastId, text: translate('loading.material'), progress: 0 } 
      }));
      
      await this.loadPresetTexturesFromServer(layerData, meshesToApply, {
        preserveColorMap: layerData.type === 'print'
      });
      
      // Готово
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: toastId, progress: 100 } 
      }));
      
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('hideLoadingToast', { 
          detail: { id: toastId } 
        }));
        // Убираем курсор загрузки
        document.body.classList.remove('loading-material');
      }, 500);
    }
    
    // Применяем материалы ко всем мешам слоя
    for (const targetMesh of meshesToApply) {
      const materials = Array.isArray(targetMesh.material) ? targetMesh.material : [targetMesh.material];
      
      for (const mat of materials) {
        const material = ensurePBR(mat) as THREE.MeshStandardMaterial;
        
        // Сначала применяем базовые параметры (кроме цвета - его применим после текстур)
        // Применяем roughness (с учетом roughnessMul если есть)
        if (layerData.params.roughness !== undefined) {
          const roughnessMul = layerData.params.roughnessMul !== undefined ? layerData.params.roughnessMul : 1;
          material.roughness = THREE.MathUtils.clamp(layerData.params.roughness * roughnessMul, 0, 1);
          console.log(`  ✓ Roughness применен: ${material.roughness}`);
        }
        
        // Применяем metalness
        if (layerData.params.metalness !== undefined) {
          material.metalness = THREE.MathUtils.clamp(layerData.params.metalness, 0, 1);
          console.log(`  ✓ Metalness применен: ${material.metalness}`);
        }
        
        // Применяем envMapIntensity из глобальных настроек
        const envControl = this.materialManager.getUIControls()?.env;
        if (envControl) {
          const envIntensity = parseFloat(envControl.value || '0');
          (material as any).envMapIntensity = envIntensity;
        }
        
        // Применяем normalScale
        if (layerData.params.normalScale !== undefined) {
          material.normalScale = new THREE.Vector2(
            layerData.params.normalScale,
            layerData.params.normalScale
          );
        }
        
        // Загружаем текстуры только если НЕТ пресета
        // (для слоёв с пресетом текстуры уже загружены из loadPresetTexturesFromServer, включая принты)
        if (layerData.maps && !layerData.preset) {
          const tiling = layerData.params.tiling !== undefined ? layerData.params.tiling : 8;
          const rotation = (layerData.params.rotationDeg || 0) * Math.PI / 180;
          
          // Вспомогательная функция для загрузки текстуры из dataURL
          const loadTextureFromMap = async (mapValue: any, mapName: string, isColorMap: boolean = false): Promise<THREE.Texture | null> => {
            if (!mapValue) return null;
            const url: string | null = typeof mapValue === 'string' ? mapValue : null;
            if (url) {
              console.log(`  Загружаем ${mapName}: ${url.substring(0, 50)}...`);
              // Для color/albedo карт используем SRGB цветовое пространство для правильных цветов
              const colorSpace = isColorMap ? THREE.SRGBColorSpace : undefined;
              const texture = await loadRemoteTexture(url as any, colorSpace);
              if (texture) {
                // Для color карт также устанавливаем wrap mode
                if (isColorMap) {
                  texture.wrapS = THREE.RepeatWrapping;
                  texture.wrapT = THREE.RepeatWrapping;
                }
                setTextureProperties(texture, tiling, rotation);
                console.log(`  ✓ ${mapName} загружена и применена${isColorMap ? ' (SRGB)' : ''}`);
                return texture;
              } else {
                console.warn(`  ⚠️ Не удалось загрузить ${mapName}`);
              }
            } else if (mapValue && typeof mapValue === 'object') {
              // Уже THREE.Texture объект - проверяем colorSpace
              const texture = mapValue as THREE.Texture;
              if (isColorMap && texture.colorSpace !== THREE.SRGBColorSpace) {
                texture.colorSpace = THREE.SRGBColorSpace;
              }
              return texture;
            }
            return null;
          };
          
          // Загружаем/сбрасываем normal map; смешиваем с нормал-картой модели если она есть
          {
            let normalTexture: THREE.Texture | null = null;
            if (layerData.maps.normal) {
              normalTexture = await loadTextureFromMap(layerData.maps.normal, 'normal map');
              if (normalTexture) {
                layerData.maps.normal = normalTexture as any;
              }
            }
            // Используем MaterialManager для блендинга (он знает о modelNormalMap)
            const tiling2 = layerData.params.tiling !== undefined ? layerData.params.tiling : 8;
            const rotation2 = (layerData.params.rotationDeg || 0) * Math.PI / 180;
            const setupTex = (tex: THREE.Texture) => {
              tex.repeat.set(tiling2, tiling2);
              tex.rotation = rotation2;
              tex.wrapS = THREE.RepeatWrapping;
              tex.wrapT = THREE.RepeatWrapping;
              tex.center.set(0.5, 0.5);
              tex.needsUpdate = true;
            };
            this.materialManager.applyNormalMapsPublic(material, normalTexture, setupTex);
            if (normalTexture && layerData.params.normalScale !== undefined) {
              material.normalScale = new THREE.Vector2(
                layerData.params.normalScale,
                layerData.params.normalScale
              );
            }
          }
          
          // Восстанавливаем/сбрасываем основную (color) текстуру (только для обычных слоёв)
          if (layerData.type !== 'print') {
            if (layerData.maps.color) {
              const colorTexture = await loadTextureFromMap(layerData.maps.color, 'color/albedo map', true);
              if (colorTexture) {
                (material as any).map = colorTexture;
                // Обновляем в layerData для синхронизации
                layerData.maps.color = colorTexture as any;
              } else {
                (material as any).map = null;
              }
            } else {
              (material as any).map = null;
            }
          }
          
          // Загружаем/сбрасываем roughness map
          if (layerData.maps.roughness) {
            const roughnessTexture = await loadTextureFromMap(layerData.maps.roughness, 'roughness map');
            if (roughnessTexture) {
              (material as any).roughnessMap = roughnessTexture;
              layerData.maps.roughness = roughnessTexture as any;
            } else {
              (material as any).roughnessMap = null;
            }
          } else {
            (material as any).roughnessMap = null;
          }
          
          // AO Map не загружаем для отдельных слоев - он применяется ко всей модели
          
          // Загружаем/сбрасываем metalness map
          if (layerData.maps.metalness) {
            const metalnessTexture = await loadTextureFromMap(layerData.maps.metalness, 'metalness map');
            if (metalnessTexture) {
              (material as any).metalnessMap = metalnessTexture;
              layerData.maps.metalness = metalnessTexture as any;
            } else {
              (material as any).metalnessMap = null;
            }
          } else {
            (material as any).metalnessMap = null;
          }
          
          // Загружаем/сбрасываем opacity map (alphaMap)
          if (layerData.maps.opacity) {
            const opacityTexture = await loadTextureFromMap(layerData.maps.opacity, 'opacity map');
            if (opacityTexture) {
              (material as any).alphaMap = opacityTexture;
              (material as any).transparent = true;
              layerData.maps.opacity = opacityTexture as any;
            } else {
              (material as any).alphaMap = null;
              // Для обычных слоёв прозрачность должна выключаться, если opacity карты нет
              if (layerData.type !== 'print') (material as any).transparent = false;
            }
          } else {
            (material as any).alphaMap = null;
            if (layerData.type !== 'print') (material as any).transparent = false;
          }
        }
        
        // ВАЖНО: Применяем цвет ПОСЛЕ загрузки текстур
        // Всегда применяем сохранённый baseColor (если есть) - он умножается с текстурой
        // Это позволяет тонировать текстуры цветом
        if (layerData.params.baseColor) {
          material.color.set(layerData.params.baseColor);
          console.log(`  ✓ Цвет применен из baseColor: ${layerData.params.baseColor}`);
        } else {
          // Нет сохранённого цвета - устанавливаем белый по умолчанию
          material.color.set(1, 1, 1);
          console.log(`  ✓ Цвет установлен в белый по умолчанию`);
        }
        
        material.needsUpdate = true;
      }
    }
    
    // Синхронизируем состояние слоя в MaterialManager
    const layerId = layerData.type === 'print' ? (mesh as any).userData?.printId : mesh.uuid;
    if (layerId) {
      // Обновляем state в projectLayers с актуальными текстурами из материала
      const state = this.project.layers[layerId];
      if (state) {
        // Вспомогательная функция для подготовки текстуры
        const prepareTexture = (tex: any): THREE.Texture | null => {
          if (!tex || typeof tex === 'string') return null;
          if (!tex.repeat || typeof tex.repeat.set !== 'function') {
            tex.repeat = new THREE.Vector2(1, 1);
          }
          if (!tex.center || typeof tex.center.set !== 'function') {
            tex.center = new THREE.Vector2(0.5, 0.5);
          }
          return tex;
        };
        
        // Копируем текстуры из материала (если они уже применены)
        const materials = meshesToApply[0] ? (Array.isArray(meshesToApply[0].material) ? meshesToApply[0].material : [meshesToApply[0].material]) : [];
        if (materials.length > 0) {
          const material = materials[0] as any;
          
          // Обновляем нормальную карту из материала
          if (material.normalMap) {
            const tex = prepareTexture(material.normalMap);
            if (tex) state.maps.normal = tex;
          } else {
            delete state.maps.normal;
          }
          
          // Обновляем основную текстуру (только для обычных слоев)
          if (layerData.type !== 'print' && material.map) {
            const tex = prepareTexture(material.map);
            if (tex) {
              state.maps.color = tex;
              state.maps.map = state.maps.map || tex;
            }
          } else if (layerData.type !== 'print') {
            delete state.maps.color;
          }
          
          // Обновляем roughness map
          if (material.roughnessMap) {
            const tex = prepareTexture(material.roughnessMap);
            if (tex) state.maps.roughness = tex;
          } else {
            delete state.maps.roughness;
          }
          
          // AO Map не обновляем для отдельных слоев - он на уровне модели
          
          // Обновляем metalness map
          if (material.metalnessMap) {
            const tex = prepareTexture(material.metalnessMap);
            if (tex) state.maps.metalness = tex;
          } else {
            delete state.maps.metalness;
          }
          
          // Обновляем opacity map
          if (material.alphaMap) {
            const tex = prepareTexture(material.alphaMap);
            if (tex) state.maps.opacity = tex;
          } else {
            delete state.maps.opacity;
          }
        }
        
        // Обновляем params
        state.params = { ...layerData.params };
        
        // Синхронизируем с MaterialManager
        this.materialManager.syncLayerState(layerId, state);
      }
    }
    
    console.log(`✅ Материалы применены к ${layerData.name}`);
    
    // Для принтов: применяем aoMap модели после применения материалов
    if (layerData.type === 'print' && ((this.project.model.aoMaps && Object.keys(this.project.model.aoMaps).length > 0) || this.project.model.aoMap)) {
      await this.applyModelAoMap();
    }
  }

  /**
   * Загружает текстуры пресета с сервера по его ID
   * Это оптимизирует размер файлов проекта: мы сохраняем только ID пресета,
   * а текстуры загружаются с сервера при открытии проекта
   */
  private async loadPresetTexturesFromServer(
    layerData: LayerData,
    meshes: THREE.Mesh[],
    options?: { preserveColorMap?: boolean }
  ): Promise<void> {
    if (!layerData.preset) return;
    const preserveColorMap = !!options?.preserveColorMap; // для принтов: не трогать map (дизайн принта)
    
    try {
      // Загружаем индекс пресетов
      const presetsIndex = await fetchJSON('/presets/index.json') as Array<{id: string, path: string}>;
      const presetInfo = presetsIndex.find(p => p.id === layerData.preset);
      
      if (!presetInfo) {
        console.warn(`  ⚠️ Пресет "${layerData.preset}" не найден в index.json`);
        return;
      }
      
      const baseURL = `/presets/${presetInfo.path}`;
      console.log(`  📂 Путь пресета: ${baseURL}`);
      
      // Загружаем manifest пресета
      const manifest = await fetchJSON(`${baseURL}preset.json`) as PresetManifest;
      const maps = manifest.maps || {};
      
      console.log(`  📋 Manifest пресета:`, { maps: Object.keys(maps), params: manifest.params });
      
      // Загружаем все PBR карты с сервера
      const loadedTextures: Record<string, THREE.Texture | null> = {};
      
      // Стандартные имена файлов для автоопределения (если нет в manifest)
      const standardNames: Record<string, string[]> = {
        color: ['color.jpg', 'color.png', 'albedo.jpg', 'albedo.png'],
        normal: ['normal.jpg', 'normal.png', 'normal.webp', 'normalgl.jpg'],
        roughness: ['roughness.jpg', 'roughness.png'],
        ao: ['ao.jpg', 'ao.png', 'occlusion.jpg'],
        metalness: ['metalness.jpg', 'metalness.png', 'metallic.jpg'],
        opacity: ['opacity.jpg', 'opacity.png', 'alpha.jpg', 'alpha.png']
      };
      
      const tryLoadTexture = async (url: string, colorSpace: THREE.ColorSpace): Promise<THREE.Texture | null> => {
        try {
          const response = await fetch(url, { method: 'HEAD' }).catch(() => null);
          if (!response || !response.ok) return null;
          return await loadRemoteTexture(url, colorSpace);
        } catch {
          return null;
        }
      };
      
      const hasAnyMapsInManifest = maps.normal || maps.color || maps.roughness || maps.ao || maps.metalness || maps.opacity;
      
      const totalMaps = Object.keys(standardNames).length;
      let loadedMaps = 0;
      
      for (const [mapType, filenames] of Object.entries(standardNames)) {
        const colorSpace = mapType === 'color' ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace;
        
        // Если карта указана в manifest - загружаем её
        if ((maps as any)[mapType]) {
          const texture = await loadRemoteTexture(baseURL + (maps as any)[mapType], colorSpace);
          if (texture) {
            loadedTextures[mapType] = texture;
            console.log(`  ✓ Загружена ${mapType} из manifest: ${(maps as any)[mapType]}`);
          }
          loadedMaps++;
          // Обновляем прогресс (0-80% для загрузки текстур)
          const progress = Math.round((loadedMaps / totalMaps) * 80);
          window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
            detail: { id: 'material-loading', progress } 
          }));
        } 
        // Автоопределение только если в manifest нет карт
        else if (!hasAnyMapsInManifest) {
          for (const filename of filenames) {
            const tex = await tryLoadTexture(baseURL + filename, colorSpace);
            if (tex) {
              loadedTextures[mapType] = tex;
              console.log(`  ✓ Автоопределена ${mapType}: ${filename}`);
              break;
            }
          }
          loadedMaps++;
          // Обновляем прогресс (0-80% для загрузки текстур)
          const progress = Math.round((loadedMaps / totalMaps) * 80);
          window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
            detail: { id: 'material-loading', progress } 
          }));
        }
      }
      
      // Прогресс 85% - текстуры загружены, применяем
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: 'material-loading', progress: 85 } 
      }));
      
      // Применяем параметры tiling и rotation
      const tiling = layerData.params.tiling !== undefined ? layerData.params.tiling : (manifest.params?.tiling || 8);
      const rotation = ((layerData.params.rotationDeg !== undefined ? layerData.params.rotationDeg : (manifest.params?.rotationDeg || 0))) * Math.PI / 180;
      
      console.log(`  🎯 Применяем параметры${preserveColorMap ? ' (принт)' : ''}:`, {
        tiling,
        rotationDeg: layerData.params.rotationDeg,
        roughness: layerData.params.roughness,
        metalness: layerData.params.metalness,
        normalScale: layerData.params.normalScale,
        savedParams: layerData.params
      });
      
      // AO Map из пресета игнорируем - используем только aoMap модели (если есть)
      if (loadedTextures.ao) {
        console.log(`  ⚠️ AO Map найден в пресете "${layerData.preset}" - будет проигнорирован (используется AO модели)`);
      }
      
      // Применяем текстуры к мешам
      for (const mesh of meshes) {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        
        for (const mat of materials) {
          // Сохраняем AO до ensurePBR: ensurePBR может вернуть новый материал без aoMap
          const prevAoMap = (mat as any)?.aoMap as THREE.Texture | null | undefined;
          const prevAoIntensity = (mat as any)?.aoMapIntensity as number | undefined;
          const material = ensurePBR(mat) as THREE.MeshStandardMaterial;
          
          if (prevAoMap && !(material as any).aoMap) {
            (material as any).aoMap = prevAoMap;
            (material as any).aoMapIntensity = prevAoIntensity ?? 1;
          }
          
          // Очищаем старые текстуры (кроме aoMap - он применяется ко всей модели)
          // normalMap будет сброшен через applyNormalMapsPublic ниже
          if (material.normalMap) { material.normalMap.dispose(); material.normalMap = null; }
          // Сбрасываем onBeforeCompile блендинг — будет переустановлен ниже
          (material as any).onBeforeCompile = () => {};
          (material as any).customProgramCacheKey = () => '';
          if (!preserveColorMap && material.map) { material.map.dispose(); material.map = null; }
          if (material.roughnessMap) { material.roughnessMap.dispose(); material.roughnessMap = null; }
          // aoMap НЕ трогаем - он применяется ко всей модели отдельно
          if (material.metalnessMap) { material.metalnessMap.dispose(); material.metalnessMap = null; }
          if ((material as any).alphaMap) { (material as any).alphaMap.dispose(); (material as any).alphaMap = null; }
          // Если opacity карты не будет — прозрачность должна выключиться (но не для принтов!)
          if (!preserveColorMap) (material as any).transparent = false;
          
          // Применяем новые текстуры из пресета (клонируем для независимого tiling)
          // Исключаем 'ao' - он не применяется из пресета. Для принтов не трогаем color (map).
          for (const [key, texture] of Object.entries(loadedTextures)) {
            if (!texture || key === 'ao') continue; // Пропускаем ao из пресета
            if (preserveColorMap && key === 'color') continue; // Принт: map — это дизайн, не перезаписываем
            
            const clonedTexture = texture.clone();
            setTextureProperties(clonedTexture, tiling, rotation);
            clonedTexture.needsUpdate = true;
            
            switch (key) {
              case 'normal':
                // Не применяем напрямую — блендинг выполняется ниже
                break;
              case 'color':
                material.map = clonedTexture;
                break;
              case 'roughness':
                material.roughnessMap = clonedTexture;
                break;
              case 'metalness':
                material.metalnessMap = clonedTexture;
                break;
              case 'opacity':
                (material as any).alphaMap = clonedTexture;
                (material as any).transparent = true;
                break;
            }
          }

          // Применяем нормал-карту с блендингом нормал-карты модели
          {
            const normalTex = loadedTextures.normal ? loadedTextures.normal.clone() : null;
            if (normalTex) setTextureProperties(normalTex, tiling, rotation);
            const setupTex = (tex: THREE.Texture) => setTextureProperties(tex, tiling, rotation);
            this.materialManager.applyNormalMapsPublic(material, normalTex, setupTex);
          }

          // Финально выставляем прозрачность: если opacity/alphaMap нет — прозрачность выключаем
          // Для принтов (preserveColorMap) transparent всегда true — прозрачность даёт map (дизайн принта)
          (material as any).transparent = preserveColorMap ? true : Boolean((material as any).alphaMap);
          
          material.needsUpdate = true;
        }
      }
      
      // Обновляем maps в layerData для синхронизации с MaterialManager
      // Исключаем 'ao' - он не сохраняется в слоях, только на уровне модели
      // Для принтов не перезаписываем color/map — там дизайн принта
      if (!layerData.maps) layerData.maps = {};
      for (const [key, texture] of Object.entries(loadedTextures)) {
        if (texture && key !== 'ao' && !(preserveColorMap && key === 'color')) {
          (layerData.maps as any)[key] = texture.clone();
        }
      }
      
      console.log(`  ✅ Текстуры пресета "${layerData.preset}" загружены с сервера`);
      
    } catch (error) {
      console.error(`  ❌ Ошибка загрузки пресета "${layerData.preset}":`, error);
    }
  }

  private mapURLs(maps: any = {}): any {
    // Синхронная версия: вытаскиваем URL/ dataURL всех карт, которые есть в слое
    const getURL = (texture: THREE.Texture | null) => {
      if (!texture) return null;
      if (texture.image && (texture.image.src || texture.image.currentSrc)) {
        const src = texture.image.currentSrc || texture.image.src;
        return src || null;
      }
      return null;
    };

    const mapKeys = ['map', 'color', 'normal', 'roughness', 'ao', 'metalness', 'opacity'] as const;
    const result: Record<string, string | null> = {};
    mapKeys.forEach((key) => {
      result[key] = getURL(maps[key]);
    });

    return result;
  }
  
  private async mapURLsAsync(maps: any = {}): Promise<any> {
    const textureToDataURL = async (mapValue: any): Promise<string | null> => {
      // Если уже строка (dataURL или URL), возвращаем её
      if (typeof mapValue === 'string') {
        return mapValue;
      }
      
      // Если не текстура или нет изображения - пропускаем
      const texture = mapValue as THREE.Texture | null;
      if (!texture || !texture.image) {
        return null;
      }
      
      const img = texture.image;
      // Если уже dataURL, возвращаем его
      if (img.src && img.src.startsWith('data:')) {
        return img.src;
      }
      
      // Конвертируем текстуру в canvas и затем в dataURL
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 512;
      canvas.height = img.height || 512;
      const ctx = canvas.getContext('2d');
      
      if (ctx && (img.complete || (img as any).complete === undefined)) {
        try {
          ctx.drawImage(img, 0, 0);
          return canvas.toDataURL('image/png');
        } catch (e) {
          console.warn('Ошибка конвертации текстуры:', e);
          return img.src || img.currentSrc || null;
        }
      } else {
        // Fallback на URL если canvas не доступен или изображение не загружено
        return img.src || img.currentSrc || null;
      }
    };
    
    // Сохраняем все используемые карты, чтобы материалы и принты восстанавливались полностью
    const mapKeys = ['map', 'color', 'normal', 'roughness', 'ao', 'metalness', 'opacity'] as const;
    const result: Record<string, string | null> = {};
    for (const key of mapKeys) {
      result[key] = await textureToDataURL(maps[key]);
    }

    return result;
  }

  public animate(): void {
    this.controls.update();
    
    // Бесконечный поворот модели вокруг оси Y
    if (this.isModelRotating && this.currentModel) {
      // В режиме "поворот модели" ручной drag может временно паузить витрину.
      if (this.modelRotationPausedByDrag) {
        // skip auto-rotation this frame
      } else {
      // Плавная остановка: постепенно снижаем скорость
      if (this.isStoppingRotation) {
        const delta = this.targetRotationSpeed - this.modelRotationSpeed;
        this.modelRotationSpeed += delta * 0.1; // Плавное изменение скорости
        
        if (Math.abs(this.modelRotationSpeed - this.targetRotationSpeed) < 0.0001) {
          this.isModelRotating = false;
          this.isStoppingRotation = false;
          this.modelRotationSpeed = 0.005; // Восстанавливаем базовую скорость
          // Возвращаем в положение, с которого стартовали витрину
          this.currentModel.rotation.y = this.turntableStartRotationY;
        }
      }
      this.currentModel.rotation.y += this.modelRotationSpeed;
      }
    }
    
    // Обновляем поворот для записи видео
    if (this.turntableState && this.currentModel) {
      const { startTime, duration, initialAngle, onDone } = this.turntableState;
      const now = performance.now();
      const t = Math.min(1, (now - startTime) / duration);
      // Easing: easeInOutCubic для плавного старта и финиша
      const tEased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      // Вращаем модель на полный оборот
      this.currentModel.rotation.y = initialAngle + tEased * Math.PI * 2;
      
      if (t >= 1) {
        // Возвращаем к начальному углу
        this.currentModel.rotation.y = initialAngle;
        this.turntableState = null;
        window.dispatchEvent(new CustomEvent('animationStopped'));
        if (onDone) onDone();
      }
    }
    
    // Анимация между позами камеры (просмотр/запись)
    if (this.camAnimState) {
      const { startTime, duration, startDir, axis, angle, startLen, endLen, pivot, startTarget, endTarget, startModelRot, endModelRot, onDone } = this.camAnimState;
      const now = performance.now();
      const t = Math.min(1, (now - startTime) / duration);
      // Linear In, Ease Out (Power2 out)
      const tEased = 1 - (1 - t) * (1 - t);
      // In "rotate model" control mode, arc animations should rotate the MODEL, not the camera.
      // Interpolate camera position and target between start and end for zoom effect.
      if (this.rotationControlMode === 'model') {
        // Интерполируем позицию камеры (для зума/приближения)
        const currentPos = new THREE.Vector3().lerpVectors(this.camAnimState.startPos, this.camAnimState.endPos, tEased);
        const currentTarget = new THREE.Vector3().lerpVectors(this.camAnimState.startTarget, this.camAnimState.endTarget, tEased);
        
        this.camera.position.copy(currentPos);
        this.controls.target.copy(currentTarget);
        this.camera.lookAt(this.controls.target);
        this.controls.update();
      } else {
        const dir = startDir.clone().applyAxisAngle(axis, angle * tEased);
        const radius = THREE.MathUtils.lerp(startLen, endLen, tEased);
        const pos = new THREE.Vector3().copy(pivot).add(dir.multiplyScalar(radius));
        const tgt = new THREE.Vector3().lerpVectors(startTarget, endTarget, tEased);
        this.camera.position.copy(pos);
        this.camera.lookAt(tgt);
        this.controls.target.copy(tgt);
        this.controls.update();
      }

      // В режиме "поворот модели" важно, чтобы Cinema учитывал Start/End поворот модели тоже
      if (this.currentModel && startModelRot && endModelRot) {
        const lerpAngleShort = (a: number, b: number, tt: number): number => {
          let d = b - a;
          if (d > Math.PI) d -= Math.PI * 2;
          if (d < -Math.PI) d += Math.PI * 2;
          return a + d * tt;
        };
        const lerpAngleLong = (a: number, b: number, tt: number): number => {
          // Take the long way around the circle (used for arc-long in model rotation mode)
          let d = b - a;
          if (d > Math.PI) d -= Math.PI * 2;
          if (d < -Math.PI) d += Math.PI * 2;
          // flip to the long arc
          if (d > 0) d -= Math.PI * 2;
          else if (d < 0) d += Math.PI * 2;
          return a + d * tt;
        };
        this.currentModel.rotation.set(
          lerpAngleShort(startModelRot.x, endModelRot.x, tEased),
          (this.rotationControlMode === 'model' && this.camArcLong)
            ? lerpAngleLong(startModelRot.y, endModelRot.y, tEased)
            : lerpAngleShort(startModelRot.y, endModelRot.y, tEased),
          lerpAngleShort(startModelRot.z, endModelRot.z, tEased)
        );
      }
      
      if (t >= 1) {
        this.camAnimState = null;
        window.dispatchEvent(new CustomEvent('animationStopped'));
        if (onDone) onDone();
      }
    }

    this.renderFrame();
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  public getControls(): OrbitControls {
    return this.controls;
  }
  
  public getLightManager() {
    return this.lightManager;
  }
  
  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }
  
  // Методы для отладки - изменение настроек рендеринга
  public setPixelRatio(value: number): void {
    this.userPixelRatio = value;
    if (!this.performanceModeEnabled) {
      this.renderer.setPixelRatio(Math.min(value, window.devicePixelRatio));
    }
    this.updateComposerSizeFromRenderer();
  }

  public setPerformanceModeEnabled(enabled: boolean): void {
    this.performanceModeEnabled = enabled;
    this.applyInteractivePixelRatio();
    this.updateComposerSizeFromRenderer();
  }

  public isPerformanceModeEnabled(): boolean {
    return this.performanceModeEnabled;
  }

  // Debug: Bloom / postprocessing controls
  public setBloomEnabled(enabled: boolean): void {
    this.bloomEnabled = enabled;
    if (enabled) this.ensureComposer();
    this.updatePostprocessingPasses();
  }

  public setBloomStrength(value: number): void {
    this.bloomStrength = value;
    if (this.bloomPass) this.bloomPass.strength = value;
    this.updatePostprocessingPasses();
  }

  public setBloomRadius(value: number): void {
    this.bloomRadius = value;
    if (this.bloomPass) this.bloomPass.radius = value;
    this.updatePostprocessingPasses();
  }

  public setBloomThreshold(value: number): void {
    this.bloomThreshold = value;
    if (this.bloomPass) this.bloomPass.threshold = value;
    this.updatePostprocessingPasses();
  }

  public setDOFEnabled(enabled: boolean): void {
    this.dofEnabled = enabled;
    if (enabled) this.ensureComposer();
    this.updatePostprocessingPasses();
  }

  public setDOFFocus(value: number): void {
    this.dofFocus = value;
    this.updatePostprocessingPasses();
  }

  public setDOFAperture(value: number): void {
    this.dofAperture = value;
    this.updatePostprocessingPasses();
  }

  public setDOFMaxBlur(value: number): void {
    this.dofMaxBlur = value;
    this.updatePostprocessingPasses();
  }

  public setFilmEnabled(enabled: boolean): void {
    this.filmEnabled = enabled;
    if (enabled) this.ensureComposer();
    this.updatePostprocessingPasses();
  }

  public setFilmNoiseIntensity(value: number): void {
    this.filmNoiseIntensity = value;
    this.updatePostprocessingPasses();
  }

  public setFilmScanlinesIntensity(value: number): void {
    this.filmScanlinesIntensity = value;
    this.updatePostprocessingPasses();
  }

  public setFilmScanlinesCount(value: number): void {
    this.filmScanlinesCount = value;
    this.updatePostprocessingPasses();
  }

  public setColorCorrectionEnabled(enabled: boolean): void {
    this.colorCorrectionEnabled = enabled;
    if (enabled) this.ensureComposer();
    this.updatePostprocessingPasses();
  }

  public setColorCorrectionBrightness(value: number): void {
    this.ccBrightness = value;
    this.updatePostprocessingPasses();
  }

  public setColorCorrectionContrast(value: number): void {
    this.ccContrast = value;
    this.updatePostprocessingPasses();
  }

  public setColorCorrectionSaturation(value: number): void {
    this.ccSaturation = value;
    this.updatePostprocessingPasses();
  }

  public setSSAOEnabled(enabled: boolean): void {
    this.ssaoEnabled = enabled;
    if (enabled) this.ensureComposer();
    this.updatePostprocessingPasses();
  }

  public setSSAORadius(value: number): void {
    this.ssaoRadius = value;
    if (this.ssaoPass) this.ssaoPass.kernelRadius = value;
    this.updatePostprocessingPasses();
  }

  public setSSAOMinDistance(value: number): void {
    this.ssaoMinDistance = value;
    if (this.ssaoPass) this.ssaoPass.minDistance = value;
    this.updatePostprocessingPasses();
  }

  public setSSAOMaxDistance(value: number): void {
    this.ssaoMaxDistance = value;
    if (this.ssaoPass) this.ssaoPass.maxDistance = value;
    this.updatePostprocessingPasses();
  }
  
  public setToneMappingExposure(value: number): void {
    this.renderer.toneMappingExposure = value;
  }
  
  public setAntialias(enabled: boolean): void {
    // Antialias нельзя изменить после создания renderer
    // Это только для информации/логирования
  }
  
  public setShadowMapEnabled(enabled: boolean): void {
    this.renderer.shadowMap.enabled = enabled;
    this.renderer.shadowMap.needsUpdate = true;
  }
  
  public setShadowMapType(type: string): void {
    const shadowTypes: Record<string, THREE.ShadowMapType> = {
      'PCFSoftShadowMap': THREE.PCFSoftShadowMap,
      'PCFShadowMap': THREE.PCFShadowMap,
      'BasicShadowMap': THREE.BasicShadowMap
    };
    if (shadowTypes[type]) {
      this.renderer.shadowMap.type = shadowTypes[type];
    }
  }

  public setShadowMapQuality(size: number): void {
    // Обновляем shadow map size для всех источников света с тенями
    this.scene.traverse((object) => {
      if ((object as any).isLight && (object as any).castShadow) {
        const light = object as THREE.Light;
        if (light.shadow) {
          light.shadow.mapSize.width = size;
          light.shadow.mapSize.height = size;
          light.shadow.map?.dispose();
          light.shadow.map = null as any;
        }
      }
    });
    this.renderer.shadowMap.needsUpdate = true;
  }

  public setShadowBias(bias: number): void {
    // Обновляем shadow bias для всех источников света с тенями
    this.scene.traverse((object) => {
      if ((object as any).isLight && (object as any).castShadow) {
        const light = object as THREE.Light;
        if (light.shadow) {
          light.shadow.bias = bias;
        }
      }
    });
  }
  
  public setToneMapping(type: string): void {
    const toneMappings: Record<string, THREE.ToneMapping> = {
      'LinearToneMapping': THREE.LinearToneMapping,
      'ReinhardToneMapping': THREE.ReinhardToneMapping,
      'CineonToneMapping': THREE.CineonToneMapping,
      'ACESFilmicToneMapping': THREE.ACESFilmicToneMapping,
      'CustomToneMapping': THREE.CustomToneMapping,
      'NoToneMapping': THREE.NoToneMapping
    };
    if (toneMappings[type]) {
      this.renderer.toneMapping = toneMappings[type];
    }
  }
  
  public setOutputColorSpace(space: string): void {
    const colorSpaces: Record<string, THREE.ColorSpace> = {
      'SRGBColorSpace': THREE.SRGBColorSpace,
      'LinearSRGBColorSpace': THREE.LinearSRGBColorSpace,
      'DisplayP3ColorSpace': THREE.DisplayP3ColorSpace
    };
    if (colorSpaces[space]) {
      this.renderer.outputColorSpace = colorSpaces[space];
    }
  }
  
  public setCameraNear(value: number): void {
    this.camera.near = value;
    this.camera.updateProjectionMatrix();
  }
  
  public setCameraFar(value: number): void {
    this.camera.far = value;
    this.camera.updateProjectionMatrix();
  }
  
  public setFogDensity(value: number): void {
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density = value;
    }
  }
  
  public setComposerRenderScale(value: number): void {
    this.composerRenderScale = value;
    this.updateComposerSizeFromRenderer();
  }
  
  public setPrintOffsetDebug(factor: number, units: number): void {
    this.printOffset = factor;
    // Обновляем polygonOffset для всех принт-материалов
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        const mesh = obj as THREE.Mesh;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((material: THREE.Material) => {
          if ((material as any).polygonOffset !== undefined) {
            (material as any).polygonOffset = true;
            (material as any).polygonOffsetFactor = factor;
            (material as any).polygonOffsetUnits = units;
          }
        });
      }
    });
  }
  
  public resetDebugSettings(): void {
    // Сбрасываем к значениям по умолчанию
    this.userPixelRatio = null;
    this.composerRenderScale = 1.5;
    this.applyInteractivePixelRatio();
    this.updateComposerSizeFromRenderer();
    this.renderer.toneMappingExposure = 1.2;
    this.renderer.shadowMap.enabled = false; // Тени выключены по умолчанию
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.camera.near = 0.1;
    this.camera.far = 2000;
    this.camera.updateProjectionMatrix();
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density = 0.08;
    }
    this.setPrintOffsetDebug(-0.2, 0);
    this.setBloomEnabled(false);
    this.setBloomStrength(0.35);
    this.setBloomRadius(0.35);
    this.setBloomThreshold(0.85);
    this.setDOFEnabled(false);
    this.setDOFFocus(1.5);
    this.setDOFAperture(0.002);
    this.setDOFMaxBlur(0.008);
    this.setFilmEnabled(false);
    this.setFilmNoiseIntensity(0.5);
    this.setFilmScanlinesIntensity(0.5);
    this.setFilmScanlinesCount(2048);
    this.setColorCorrectionEnabled(false);
    this.setColorCorrectionBrightness(0);
    this.setColorCorrectionContrast(0);
    this.setColorCorrectionSaturation(0);
  }
  
  public getScene(): THREE.Scene {
    return this.scene;
  }

  public getCurrentModel(): THREE.Group | null {
    return this.currentModel;
  }

  /** Возвращает словарь имён UV-групп (groupId → отображаемое имя) */
  public getUVGroupNames(): Record<string, string> {
    const result: Record<string, string> = {};
    if (this.project.uvGroups) {
      Object.entries(this.project.uvGroups).forEach(([id, info]) => {
        result[id] = info.name;
      });
    }
    return result;
  }

  private getAllPrintMeshes(): THREE.Mesh[] {
    const printMeshes: THREE.Mesh[] = [];
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        printMeshes.push(obj as THREE.Mesh);
      }
    });
    return printMeshes;
  }

  public setCurrentEditingPrintId(printId: string | null): void {
    this.currentEditingPrintId = printId;
  }

  public getCurrentEditingPrintId(): string | null {
    return this.currentEditingPrintId;
  }

  public getPrintTextureAsDataURL(printId: string): string | null {
    // Получаем текстуру из project.layers
    const layer = this.project.layers[printId];
    if (!layer || !layer.maps.map) return null;
    
    const mapValue = layer.maps.map;
    
    // Если maps.map уже строка (dataURL), возвращаем её напрямую
    if (typeof mapValue === 'string') {
      return mapValue;
    }
    
    // Если это THREE.Texture, конвертируем в dataURL
    const texture = mapValue as THREE.Texture;
    const img = texture.image;
    if (!img) return null;
    
    // Сохраняем ОРИГИНАЛЬНЫЙ размер текстуры
    const width = img.width || img.naturalWidth || 4096;
    const height = img.height || img.naturalHeight || 4096;
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL('image/png');
    }
    return null;
  }

  public getPrintLayerUVGroupIds(printId: string): string[] {
    const layer = this.project.layers[printId] as any;
    const ids = layer?.uvGroupIds;
    if (Array.isArray(ids) && ids.length > 0) return ids.filter(Boolean);
    const legacy = layer?.uvGroupId;
    if (typeof legacy === 'string' && legacy) return [legacy];
    // fallback: сначала инференс по ВИДИМЫМ мешам принта, затем по всем
    // Это сохраняет корректный UI в редакторе, если старые данные слоя не содержат uvGroupIds.
    const allMeshes = this.getPrintMeshesById(printId);
    const meshes = allMeshes.filter((m) => m.visible);
    const sourceMeshes = meshes.length > 0 ? meshes : allMeshes;
    const groups = new Set<string>();
    sourceMeshes.forEach((m) => {
      const gid = (m as any).userData?.uvGroup;
      if (typeof gid === 'string' && gid) groups.add(gid);
    });
    return Array.from(groups);
  }

  // Legacy helper: первый uvGroupId
  public getPrintLayerUVGroupId(printId: string): string | null {
    const ids = this.getPrintLayerUVGroupIds(printId);
    return ids[0] || null;
  }

  private async recordCameraAnimVideo(): Promise<void> {
    if (this.isRecordingVideo) {
      alert('Запись уже идёт');
      return;
    }

    if (!this.camPoseStart || !this.camPoseEnd) {
      alert('Не установлены начальная и конечная позиции камеры');
      return;
    }

    // Сохраняем ссылки для TypeScript type narrowing
    const camPoseStart = this.camPoseStart;
    const camPoseEnd = this.camPoseEnd;

    this.isRecordingVideo = true;
    this.cancelVideoRecording = false;
    
    // Показываем кнопку отмены
    window.dispatchEvent(new CustomEvent('showCancelButton'));

    // Параметры видео - 60 FPS для плавности
    const fps = 60;
    const durationSec = this.animDuration / 1000;
    const totalFrames = Math.round(fps * durationSec);
    const frameDurationMicros = 1_000_000 / fps;

    // Сохраняем состояние
    const prevSize = { w: this.renderer.getSize(new THREE.Vector2()).x, h: this.renderer.getSize(new THREE.Vector2()).y };
    const prevPixelRatio = this.renderer.getPixelRatio();
    const prevControlsEnabled = this.controls.enabled;
    const prevCamPos = this.camera.position.clone();
    const prevTarget = this.controls.target.clone();
    const prevAspect = this.camera.aspect;
    const prevModelRot = this.currentModel ? this.currentModel.rotation.clone() : null;
    this.controls.enabled = false;

    // Устанавливаем размер в соответствии с выбранным aspect ratio
    const { width, height } = this.getVideoResolution();
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    // Вычисляем параметры дуги камеры
    const pivot = camPoseStart.target.clone().add(camPoseEnd.target).multiplyScalar(0.5);
    const sDir = camPoseStart.position.clone().sub(pivot);
    const eDir = camPoseEnd.position.clone().sub(pivot);
    const startLen = sDir.length();
    const endLen = eDir.length();
    const startDirNorm = sDir.normalize();
    const eDirNorm = eDir.normalize();
    const dot = THREE.MathUtils.clamp(startDirNorm.dot(eDirNorm), -1, 1);
    const axis = new THREE.Vector3().crossVectors(startDirNorm, eDirNorm);
    if (axis.lengthSq() < 1e-6) {
      axis.set(0, 1, 0).cross(startDirNorm);
      if (axis.lengthSq() < 1e-6) axis.set(1, 0, 0);
    }
    axis.normalize();
    const angleShort = Math.acos(dot);
    const totalAngle = this.camArcLong ? (angleShort - 2 * Math.PI) : angleShort;

    // Показываем прогресс через overlay
    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: 'Рендеринг видео…', progress: 0 } 
    }));

    // Пробуем WebCodecs (надёжный 60fps)
    if (this.isWebCodecsSupported()) {
      try {
        await this.recordWithWebCodecs(
          width, height, fps, totalFrames, frameDurationMicros,
          (frame, total) => {
            const t = frame / (total - 1);
            const eased = 1 - Math.pow(1 - t, 3); // ease out cubic

            // В режиме "поворот модели" интерполируем позицию камеры линейно
            if (this.rotationControlMode === 'model') {
              const currentPos = camPoseStart.position.clone().lerp(camPoseEnd.position, eased);
              const currentTarget = camPoseStart.target.clone().lerp(camPoseEnd.target, eased);
              
              this.camera.position.copy(currentPos);
              this.controls.target.copy(currentTarget);
              this.camera.lookAt(currentTarget);
            } else {
              // В режиме "сцена" интерполяция по дуге
              const currentAngle = totalAngle * eased;
              const currentLen = startLen + (endLen - startLen) * eased;

              const q = new THREE.Quaternion().setFromAxisAngle(axis, currentAngle);
              const dir = startDirNorm.clone().applyQuaternion(q).multiplyScalar(currentLen);
              const pos = pivot.clone().add(dir);

              // Интерполяция target
              const currentTarget = camPoseStart.target.clone().lerp(camPoseEnd.target, eased);

              this.camera.position.copy(pos);
              this.controls.target.copy(currentTarget);
              this.camera.lookAt(currentTarget);
            }

            // Интерполяция поворота модели между Start/End
            if (this.currentModel) {
              const lerpAngleShort = (a: number, b: number, tt: number): number => {
                let d = b - a;
                if (d > Math.PI) d -= Math.PI * 2;
                if (d < -Math.PI) d += Math.PI * 2;
                return a + d * tt;
              };
              const lerpAngleLong = (a: number, b: number, tt: number): number => {
                let d = b - a;
                if (d > Math.PI) d -= Math.PI * 2;
                if (d < -Math.PI) d += Math.PI * 2;
                if (d > 0) d -= Math.PI * 2;
                else if (d < 0) d += Math.PI * 2;
                return a + d * tt;
              };
              this.currentModel.rotation.set(
                lerpAngleShort(camPoseStart.modelRotation.x, camPoseEnd.modelRotation.x, eased),
                (this.rotationControlMode === 'model' && this.camArcLong)
                  ? lerpAngleLong(camPoseStart.modelRotation.y, camPoseEnd.modelRotation.y, eased)
                  : lerpAngleShort(camPoseStart.modelRotation.y, camPoseEnd.modelRotation.y, eased),
                lerpAngleShort(camPoseStart.modelRotation.z, camPoseEnd.modelRotation.z, eased)
              );
            }
          },
          `video-${this.videoAspectRatio}-${Date.now()}.mp4`
        );
        if (isDemoMode()) consumeDemoQuota('video');
        this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
        if (this.currentModel && prevModelRot) this.currentModel.rotation.copy(prevModelRot);
        return;
      } catch (e) {
        console.warn('WebCodecs failed, falling back to MediaRecorder:', e);
      }
    }

    // Fallback на MediaRecorder
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    const stream = canvas.captureStream(fps);

    if (!stream) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Браузер не поддерживает запись canvas');
      this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
      return;
    }

    const chunks: BlobPart[] = [];
    const mimeTypes = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ];

    let recorder: MediaRecorder | null = null;
    for (const m of mimeTypes) {
      if (MediaRecorder.isTypeSupported(m)) {
        recorder = new MediaRecorder(stream, { mimeType: m, videoBitsPerSecond: 20_000_000 });
        break;
      }
    }

    if (!recorder) {
      window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
      alert('Не удалось инициализировать запись видео');
      this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
      return;
    }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };

    const frameDelay = 1000 / fps;
    recorder.start();

    for (let frame = 0; frame < totalFrames; frame++) {
      // Проверяем флаг отмены
      if (this.cancelVideoRecording) {
        recorder.stop();
        stream.getTracks().forEach(track => track.stop());
        window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
        this.showToolbarMessage(translate('message.render.cancelled'));
        this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
        return;
      }
      
      const t = frame / (totalFrames - 1);
      const eased = 1 - Math.pow(1 - t, 3);

      // В режиме "поворот модели" интерполируем позицию камеры линейно
      if (this.rotationControlMode === 'model') {
        const currentPos = camPoseStart.position.clone().lerp(camPoseEnd.position, eased);
        const currentTarget = camPoseStart.target.clone().lerp(camPoseEnd.target, eased);
        
        this.camera.position.copy(currentPos);
        this.controls.target.copy(currentTarget);
        this.camera.lookAt(currentTarget);
      } else {
        // В режиме "сцена" интерполяция по дуге
        const currentAngle = totalAngle * eased;
        const currentLen = startLen + (endLen - startLen) * eased;

        const q = new THREE.Quaternion().setFromAxisAngle(axis, currentAngle);
        const dir = startDirNorm.clone().applyQuaternion(q).multiplyScalar(currentLen);
        const pos = pivot.clone().add(dir);
        const currentTarget = camPoseStart.target.clone().lerp(camPoseEnd.target, eased);

        this.camera.position.copy(pos);
        this.controls.target.copy(currentTarget);
        this.camera.lookAt(currentTarget);
      }
      
      if (this.currentModel) {
        const lerpAngleShort = (a: number, b: number, tt: number): number => {
          let d = b - a;
          if (d > Math.PI) d -= Math.PI * 2;
          if (d < -Math.PI) d += Math.PI * 2;
          return a + d * tt;
        };
        const lerpAngleLong = (a: number, b: number, tt: number): number => {
          let d = b - a;
          if (d > Math.PI) d -= Math.PI * 2;
          if (d < -Math.PI) d += Math.PI * 2;
          if (d > 0) d -= Math.PI * 2;
          else if (d < 0) d += Math.PI * 2;
          return a + d * tt;
        };
        this.currentModel.rotation.set(
          lerpAngleShort(camPoseStart.modelRotation.x, camPoseEnd.modelRotation.x, eased),
          (this.rotationControlMode === 'model' && this.camArcLong)
            ? lerpAngleLong(camPoseStart.modelRotation.y, camPoseEnd.modelRotation.y, eased)
            : lerpAngleShort(camPoseStart.modelRotation.y, camPoseEnd.modelRotation.y, eased),
          lerpAngleShort(camPoseStart.modelRotation.z, camPoseEnd.modelRotation.z, eased)
        );
      }
      this.renderFrame();

      await new Promise(resolve => setTimeout(resolve, frameDelay));

      if (frame % Math.floor(totalFrames / 10) === 0) {
        const progress = Math.round((frame / totalFrames) * 100);
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
          detail: { text: translate('loading.render.video'), progress } 
        }));
      }
    }

    window.dispatchEvent(new CustomEvent('updateLoadingOverlay', { 
      detail: { text: translate('loading.save.video'), progress: 100 } 
    }));

    const donePromise = new Promise<void>((resolve) => {
      recorder!.onstop = () => {
        const blob = new Blob(chunks, { type: recorder!.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `video-${this.videoAspectRatio}-${Date.now()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        resolve();
      };
    });

    recorder.stop();
    await donePromise;

    // Скрываем overlay
    window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));

    this.showToolbarMessage('Видео сохранено!');
    if (isDemoMode()) consumeDemoQuota('video');

    this.restoreAfterRecording(prevSize, prevPixelRatio, prevAspect, prevControlsEnabled, prevCamPos, prevTarget);
    if (this.currentModel && prevModelRot) this.currentModel.rotation.copy(prevModelRot);
  }

  public savePrintCanvasData(printId: string, canvasData: any): void {
    const normalized = this.normalizePrintCanvasData(canvasData);
    if (!normalized) {
      console.warn(`🖼️ savePrintCanvasData(${printId}): invalid canvasData, skip`);
      return;
    }

    // Защита от затирания: если по ошибке прилетел пустой холст,
    // но у нас уже есть сохранённые объекты — НЕ перезаписываем.
    const existing = this.normalizePrintCanvasData(
      this.project.layers?.[printId]?.canvasData ?? this.printCanvasData?.[printId]
    );
    const newCount = normalized.objects?.length ?? 0;
    const existingCount = existing?.objects?.length ?? 0;
    if (newCount === 0 && existingCount > 0) {
      console.warn(`🖼️ savePrintCanvasData(${printId}): skip overwrite with empty canvas`, {
        existingCount
      });
      // На всякий случай держим кеш в актуальном виде
      this.printCanvasData[printId] = existing;
      return;
    }

    // Гарантируем запись слоя, чтобы canvasData не зависело только от кеша.
    if (!this.project.layers[printId]) {
      this.project.layers[printId] = {
        name: 'Print Layer',
        preset: null,
        params: {},
        maps: {},
        type: 'print',
        visible: true,
        labelColor: this.defaultPrintLabelColor(printId),
      } as any;
    } else {
      // Убеждаемся, что это print-слой
      (this.project.layers[printId] as any).type = 'print';
      if ((this.project.layers[printId] as any).visible === undefined) (this.project.layers[printId] as any).visible = true;
      if (!(this.project.layers[printId] as any).labelColor) (this.project.layers[printId] as any).labelColor = this.defaultPrintLabelColor(printId);
    }

    // Сохраняем данные canvas в layer
    this.project.layers[printId].canvasData = normalized;
    // И отдельно кешируем исходный canvasData для редактора
    this.printCanvasData[printId] = normalized;
  }

  public getPrintCanvasData(printId: string): any | null {
    // Сначала смотрим в кеш исходного canvasData, чтобы не зависеть от запечённых карт
    const cachedRaw = this.printCanvasData[printId];
    const layer = this.project.layers[printId];
    const layerCanvasRaw = layer?.canvasData;

    const cached = this.normalizePrintCanvasData(cachedRaw);
    const layerCanvas = this.normalizePrintCanvasData(layerCanvasRaw);
    console.log(`🖼️ getPrintCanvasData(${printId}):`, {
      layerExists: !!layer,
      hasLayerCanvasData: !!layerCanvas,
      hasCached: !!cached,
      objectsCount: cached?.objects?.length || layerCanvas?.objects?.length,
      firstObjectType: cached?.objects?.[0]?.type || layerCanvas?.objects?.[0]?.type
    });
    return cached || layerCanvas || null;
  }

  private normalizePrintCanvasData(input: any): { objects: any[] } | null {
    if (!input) return null;

    // Частый кейс после импорта/старых версий — строка JSON
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return this.normalizePrintCanvasData(parsed);
      } catch {
        return null;
      }
    }

    if (typeof input !== 'object') return null;

    const objects = (input as any).objects;
    if (Array.isArray(objects)) {
      return { ...input, objects };
    }

    // Если структура неожиданная — считаем, что данных для восстановления нет
    return null;
  }

  public getPrintMeshesById(printId: string): THREE.Mesh[] {
    const res: THREE.Mesh[] = [];
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        const m = obj as THREE.Mesh;
        const meshPrintId = (m as any).userData?.printId;
        if (meshPrintId === printId) res.push(m);
      }
    });
    return res;
  }

  private selectPrintLayerById(printId: string): void {
    // ВАЖНО: Очищаем старое мультивыделение перед выбором нового принта
    this.clearLayerSelection();
    
    // Сохраняем ID выбранного принта для редактирования
    this.setCurrentEditingPrintId(printId);
    
    const printMeshes = this.getPrintMeshesById(printId);
    if (printMeshes.length > 0) {
      const firstPrintMesh = printMeshes[0];
      
      // Добавляем новый принт в выделение
      this.selectedLayers.add(printId);
      this.selectedMeshes = printMeshes; // Все меши принта
      
      // ВАЖНО: передаём новое выделение в MaterialManager
      this.materialManager.setSelectedMeshes(this.selectedMeshes);
      this.materialManager.selectLayer(firstPrintMesh);
      
      // Находим соответствующий элемент в UI и выделяем его
      const element = document.querySelector(`[data-id="${printId}"]`) as HTMLElement;
      if (element) {
        this.highlightSelectedLayer(element);
        // Устанавливаем якорь для последующего shift-клика
        this.lastAnchorIndex = this.allLayerElements.indexOf(element);
      }
    }
  }

  private highlightSelectedLayer(element: HTMLElement): void {
    // Убираем выделение со всех слоев
    document.querySelectorAll('.layer').forEach(el => {
      el.classList.remove('active');
    });
    
    // Выделяем выбранный слой
    element.classList.add('active');
  }

  private ensureLayerSelected(): void {
    // Проверяем, есть ли уже выделенный слой
    const selectedLayer = document.querySelector('.layer.active');
    if (selectedLayer) return;
    
    // Выбираем первый доступный слой
    const firstLayer = document.querySelector('.layer') as HTMLElement;
    if (firstLayer) {
      const layerId = firstLayer.dataset.id;
      if (layerId) {
        // Проверяем, это принт-слой или обычный
        if (layerId.startsWith('print_')) {
          this.selectPrintLayer(firstLayer);
        } else {
          // Находим соответствующий меш
          const mesh = this.findMeshById(layerId);
          if (mesh) {
            this.selectLayer(mesh, firstLayer);
          }
        }
        
        // Обновляем превьюшку материала после выбора слоя
        setTimeout(() => {
          const layerData = this.project.layers[layerId];
          if (layerData) {
            window.dispatchEvent(new CustomEvent('materialPresetSelect', { 
              detail: { id: layerData.preset || null } 
            }));
          }
        }, 200);
      }
    }
  }

  private findMeshById(id: string): THREE.Mesh | null {
    let foundMesh: THREE.Mesh | null = null;
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.uuid === id) {
        foundMesh = obj as THREE.Mesh;
      }
    });
    return foundMesh;
  }

  public disableUVView(): void {
    if (this.uvViewEnabled) {
      this.toggleUVView();
    }
  }

  public toggleUVView(): void {
    this.uvViewEnabled = !this.uvViewEnabled;
    
    if (!this.currentModel) return;
    
    if (this.uvViewEnabled) {
      // Включаем UV режим - сохраняем оригинальные материалы и заменяем на UV debug
      this.currentModel.traverse((object) => {
        if ((object as any).isMesh) {
          const mesh = object as THREE.Mesh;
          
          // Сохраняем оригинальный материал
          this.originalMaterials.set(mesh, mesh.material);
          
          // Создаём UV debug материал
          const uvMaterial = new THREE.MeshBasicMaterial({
            map: this.createUVDebugTexture(),
            side: THREE.DoubleSide
          });
          
          mesh.material = uvMaterial;
        }
      });
      
      console.log('UV режим включён - видна UV развёртка модели');
    } else {
      // Выключаем UV режим - восстанавливаем оригинальные материалы
      this.currentModel.traverse((object) => {
        if ((object as any).isMesh) {
          const mesh = object as THREE.Mesh;
          const originalMaterial = this.originalMaterials.get(mesh);
          
          if (originalMaterial) {
            mesh.material = originalMaterial;
          }
        }
      });
      
      this.originalMaterials.clear();
      console.log('UV режим выключен - восстановлены оригинальные материалы');
    }
  }
  
  private createUVDebugTexture(): THREE.Texture {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    // Фон - шахматная доска
    const gridSize = size / 8;
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#333333' : '#666666';
        ctx.fillRect(x * gridSize, y * gridSize, gridSize, gridSize);
      }
    }
    
    // Сетка
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 2;
    for (let i = 0; i <= 8; i++) {
      ctx.beginPath();
      ctx.moveTo(i * gridSize, 0);
      ctx.lineTo(i * gridSize, size);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(0, i * gridSize);
      ctx.lineTo(size, i * gridSize);
      ctx.stroke();
    }
    
    // Координаты углов
    ctx.fillStyle = '#00ff00';
    ctx.font = 'bold 48px Arial';
    ctx.fillText('(0,0)', 20, 50);
    ctx.fillText('(1,0)', size - 150, 50);
    ctx.fillText('(0,1)', 20, size - 20);
    ctx.fillText('(1,1)', size - 150, size - 20);
    
    // Центральная метка
    ctx.fillStyle = '#ff0000';
    ctx.font = 'bold 64px Arial';
    ctx.fillText('UV', size / 2 - 50, size / 2 + 20);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    
    return texture;
  }
  
  public toggleModelRotation(): boolean {
    if (this.currentModel) {
      if (this.isModelRotating || this.isStoppingRotation) {
        // Резкая остановка поворота
        this.stopModelRotationImmediate();
        return false;
      } else {
        // Сбрасываем положение камеры перед началом поворота
        this.fitCamera(this.currentModel);
        
        // Сохраняем текущее положение перед началом поворота
        this.turntableStartRotationY = this.currentModel.rotation.y;
        this.isModelRotating = true;
        this.isStoppingRotation = false;
        return true;
      }
    }
    return false;
  }
  
  public stopModelRotationSmooth(): void {
    if (this.isModelRotating && !this.isStoppingRotation) {
      this.isStoppingRotation = true;
      this.targetRotationSpeed = 0;
    }
  }
  
  public stopModelRotationImmediate(): void {
    if (this.isModelRotating || this.isStoppingRotation) {
      this.isModelRotating = false;
      this.isStoppingRotation = false;
      this.modelRotationSpeed = 0.005; // Восстанавливаем базовую скорость
      if (this.currentModel) {
        this.currentModel.rotation.y = this.turntableStartRotationY;
      }
    }
  }

  /**
   * Останавливает автоповорот модели (витрину), НЕ сбрасывая текущий угол.
   * Нужен для ручного управления (drag-виджет).
   */
  public stopModelRotationPreserve(): void {
    if (this.isModelRotating || this.isStoppingRotation) {
      this.isModelRotating = false;
      this.isStoppingRotation = false;
      this.modelRotationSpeed = 0.005;
      if (this.currentModel) {
        // Фиксируем текущий угол как "стартовый" для будущих режимов
        this.turntableStartRotationY = this.currentModel.rotation.y;
      }
    }
  }

  /**
   * Повернуть модель вручную вокруг оси Y на delta (в радианах).
   */
  public rotateModelBy(deltaY: number): void {
    this.rotateModelEuler(0, deltaY, 0);
  }

  /**
   * Ручное вращение модели по осям (в радианах).
   * X = pitch, Y = yaw, Z = roll.
   */
  public rotateModelEuler(deltaX: number, deltaY: number, deltaZ: number): void {
    if (!this.currentModel) return;
    // Если включена витрина — выключаем, но без отката угла
    if (this.isModelRotating || this.isStoppingRotation) {
      this.stopModelRotationPreserve();
    }

    // X ограничим умеренно, чтобы не переворачивать "вверх ногами" случайно
    const nextX = this.currentModel.rotation.x + deltaX;
    this.currentModel.rotation.x = THREE.MathUtils.clamp(nextX, -Math.PI / 2, Math.PI / 2);
    this.currentModel.rotation.y += deltaY;
    this.currentModel.rotation.z += deltaZ;
  }
  
  public stopCameraAnimation(): void {
    // Останавливаем анимацию
    const wasTurntable = !!this.turntableState;
    this.camAnimState = null;
    this.turntableState = null;
    
    // Возвращаем к первому кадру только для turntable
    if (wasTurntable && this.currentModel) {
      // Для turntable - возвращаем модель к начальному углу
      this.currentModel.rotation.y = this.turntableStartRotationY;
    }
    // Для camera animation - оставляем камеру и модель на текущей позиции
    // (не возвращаем к Start)
    
    // Уведомляем UI что анимация остановлена
    window.dispatchEvent(new CustomEvent('animationStopped'));
  }
  
  public showToolbarMessage(text: string, duration: number = 3000): void {
    const messageContainer = document.getElementById('toolbar-message');
    const messageText = document.getElementById('toolbar-message-text');
    
    if (messageContainer && messageText) {
      // Сбрасываем предыдущий таймер, если есть
      if (this.toolbarMessageTimeout !== null) {
        clearTimeout(this.toolbarMessageTimeout);
        this.toolbarMessageTimeout = null;
      }
      
      messageText.textContent = text;
      messageContainer.classList.remove('opacity-0', 'pointer-events-none');
      messageContainer.classList.add('opacity-100');
      
      // Устанавливаем новый таймер для автоскрытия
      if (duration > 0) {
        this.toolbarMessageTimeout = window.setTimeout(() => {
          this.hideToolbarMessage();
          this.toolbarMessageTimeout = null;
        }, duration);
      }
    }
  }
  
  private hideToolbarMessage(): void {
    // Сбрасываем таймер, если есть
    if (this.toolbarMessageTimeout !== null) {
      clearTimeout(this.toolbarMessageTimeout);
      this.toolbarMessageTimeout = null;
    }
    
    const messageContainer = document.getElementById('toolbar-message');
    if (messageContainer) {
        messageContainer.classList.remove('opacity-100');
        messageContainer.classList.add('opacity-0', 'pointer-events-none');
    }
  }
  
  public isModelRotationEnabled(): boolean {
    return this.isModelRotating || this.isStoppingRotation;
  }
}
